import React, { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Doughnut, Bar, Line } from "react-chartjs-2"
import "chart.js/auto"
import WeeklyBuyPlanCard from "../components/WeeklyBuyPlanCard";
import POSalesOrderEarmarks from "../components/POSalesOrderEarmarks";
import POFollowUpPanel from "../components/POFollowUpPanel";
// Adjust the import path as needed based on your folder structure
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import { emailSenderHeaders } from '../utils/emailSender';
import openPdf from '../utils/openPdf';
import {
  loadPurchaseOrders as fetchPurchaseOrders, PO_STATUS_OPTIONS, filterPurchaseOrders, groupRfqBundles, rfqBundleName, poLateInfo,
  loadPurchaseOrder, findPurchaseOrderByNumber, poLinesTotal, lineRemaining, packingListDocs, poDocUrl, poPdfUrl, loadPoEmailHistory, emailSentAt,
} from '../features/purchaseOrders/poData';
import { shopToday } from '../utils/shopDate';
import { setAiContext, clearAiContext } from '../utils/aiContext';
import { apiErrorMessage, detailToString } from '../utils/apiError';
import { deriveJobStopShortages, orderableJobStopRows, dropPlanSummary, dropPlanHeading } from '../utils/jobStopShortages';
import ShipmentBeaconsModal from '../components/ShipmentBeaconsModal';
import { useFeatures } from '../utils/features';
import { loadBeacons } from '../utils/beacons';
import { shipmentHasPallets, beaconsByLot } from '../features/materials/shipmentBeacons';

// Simple Tooltip Component
const InfoTooltip = ({ text, className = "text-primary" }) => (
  <span 
    className={`ms-2 ${className}`} 
    title={text} 
    style={{ cursor: "help", fontSize: "0.9rem", textDecoration: "none" }}
  >
    &#9432;
  </span>
);

// --- PO Email Modal ---
const POEmailModal = ({ show, supplierName, onClose, onSend, isLoading, isRFQ, docType = 'po' }) => {
    const [body, setBody] = useState("");
    // Attach the drawings for any purchased component on this PO, at the
    // revision each line was written against. Default ON — the point of putting
    // a revision on the PO is that the vendor can see which print it means.
    // Untick for a routine reorder of something they already have on file.
    const [includeDrawings, setIncludeDrawings] = useState(true);
    const isStatement = !isRFQ && docType === 'statement';

    useEffect(() => {
        if (show) {
            setBody(isRFQ
                ? `Hello,\n\nWe would like to request a quote for the attached materials. ` +
                  `Please provide pricing in $/CWT (per hundredweight) for each line item.\n\n` +
                  `Please reply to this email with your quoted prices and estimated lead time.\n\n` +
                  `Thank you,\nKanCraft MFG`
                : docType === 'statement'
                ? `Hello,\n\nPlease find attached a statement for our purchase order showing what was ordered, ` +
                  `the receipts we have on record, and the quantities still outstanding.\n\n` +
                  `Please review it against your shipping records and confirm the status of the outstanding items, ` +
                  `or let us know of any discrepancies.\n\n` +
                  `Thank you,\nKanCraft MFG`
                : `Hello,\n\nPlease find the attached purchase order for your review.\n\n` +
                  `Please reply to this email to confirm receipt and expected delivery date.\n\n` +
                  `Thank you,\nKanCraft MFG`
            );
        }
    }, [show, isRFQ, docType]);

    if (!show) return null;

    const title = isRFQ ? "Email RFQ to Vendor" : isStatement ? "Email PO Statement to Vendor" : "Email PO to Vendor";
    const attachNote = isRFQ ? "The RFQ PDF will be attached automatically."
        : isStatement ? "The PO Statement PDF (order, receipts, outstanding balance) will be attached automatically."
        : "The PO PDF will be attached automatically.";

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <p className="mb-2"><strong>To:</strong> {supplierName || "Vendor"}</p>
                        <div className="mb-3">
                            <label className="form-label">Message Body</label>
                            <textarea
                                className="form-control"
                                rows="6"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                            ></textarea>
                            <small className="text-muted">{attachNote}</small>
                        </div>
                        {!isStatement && (
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="po-include-drawings"
                                    checked={includeDrawings}
                                    onChange={(e) => setIncludeDrawings(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="po-include-drawings">
                                    Attach component drawings
                                </label>
                                <div className="form-text">
                                    Any file marked “send to vendor” on a component on this order, at
                                    the revision the line was written against. Untick for a repeat
                                    order of something they already have.
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isLoading}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={() => onSend(body, includeDrawings)} disabled={isLoading}>
                            {isLoading ? 'Sending...' : isRFQ ? 'Send RFQ' : 'Send Email'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- PO Email History Modal ---
const POEmailHistoryModal = ({ show, poId, apiBase, onClose }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show && poId) {
            setLoading(true);
            loadPoEmailHistory(poId) // features/purchaseOrders/poData
                .then(setHistory)
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        }
    }, [show, poId, apiBase]);

    if (!show) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">PO Email History</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {loading ? (
                            <div className="text-center"><div className="spinner-border text-primary"></div></div>
                        ) : history.length === 0 ? (
                            <p className="text-muted text-center">No emails sent for this PO.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date (CST)</th>
                                            <th>Recipient</th>
                                            <th>Subject</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map(email => (
                                            <tr key={email.email_id}>
                                                <td>{emailSentAt(email.sent_at)}</td>
                                                <td>{email.to_email}</td>
                                                <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {email.subject}
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${email.status === 'sent' ? 'success' : 'danger'}`}>
                                                        {email.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function PurchaseOrdersPage() {
  const API_BASE = config.API_BASE_URL;

  // ------------------ View Mode -------------------
  // "DASH" (Dashboard), "PO" (PO Management), "DOCS" (Documents Queue)
  const [viewMode, setViewMode] = useState("DASH")

  // =============== State for the Left Panel (PO listing) ==================
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [statusFilter, setStatusFilter] = useState("Committed") // default filter
  // Cancels the previous in-flight PO list fetch when a new one starts. The
  // DASH-mount unfiltered fetch (slow, all POs) must not clobber a later
  // filtered PO-view fetch (fast) — last CALLER wins, not last response.
  const poListAbortRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [vendorSalesOrderSearch, setVendorSalesOrderSearch] = useState("")
  const [selectedPO, setSelectedPO] = useState(null)

  // =============== State for the Right Panel (PO detail/edit) =============
  const [poId, setPoId] = useState(null)
  const [poNumber, setPoNumber] = useState("")
  const [status, setStatus] = useState("Committed")
  const [supplier, setSupplier] = useState("")
  const [orderDate, setOrderDate] = useState("")
  const [estimatedReceiveDate, setEstimatedReceiveDate] = useState("")
  const [paymentTermsDays, setPaymentTermsDays] = useState("")
  const [notes, setNotes] = useState("")
  // Internal project label (purchase_orders.project_name). JobQ-only — the
  // vendor PDF/email print Notes, never this.
  const [projectName, setProjectName] = useState("")
  const [vendorSalesOrder, setVendorSalesOrder] = useState("")

  // NEW FIELDS: Fulfillment + Confirmed
  const [fulfillment, setFulfillment] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  // Bumped after Save / Email PO so the vendor follow-up box re-reads its state.
  const [followUpRefresh, setFollowUpRefresh] = useState(0)

  // Lines array: each => {
  //   po_line_id, item_type, material_id, material_code, material_description,
  //   consumable_id, quantity_ordered, quantity_received, cost_per_unit,
  //   vendor_sku (added from backend if item_type="Consumable")
  // }
  const [poLines, setPoLines] = useState([])
  // Sales orders for the earmark picker. Open only: the API refuses an earmark
  // on a closed order, and the reservation engine ignores one.
  const [earmarkSalesOrders, setEarmarkSalesOrders] = useState([])

  // Shipments => each { shipment_id, shipment_date, notes, lines[], documents[] }
  const [poShipments, setPoShipments] = useState([])

  // Pallet beacons (GET /features pallet_beacons): the receipt whose Beacons popup is open, and
  // {mtn_id: beacon_id} for the chips on the shipment lines. Advisory: a failed read shows no chips.
  const beaconsOn = useFeatures().palletBeacons.enabled
  const [beaconShipment, setBeaconShipment] = useState(null)
  const [lotBeacons, setLotBeacons] = useState({})
  const lotBeaconsReq = useRef(0)

  // For partial receiving form (multi-line).
  // Key=po_line_id, Value= qty/heat to receive now.
  const [receiveQuantities, setReceiveQuantities] = useState({})
  const [receiveHeatNumbers, setReceiveHeatNumbers] = useState({})

  // Multi-pallet split: when a vendor splits a single PO line across multiple
  // pallets with different heat numbers, the operator clicks "Split" on that
  // line and enters one row per pallet (handling unit). Key = po_line_id,
  // Value = array of { quantity, heat_number } objects. When a line has an
  // entry here it overrides receiveQuantities / receiveHeatNumbers for submit.
  const [receiveHandlingUnits, setReceiveHandlingUnits] = useState({})

  // For creating a new PO => "newLines" sub-form
  // each => { item_type, material_id, consumable_id, quantity_ordered, cost_per_unit }
  const [newLines, setNewLines] = useState([])

  // Error/loading
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  // Store all materials so we can display them in a dropdown
  const [allMaterials, setAllMaterials] = useState([])

  // Store all consumables so we can display them in a dropdown
  const [allConsumables, setAllConsumables] = useState([])

  // Store the list of vendors for the PO-level supplier field
  const [vendors, setVendors] = useState([])

  // "Attach Documents" inside a specific PO
  const [unlinkedDocsForAttach, setUnlinkedDocsForAttach] = useState([])
  const [attachDocId, setAttachDocId] = useState("")
  const [attachShipmentId, setAttachShipmentId] = useState("")

  // "Documents Queue" view
  const [unlinkedDocs, setUnlinkedDocs] = useState([])
  const [queueSelectedDoc, setQueueSelectedDoc] = useState(null)
  const [queueSelectedPO, setQueueSelectedPO] = useState("")
  const [queueReceiveQuantities, setQueueReceiveQuantities] = useState({})
  const [queueReceiveHeatNumbers, setQueueReceiveHeatNumbers] = useState({}) // New state for queue
  const [queuePOLines, setQueuePOLines] = useState([])

  // For document previews
  const [previewDoc, setPreviewDoc] = useState(null)

  // Reorder alerts for consumables (for Dashboard)
  const [consumableAlerts, setConsumableAlerts] = useState([])

  // Material shortages
  const [materialShortages, setMaterialShortages] = useState([])

  // For the local quick-reorder rows (in the Dashboard)
  const [consumableRows, setConsumableRows] = useState([])
  // Short items with no vendor link — can't be POed, must not vanish either.
  const [consumableNoVendor, setConsumableNoVendor] = useState([])
  const [consumableVendorFilter, setConsumableVendorFilter] = useState("All")

  const [materialRows, setMaterialRows] = useState([])
  const [materialVendorFilter, setMaterialVendorFilter] = useState("All")
  // Job-stopping subset of the material shortages: reserved > qoh + inbound.
  const [jobStopRows, setJobStopRows] = useState([])
  // Drop planner on the card (stock drops are cut in-house, not bought): the last
  // preview / run as display lines, whether it was a preview, and a busy flag.
  const [dropPlan, setDropPlan] = useState(null)
  const [dropPlanBusy, setDropPlanBusy] = useState(false)

  // For auto-creating new PO lines from either consumables or materials
  const [pendingNewLines, setPendingNewLines] = useState([])

  // Additional chart data states for the Dashboard
  const [statusDistributionData, setStatusDistributionData] = useState(null)
  const [monthlySpendData, setMonthlySpendData] = useState(null)

  // NEW charts states
  const [selectedSupplier, setSelectedSupplier] = useState("All")
  const [dateRange, setDateRange] = useState("30") // "7", "30", "90", "365", etc.
  const [onTimeOverdueData, setOnTimeOverdueData] = useState(null)
  const [leadTimeData, setLeadTimeData] = useState(null)
  const [top10SpendData, setTop10SpendData] = useState(null)
  const [top10VolumeData, setTop10VolumeData] = useState(null)
  const [openClosedLines, setOpenClosedLines] = useState(null)
  const [rolling12Data, setRolling12Data] = useState(null)
  const [avgCostData, setAvgCostData] = useState(null)

  // NEW: Avg Cost Filter
  const [avgCostFilter, setAvgCostFilter] = useState("All") 
  const [availableItems, setAvailableItems] = useState([])

  // Label Printing Modal State
  const [showLabelModal, setShowLabelModal] = useState(false)
  const [labelModalItems, setLabelModalItems] = useState([]) // Array of { material_id, code, description, printQty }
  const [labelModalShipmentId, setLabelModalShipmentId] = useState(null)

  // For creating stable temporary IDs for new PO lines
  const [tempIdCounter, setTempIdCounter] = useState(0);

  // RFQ State
  const [showRFQModal, setShowRFQModal] = useState(false);
  const [rfqSelectedVendors, setRfqSelectedVendors] = useState([]); // Array of vendor_ids
  const [rfqProjectName, setRfqProjectName] = useState("");
  const [rfqGroupView, setRfqGroupView] = useState(null); // If set, we show the comparison table
  const [rfqNameDraft, setRfqNameDraft] = useState(null); // non-null while renaming the open bundle

  // Email Modal State
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailSending, setEmailSending] = useState(false)
  const [showEmailHistory, setShowEmailHistory] = useState(false)
  const [emailDocType, setEmailDocType] = useState('po') // 'po' | 'statement'

  // RFQ Comparison email/preview — tracks which PO ID is being acted on
  const [rfqComparisonPoId, setRfqComparisonPoId] = useState(null)
  const [rfqComparisonSupplier, setRfqComparisonSupplier] = useState("")

  // CWT Pricing
  const [bulkCWT, setBulkCWT] = useState(""); // State for the "Apply to All" input

  // RFQ Comparison inline CWT editing: { "poId-materialKey": "cwtValue" }
  const [rfqCwtEdits, setRfqCwtEdits] = useState({});
  // RFQ Comparison inline per-unit price editing: { "poId-materialKey": "unitValue" }
  // Takes precedence over CWT when both are set (the two inputs are mutually
  // exclusive — typing in one clears the other for that cell).
  const [rfqUnitEdits, setRfqUnitEdits] = useState({});
  const [rfqSaving, setRfqSaving] = useState(false);

  // Per-line vendor award (May 19 2026). Authoritative awarded state lives on
  // rfqGroupView.childPOs[*].lines[*].awarded_at; we mutate it optimistically
  // on radio click. Awarding flag flips per-line while POST /award round-trips
  // so the cell can show in-flight feedback.
  const [rfqAwardingLineId, setRfqAwardingLineId] = useState(null);
  const [rfqShowFinalizeModal, setRfqShowFinalizeModal] = useState(false);
  const [rfqFinalizing, setRfqFinalizing] = useState(false);

  // RFQ Comparison inline quantity editing: { "materialKey": "qtyValue" }
  // Qty is shared across every vendor RFQ in the bundle (all child POs have
  // identical lines), so the key is the item key, not per-PO. Save broadcasts
  // to every child PO so they stay in lockstep.
  const [rfqQtyEdits, setRfqQtyEdits] = useState({});

  // Material Tracking Number Search
  const [mtnSearchTerm, setMtnSearchTerm] = useState("");
  const [mtnSearchResults, setMtnSearchResults] = useState([]);
  const [mtnSearching, setMtnSearching] = useState(false);

  // Manual RFQ Creation
  const [showManualRFQModal, setShowManualRFQModal] = useState(false);
  const [manualRfqLines, setManualRfqLines] = useState([]);
  const [manualRfqVendors, setManualRfqVendors] = useState([]);
  const [manualRfqNotes, setManualRfqNotes] = useState("");
  const [manualRfqProjectName, setManualRfqProjectName] = useState("");
  const [manualRfqDueDate, setManualRfqDueDate] = useState("");

  const navigate = useNavigate()

  // Clear AI context on unmount
  useEffect(() => () => clearAiContext(), []);

  // Handle ?action=create-rfq from Dashboard navigation
  // Sales orders for the earmark picker. Loaded once; a failure leaves the
  // picker empty rather than breaking the purchase order screen.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/sales_orders?status=Open`);
        if (!res.ok) return;
        const rows = await res.json();
        setEarmarkSalesOrders(Array.isArray(rows) ? rows : []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'create-rfq') {
      setShowManualRFQModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Handle ?po=1249 deep links (e.g. from the finance PO Audit page): open that
  // PO's edit view directly. Resolved against the unfiltered list endpoint so
  // Completed/RFQ POs open too (the page's own list defaults to Committed).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const poParam = params.get('po');
    if (!poParam) return;
    (async () => {
      try {
        const hit = await findPurchaseOrderByNumber(poParam); // features/purchaseOrders/poData (the phone's too)
        if (hit) handleSelectPO(hit);
      } catch {
        // deep link is best-effort — fall through to the normal dashboard
      } finally {
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The beacon chips on the open PO's shipment lines: one GET /beacons, re-read after the Beacons
  // popup assigns. A slower answer for a PO the user already left is dropped (request counter).
  async function refreshLotBeacons(shipments = poShipments) {
    const req = ++lotBeaconsReq.current
    if (!beaconsOn || !(shipments || []).some(shipmentHasPallets)) {
      setLotBeacons({})
      return
    }
    const r = await loadBeacons()
    if (req !== lotBeaconsReq.current) return
    setLotBeacons(r.ok && r.body ? beaconsByLot(r.body.beacons) : {})
  }

  useEffect(() => {
    refreshLotBeacons(poShipments)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beaconsOn, poShipments]);

  // =========================================================================
  // 1) Load POs (and data) from the server
  // =========================================================================
  useEffect(() => {
    if (viewMode === "PO" || viewMode === "DASH") {
      loadPurchaseOrders()
    }
    // searchTerm is filtered client-side (filteredPOs) — refetching per
    // keystroke was pure waste and another stale-response race source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, viewMode, vendorSalesOrderSearch])

  // Load materials, consumables, vendors, reorder alerts once at mount
  useEffect(() => {
    loadMaterialsList()
    loadConsumablesList()
    loadVendors()
    fetchConsumableAlerts()
    fetchMaterialShortages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If user goes to "DOCS" view, load unlinked docs
  useEffect(() => {
    if (viewMode === "DOCS") {
      fetchUnlinkedDocs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode])

  // After loading/refreshing purchaseOrders, build the default dashboard charts
  useEffect(() => {
    if (purchaseOrders && purchaseOrders.length > 0) {
      buildStatusDistributionChart(purchaseOrders)
      buildMonthlySpendChart(purchaseOrders)
      
      // Build filter list for Avg Cost
      buildAvailableItemsList(purchaseOrders)

      buildAllNewCharts()
    } else {
      setStatusDistributionData(null)
      setMonthlySpendData(null)
      setOnTimeOverdueData(null)
      setLeadTimeData(null)
      setTop10SpendData(null)
      setTop10VolumeData(null)
      setOpenClosedLines(null)
      setRolling12Data(null)
      setAvgCostData(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrders, selectedSupplier, dateRange, avgCostFilter])

  // Once consumableAlerts loads, build local consumableRows
  useEffect(() => {
    // Items with no vendor can't go on a PO, but dropping them silently is how
    // a real shortage disappears from the buyer's view entirely — this card was
    // hiding six components that the Components page did show. Keep them out of
    // the PO table, surface them as a named warning above it.
    const hasVendor = item => !!(item.vendor_name && item.vendor_name.trim() !== "")
    setConsumableNoVendor(consumableAlerts.filter(item => !hasVendor(item)))

    const newConsRows = consumableAlerts
      .filter(hasVendor)
      .map(item => ({
        ...item,
        selected: false,
        vendorOverride: item.vendor_name,
        // recommended_qty is computed server-side net of reserved AND inbound,
        // then rounded to MOQ / order multiple. The old local formula
        // (reorder_point - stock_quantity) saw neither.
        recommendedQty: item.recommended_qty
      }))
    setConsumableRows(newConsRows)
  }, [consumableAlerts])

  // Once materialShortages loads, build local materialRows
  useEffect(() => {
    const newMatRows = materialShortages.map(m => {
      const needed = m.minimum_available - m.total_avail
      const recommendedQty = needed > 0 ? needed : 1

      let defaultVendor = ""
      const foundMat = allMaterials.find(mm => mm.material_id === m.material_id)
      if (foundMat && foundMat.supplier) {
        defaultVendor = foundMat.supplier
      }
      const hasVendor = defaultVendor.trim() !== ""

      return {
        ...m,
        selected: false,
        vendorOverride: defaultVendor,
        recommendedQty,
        hasVendor
      }
    }).filter(item => item.hasVendor)

    setMaterialRows(newMatRows)
  }, [materialShortages, allMaterials])

  // Job-stopping shortages are derived from the SAME payload — one definition
  // of reserved / inbound, no second fetch — but kept as their own rows so a
  // tick in that card never ticks the floor card below it, and so a material
  // with no vendor is flagged there instead of dropped like it is here.
  useEffect(() => {
    setJobStopRows(deriveJobStopShortages(materialShortages, allMaterials))
  }, [materialShortages, allMaterials])

  async function loadPurchaseOrders() {
    if (poListAbortRef.current) poListAbortRef.current.abort()
    const controller = new AbortController()
    poListAbortRef.current = controller
    setLoading(true)
    setError(null)
    try {
      // searchTerm logic handled client-side (filterPurchaseOrders)
      const data = await fetchPurchaseOrders({ // features/purchaseOrders/poData
        status: viewMode === "PO" ? statusFilter : "",
        vendorSalesOrder: vendorSalesOrderSearch,
        signal: controller.signal,
      })
      setPurchaseOrders(data)
    } catch (err) {
      if (err.name === 'AbortError') return // superseded by a newer load
      setError(err.message)
    } finally {
      // An aborted call must not clear the newer call's loading spinner
      if (poListAbortRef.current === controller) setLoading(false)
    }
  }

  async function loadMaterialsList() {
    try {
      const res = await fetch(`${API_BASE}/materials`)
      if (!res.ok) throw new Error(`Failed to list materials: ${res.status}`)
      const data = await res.json()
      setAllMaterials(data)
    } catch (err) {
      console.error("Failed to load materials for dropdown:", err)
    }
  }

  async function loadConsumablesList() {
    try {
      const res = await fetch(`${API_BASE}/consumables`)
      if (!res.ok) throw new Error(`Failed to list consumables: ${res.status}`)
      const data = await res.json()
      setAllConsumables(data)
    } catch (err) {
      console.error("Failed to load consumables for dropdown:", err)
    }
  }

  async function loadVendors() {
    try {
      const res = await fetch(`${API_BASE}/vendors`)
      if (!res.ok) throw new Error(`Failed to list vendors: ${res.status}`)
      const data = await res.json()
      setVendors(data)
    } catch (err) {
      console.error("Failed to load vendors for dropdown:", err)
    }
  }

  async function fetchConsumableAlerts() {
    try {
      const res = await fetch(`${API_BASE}/consumable/reorder_alerts`)
      if (res.ok) {
        const data = await res.json()
        setConsumableAlerts(data)
      } else {
        console.warn("Failed to fetch consumable alerts:", res.status)
      }
    } catch (err) {
      console.error("Error fetching consumable reorder alerts:", err)
    }
  }

  async function fetchMaterialShortages() {
    try {
      const r = await fetch(`${API_BASE}/materials/below_min`)
      if (!r.ok) throw new Error(`Failed: ${r.status}`)
      const data = await r.json()
      setMaterialShortages(data)
    } catch (err) {
      console.error("Failed to load material shortages:", err)
    }
  }

  // Filter POs for search (used only in PO view); RFQ POs fold into one bundle row
  // (features/purchaseOrders/poData).
  const filteredPOs = filterPurchaseOrders(purchaseOrders, { searchTerm, statusFilter })
  const groupedPOs = groupRfqBundles(filteredPOs)

  // Helper: Find weight for a material ID
  const getMaterialWeight = (matId) => {
    const mat = allMaterials.find(m => m.material_id === matId);
    return mat ? parseFloat(mat.weight || 0) : 0;
  };

  // Handler: Apply CWT to ALL material lines (Bulk)
  const handleBulkApplyCWT = () => {
    const cwtVal = parseFloat(bulkCWT);
    if (isNaN(cwtVal) || cwtVal <= 0) {
      alert("Please enter a valid CWT price.");
      return;
    }

    // Update existing lines
    const updatedPoLines = poLines.map(line => {
      if (line.item_type === "Material" && line.material_id) {
        const weight = getMaterialWeight(line.material_id);
        if (weight > 0) {
          // Formula: (Weight / 100) * CWT
          const newCost = (weight / 100) * cwtVal;
          return { ...line, cost_per_unit: parseFloat(newCost.toFixed(4)) };
        }
      }
      return line;
    });

    setPoLines(updatedPoLines);
    alert(`Applied ${cwtVal} CWT to all materials with valid weights.`);
    setBulkCWT(""); // Clear input
  };

  // Handler: Calculate single line cost from CWT input
  const handleLineCWTChange = (lineKey, cwtValue, materialId) => {
    const cwt = parseFloat(cwtValue);
    if (!isNaN(cwt) && materialId) {
      const weight = getMaterialWeight(materialId);
      if (weight > 0) {
        const newCost = (weight / 100) * cwt;
        handleLineFieldChange(lineKey, "cost_per_unit", newCost.toFixed(4));
      }
    }
  };

  // --- NEW: Helper to calculate unique materials summary ---
  const uniqueMaterialSummary = [];
  const matMap = {};

  poLines.forEach(line => {
    if (line.item_type === "Material" && line.material_id) {
      if (!matMap[line.material_id]) {
        // Find definition for weight lookup
        const matDef = allMaterials.find(m => m.material_id === line.material_id);
        matMap[line.material_id] = {
          material_id: line.material_id,
          code: line.material_code,
          description: line.material_description,
          total_qty: 0,
          weight: matDef ? parseFloat(matDef.weight || 0) : 0,
          // Use the cost from the first occurrence as the initial display value
          current_cost: line.cost_per_unit
        };
        uniqueMaterialSummary.push(matMap[line.material_id]);
      }
      matMap[line.material_id].total_qty += (line.quantity_ordered || 0);
    }
  });

  // --- NEW: Handler to update ALL lines for a specific material ---
  const handleBulkMaterialCostChange = (materialId, newCost) => {
    const updatedLines = poLines.map(line => {
      if (line.item_type === "Material" && line.material_id === materialId) {
        return { ...line, cost_per_unit: newCost };
      }
      return line;
    });
    setPoLines(updatedLines);
  };

  // --- NEW: Handler to calculate CWT for specific material group ---
  const handleBulkMaterialCWTChange = (materialId, cwtValue) => {
    const cwt = parseFloat(cwtValue);
    const mat = uniqueMaterialSummary.find(m => m.material_id === materialId);
    
    if (!isNaN(cwt) && mat && mat.weight > 0) {
      const newCost = (mat.weight / 100) * cwt;
      handleBulkMaterialCostChange(materialId, parseFloat(newCost.toFixed(4)));
    }
  };

  // =========================================================================
  // 2) Dashboard Charts
  // =========================================================================
  function buildStatusDistributionChart(poList) {
    const counts = {}
    poList.forEach(po => {
      const st = po.status || "Unknown"
      counts[st] = (counts[st] || 0) + 1
    })
    const labels = Object.keys(counts)
    const data = Object.values(counts)
    setStatusDistributionData({
      labels,
      datasets: [
        {
          label: "PO Count by Status",
          data,
          backgroundColor: [
            "#007bff", // Committed
            "#28a745", // Completed
            "#dc3545", // Cancelled
            "#6f42c1"  // fallback
          ]
        }
      ]
    })
  }

  function buildMonthlySpendChart(poList) {
    const monthlyMap = {}
    poList.forEach(po => {
      if (!po.order_date) return
      const d = new Date(po.order_date)
      const ym = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0")

      let totalCost = 0
      if (po.lines) {
        po.lines.forEach(line => {
          const qty = line.quantity_ordered || 0
          const cpu = line.cost_per_unit || 0
          totalCost += qty * cpu
        })
      }
      monthlyMap[ym] = (monthlyMap[ym] || 0) + totalCost
    })

    const sortedKeys = Object.keys(monthlyMap).sort()
    const chartData = sortedKeys.map(k => monthlyMap[k])
    setMonthlySpendData({
      labels: sortedKeys,
      datasets: [
        {
          label: "Monthly PO Spend",
          data: chartData,
          backgroundColor: "#ffcd56"
        }
      ]
    })
  }

  function filterByDateRange(poList, range) {
    const now = new Date()
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - parseInt(range, 10))
    return poList.filter(po => {
      if (!po.order_date) return false
      const od = new Date(po.order_date)
      return od >= cutoff && od <= now
    })
  }

  function buildOnTimeOverdueChartData(poList, supplierName) {
    let filtered = poList
    if (supplierName !== "All") {
      filtered = filtered.filter(po => (po.supplier || "") === supplierName)
    }
    let onTimeCount = 0
    let overdueCount = 0
    const todayStr = shopToday() // the shop's (Central) day, as the phone's list

    filtered.forEach(po => {
      if (po.status === "Committed") {
        if (po.estimated_receive_date && po.estimated_receive_date < todayStr) {
          overdueCount++
        } else {
          onTimeCount++
        }
      }
    })

    return {
      labels: ["On-Time", "Overdue"],
      datasets: [
        {
          data: [onTimeCount, overdueCount],
          backgroundColor: ["#28a745", "#dc3545"]
        }
      ]
    }
  }

  // --- REPLACED: Avg Days Late (Instead of Lead Time) ---
  function buildAvgDaysLateBySupplier(poList) {
    const supplierMap = {}
    
    poList.forEach(po => {
      // Logic: Only look at POs that have an estimated receive date and at least one shipment
      if (!po.estimated_receive_date) return
      if (!po.shipments || po.shipments.length === 0) return

      const estDate = new Date(po.estimated_receive_date)
      
      // Find the date of the LAST shipment to determine if the whole order was late
      let maxShipDate = new Date(po.shipments[0].shipment_date)
      po.shipments.forEach(s => {
        const sd = new Date(s.shipment_date)
        if (sd > maxShipDate) {
          maxShipDate = sd
        }
      })

      // Calculate difference in milliseconds
      const diffTime = maxShipDate - estDate
      // Convert to days (1000ms * 60s * 60m * 24h)
      const daysLate = diffTime / (1000 * 60 * 60 * 24)

      const sup = po.supplier || "Unknown"
      if (!supplierMap[sup]) {
        supplierMap[sup] = { totalDaysLate: 0, count: 0 }
      }
      supplierMap[sup].totalDaysLate += daysLate
      supplierMap[sup].count++
    })

    const labels = []
    const data = []
    
    Object.keys(supplierMap).forEach(sup => {
      const avg = supplierMap[sup].totalDaysLate / supplierMap[sup].count
      labels.push(sup)
      data.push(avg.toFixed(1))
    })

    return {
      labels,
      datasets: [
        {
          label: "Avg Days Late (Neg=Early)",
          data,
          backgroundColor: data.map(d => d > 0 ? "#dc3545" : "#28a745") // Red if late, Green if early
        }
      ]
    }
  }

  // --- UPDATED: Open Purchases Only ---
  function buildOpenClosedLines(poList) {
    const lines = []
    poList.forEach(po => {
      if (!po.lines) return
      // Skip completed or cancelled POs entirely for this view
      if (po.status === 'Completed' || po.status === 'Cancelled') return

      po.lines.forEach(ln => {
        const remain = (ln.quantity_ordered || 0) - (ln.quantity_received || 0)
        // Only show lines that are actually open
        if (remain > 0) {
          lines.push({
            po_number: po.po_number,
            supplier: po.supplier,
            itemCode:
              ln.item_type === "Material"
                ? ln.material_code
                : ln.consumable_code || ln.consumable_id, // safeguard if consumable_code is missing
            description: ln.material_description,
            qtyOrdered: ln.quantity_ordered,
            qtyReceived: ln.quantity_received,
            qtyRemaining: remain
          })
        }
      })
    })
    return lines
  }

  function buildTop10BySpend(poList) {
    const itemMap = {}
    poList.forEach(po => {
      if (!po.lines) return
      po.lines.forEach(ln => {
        const itemKey =
          ln.item_type === "Material"
            ? `M${ln.material_id}`
            : `C${ln.consumable_id}`
        const cost = (ln.quantity_ordered || 0) * (ln.cost_per_unit || 0)
        if (!itemMap[itemKey]) {
          itemMap[itemKey] = {
            desc: ln.material_description || "",
            totalSpend: 0
          }
        }
        itemMap[itemKey].totalSpend += cost
        if (!itemMap[itemKey].desc && ln.consumable_id) {
          itemMap[itemKey].desc = "Consumable"
        }
      })
    })
    const arr = Object.entries(itemMap).map(([key, val]) => ({ key, ...val }))
    arr.sort((a, b) => b.totalSpend - a.totalSpend)
    const top10 = arr.slice(0, 10)

    return {
      labels: top10.map(x => x.desc || x.key),
      datasets: [
        {
          label: "Top 10 Items by Spend",
          data: top10.map(x => x.totalSpend),
          backgroundColor: "#6f42c1"
        }
      ]
    }
  }

  function buildTop10ByVolume(poList) {
    const itemMap = {}
    poList.forEach(po => {
      if (!po.lines) return
      po.lines.forEach(ln => {
        const itemKey =
          ln.item_type === "Material"
            ? `M${ln.material_id}`
            : `C${ln.consumable_id}`
        const qty = ln.quantity_ordered || 0
        if (!itemMap[itemKey]) {
          itemMap[itemKey] = {
            desc: ln.material_description || "",
            totalQty: 0
          }
        }
        itemMap[itemKey].totalQty += qty
        if (!itemMap[itemKey].desc && ln.consumable_id) {
          itemMap[itemKey].desc = "Consumable"
        }
      })
    })
    const arr = Object.entries(itemMap).map(([key, val]) => ({ key, ...val }))
    arr.sort((a, b) => b.totalQty - a.totalQty)
    const top10 = arr.slice(0, 10)

    return {
      labels: top10.map(x => x.desc || x.key),
      datasets: [
        {
          label: "Top 10 Items by Volume",
          data: top10.map(x => x.totalQty),
          backgroundColor: "#fd7e14"
        }
      ]
    }
  }

  function buildRolling12Chart(poList) {
    const monthlyMap = {}
    poList.forEach(po => {
      if (!po.order_date) return
      const d = new Date(po.order_date)
      const ym = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0")
      let totalCost = 0
      if (po.lines) {
        po.lines.forEach(ln => {
          totalCost += (ln.quantity_ordered || 0) * (ln.cost_per_unit || 0)
        })
      }
      monthlyMap[ym] = (monthlyMap[ym] || 0) + totalCost
    })

    const now = new Date()
    const months = []
    for (let i = 11; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const labelYM = dt.getFullYear() + "-" + String(dt.getMonth() + 1).padStart(2, "0")
      months.push(labelYM)
    }

    const labels = []
    const data = []
    months.forEach(ym => {
      labels.push(ym)
      data.push(monthlyMap[ym] || 0)
    })

    return {
      labels,
      datasets: [
        {
          label: "Rolling 12-Month Spend",
          data,
          borderColor: "#ff6384",
          backgroundColor: "#ff638450",
          fill: true
        }
      ]
    }
  }

  // --- UPDATED: Available Items for Filter ---
  function buildAvailableItemsList(poList) {
    const items = {}
    poList.forEach(po => {
      if(!po.lines) return;
      po.lines.forEach(ln => {
        const key = ln.item_type === "Material" ? `M-${ln.material_id}` : `C-${ln.consumable_id}`;
        // Prefer Description, fallback to Code
        const label = ln.material_description || ln.material_code || "Unknown";
        if (!items[key]) items[key] = label;
      })
    })
    const array = Object.entries(items).map(([key, label]) => ({ key, label }));
    array.sort((a,b) => a.label.localeCompare(b.label));
    setAvailableItems(array);
  }

  // --- UPDATED: Avg Cost with Filtering ---
  function buildAverageCostData(poList) {
    const monthlyCosts = {}
    poList.forEach(po => {
      if (!po.order_date || !po.lines) return
      const d = new Date(po.order_date)
      const ym = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0")
      
      po.lines.forEach(ln => {
        // FILTER LOGIC
        if (avgCostFilter !== "All") {
          const key = ln.item_type === "Material" ? `M-${ln.material_id}` : `C-${ln.consumable_id}`;
          if (key !== avgCostFilter) return;
        }

        if (ln.cost_per_unit != null) {
          if (!monthlyCosts[ym]) {
            monthlyCosts[ym] = { sum: 0, cnt: 0 }
          }
          monthlyCosts[ym].sum += parseFloat(ln.cost_per_unit)
          monthlyCosts[ym].cnt += 1
        }
      })
    })

    const sortedKeys = Object.keys(monthlyCosts).sort()
    const labels = []
    const data = []
    sortedKeys.forEach(ym => {
      labels.push(ym)
      const avg = monthlyCosts[ym].sum / monthlyCosts[ym].cnt
      data.push(avg)
    })

    return {
      labels,
      datasets: [
        {
          label: "Avg Cost/Unit",
          data,
          borderColor: "#36a2eb",
          backgroundColor: "#36a2eb50",
          fill: true
        }
      ]
    }
  }

  function buildAllNewCharts() {
    const dateFiltered = filterByDateRange(purchaseOrders, dateRange)

    const onTimeData = buildOnTimeOverdueChartData(dateFiltered, selectedSupplier)
    setOnTimeOverdueData(onTimeData)

    // CHANGED: Use the new Late calculation
    const daysLateData = buildAvgDaysLateBySupplier(dateFiltered)
    setLeadTimeData(daysLateData) 

    const topSpend = buildTop10BySpend(dateFiltered)
    setTop10SpendData(topSpend)

    const topVolume = buildTop10ByVolume(dateFiltered)
    setTop10VolumeData(topVolume)

    const ocLines = buildOpenClosedLines(dateFiltered)
    setOpenClosedLines(ocLines)

    const rolling = buildRolling12Chart(dateFiltered)
    setRolling12Data(rolling)

    // Recalculate cost data based on full dataset (or filtered by item)
    const avgCost = buildAverageCostData(purchaseOrders)
    setAvgCostData(avgCost)
  }

  // =========================================================================
  // 3) Selecting/Viewing a PO => fetch detail
  // =========================================================================
  async function handleSelectPO(po) {
    setSelectedPO(po)
    setViewMode("PO")
    setError(null)
    setAiContext('purchaseOrder', { purchaseOrderId: po.purchase_order_id, poNumber: po.po_number, supplier: po.supplier });
    setLoading(true)
    try {
      const detail = await loadPurchaseOrder(po.purchase_order_id) // features/purchaseOrders/poData
      fillDetailForm(detail)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function fillDetailForm(detail) {
    setPoId(detail.purchase_order_id)
    setPoNumber(detail.po_number)
    setStatus(detail.status || "Committed")
    setSupplier(detail.supplier || "")
    setOrderDate(detail.order_date || "")
    setEstimatedReceiveDate(detail.estimated_receive_date || "")
    setPaymentTermsDays(detail.payment_terms_days ?? "")
    setNotes(detail.notes || "")
    setProjectName(detail.project_name || "")
    setVendorSalesOrder(detail.vendor_sales_order || "")
    setFulfillment(detail.fulfillment || "")
    setConfirmed(!!detail.confirmed)

    setPoShipments(detail.shipments || [])
    setPoLines(detail.lines || [])

    setReceiveQuantities({})
    setReceiveHeatNumbers({})
    setReceiveHandlingUnits({})
    setNewLines([])
    setUnlinkedDocsForAttach([])
    setAttachDocId("")
    setAttachShipmentId("")
  }

  // =========================================================================
  // 4) Creating a new PO
  // =========================================================================
  function handleCreateNewPO() {
    setViewMode("PO")
    const todayStr = shopToday() // the shop's (Central) day: UTC's is tomorrow after 7 PM CT

    setSelectedPO(null)
    setPoId(null)
    setPoNumber("")
    setStatus("Committed")
    setSupplier("")
    setOrderDate(todayStr)
    setEstimatedReceiveDate("")
    setPaymentTermsDays("")
    setNotes("")
    setProjectName("")
    setVendorSalesOrder("")
    setFulfillment("")
    setConfirmed(false)

    setPoLines([])
    setPoShipments([])
    setReceiveQuantities({})
    setReceiveHeatNumbers({})
    setReceiveHandlingUnits({})
    setNewLines([])
    setUnlinkedDocsForAttach([])
    setAttachDocId("")
    setAttachShipmentId("")

    if (pendingNewLines.length > 0) {
      setNewLines(pendingNewLines)
      setPendingNewLines([])
    }
  }

  // MODIFIED: Add filter fields to new line object
  function handleAddNewLine() {
    setNewLines(prev => [
      ...prev,
      {
        item_type: "Material",
        material_id: "",
        consumable_id: "",
        quantity_ordered: 0,
        cost_per_unit: null,
        // NEW: Add filter fields
        filterOwner: "",
        filterMaterialType: "",
        filterThickness: "",
        filterSurfaceFinish: ""
      }
    ])
  }

  function handleRemoveNewLine(idx) {
    setNewLines(prev => prev.filter((_, i) => i !== idx))
  }

  // MODIFIED: Handle filter changes by resetting material selection
  function updateNewLineField(idx, field, value) {
    setNewLines(prev => {
      const copy = [...prev]
      let line = { ...copy[idx], [field]: value }

      // Reset dependent fields when item type changes
      if (field === "item_type") {
        line = {
          ...line,
          material_id: "",
          consumable_id: "",
          cost_per_unit: null,
          cost_source: null,
          cost_auto_filled: false,
          filterOwner: "",
          filterMaterialType: "",
          filterThickness: "",
          filterSurfaceFinish: ""
        };
      }

      // NEW: When a filter changes, reset the material ID to force re-selection
      const filterFields = ["filterOwner", "filterMaterialType", "filterThickness", "filterSurfaceFinish"];
      if (filterFields.includes(field)) {
        line.material_id = "";
        line.cost_per_unit = null;
        line.cost_source = null;
        line.cost_auto_filled = false;
      }

      // When material or consumable changes, reset cost (auto-fill runs next)
      if (field === "material_id" || field === "consumable_id") {
        line.cost_per_unit = null
        line.cost_source = null
        line.cost_auto_filled = false
      }

      // A direct edit of Cost/Unit is a manual entry — protect it from re-pricing
      if (field === "cost_per_unit") {
        line.cost_auto_filled = false
        line.cost_source = null
      }

      copy[idx] = line
      return copy
    })
  }

  // =========================================================================
  // Auto-load the current vendor price for a New PO line on item selection.
  // Prefers the price for the PO's selected supplier; falls back to the item's
  // preferred vendor. Best-effort — silent on any failure (leaves field blank).
  // =========================================================================
  function pickVendorCost(rows, selectedVendorId, costKey) {
    if (!Array.isArray(rows) || rows.length === 0) return null
    // rows arrive preferred-first from the API
    const candidates = []
    if (selectedVendorId != null) {
      const exact = rows.find(r => r.vendor_id === selectedVendorId)
      if (exact) candidates.push({ row: exact, exact: true })
    }
    const preferred = rows.find(r => r.is_preferred) || rows[0]
    if (preferred) candidates.push({ row: preferred, exact: false })
    // Prefer the first candidate that actually carries a positive price
    const chosen = candidates.find(c => Number(c.row[costKey]) > 0) || candidates[0]
    if (!chosen) return null
    return {
      cost: Number(chosen.row[costKey]) || 0,
      source: chosen.row.vendor_name,
      exact: chosen.exact
    }
  }

  async function autoFillNewLineVendorPrice(idx, itemType, itemId, supplierName = supplier) {
    if (!itemId) return
    const url = itemType === "Material"
      ? `${API_BASE}/materials/${itemId}/vendors`
      : `${API_BASE}/consumables/${itemId}/vendors`
    const costKey = itemType === "Material" ? "cost_per_unit" : "cost"

    let rows
    try {
      const res = await fetch(url)
      if (!res.ok) return
      rows = await res.json()
    } catch (e) {
      return // best-effort; leave the field blank for manual entry
    }

    const selVendor = vendors.find(v => v.name === supplierName)
    const selVendorId = selVendor ? selVendor.vendor_id : null
    const picked = pickVendorCost(rows, selVendorId, costKey)

    setNewLines(prev => {
      const copy = [...prev]
      const line = copy[idx]
      if (!line) return prev
      // Guard against races: only apply if this line still references the same item
      const stillSameItem = itemType === "Material"
        ? String(line.material_id) === String(itemId)
        : String(line.consumable_id) === String(itemId)
      if (!stillSameItem) return prev
      // Never clobber a price the operator typed in by hand
      if (line.cost_per_unit != null && !line.cost_auto_filled) return prev

      if (!picked || !(picked.cost > 0)) {
        copy[idx] = { ...line, cost_source: "no vendor price on file", cost_auto_filled: false }
        return copy
      }
      copy[idx] = {
        ...line,
        cost_per_unit: parseFloat(picked.cost.toFixed(4)),
        cost_auto_filled: true,
        cost_source: picked.exact ? picked.source : `${picked.source} · preferred`
      }
      return copy
    })
  }

  // When the PO supplier changes, re-price any auto-filled lines (manual entries
  // are protected by the guard inside autoFillNewLineVendorPrice).
  function repriceNewLinesForSupplier(supplierName) {
    newLines.forEach((ln, idx) => {
      if (ln.item_type === "Material" && ln.material_id) {
        autoFillNewLineVendorPrice(idx, "Material", ln.material_id, supplierName)
      } else if (ln.item_type === "Consumable" && ln.consumable_id) {
        autoFillNewLineVendorPrice(idx, "Consumable", ln.consumable_id, supplierName)
      }
    })
  }


  // =========================================================================
  // Manual RFQ Line Builder
  // =========================================================================
  function handleAddManualRfqLine() {
    setManualRfqLines(prev => [
      ...prev,
      {
        item_type: "Material",
        material_id: "",
        consumable_id: "",
        quantity_ordered: 0,
        cost_per_unit: null,
        filterOwner: "",
        filterMaterialType: "",
        filterThickness: "",
        filterSurfaceFinish: ""
      }
    ]);
  }

  function handleRemoveManualRfqLine(idx) {
    setManualRfqLines(prev => prev.filter((_, i) => i !== idx));
  }

  function updateManualRfqLineField(idx, field, value) {
    setManualRfqLines(prev => {
      const copy = [...prev];
      let line = { ...copy[idx], [field]: value };

      if (field === "item_type") {
        line = {
          ...line,
          material_id: "",
          consumable_id: "",
          cost_per_unit: null,
          filterOwner: "",
          filterMaterialType: "",
          filterThickness: "",
          filterSurfaceFinish: ""
        };
      }

      const filterFields = ["filterOwner", "filterMaterialType", "filterThickness", "filterSurfaceFinish"];
      if (filterFields.includes(field)) {
        line.material_id = "";
        line.cost_per_unit = null;
      }

      if (field === "material_id" || field === "consumable_id") {
        line.cost_per_unit = null;
      }

      copy[idx] = line;
      return copy;
    });
  }

  async function handleSubmitManualRFQ() {
    if (manualRfqVendors.length === 0) {
      alert("Please select at least one vendor.");
      return;
    }
    const validLines = manualRfqLines.filter(ln =>
      (ln.item_type === "Material" && ln.material_id) ||
      (ln.item_type === "Consumable" && ln.consumable_id)
    );
    if (validLines.length === 0) {
      alert("Please add at least one item with a material or consumable selected.");
      return;
    }
    if (validLines.some(ln => !ln.quantity_ordered || ln.quantity_ordered <= 0)) {
      alert("All lines must have a quantity greater than 0.");
      return;
    }

    const payload = {
      vendor_ids: manualRfqVendors,
      lines: validLines.map(ln => ({
        item_type: ln.item_type,
        material_id: ln.item_type === "Material" ? parseInt(ln.material_id, 10) : null,
        consumable_id: ln.item_type === "Consumable" ? parseInt(ln.consumable_id, 10) : null,
        quantity_ordered: parseInt(ln.quantity_ordered, 10),
        cost_per_unit: null,
        requested_delivery_date: manualRfqDueDate || null,
      })),
      due_date: manualRfqDueDate || null,
      notes: manualRfqNotes || "Manual RFQ",
      project_name: manualRfqProjectName.trim() || null
    };

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/purchase_orders/rfq_group`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json();
      alert(data.message);

      // Cleanup
      setShowManualRFQModal(false);
      setManualRfqLines([]);
      setManualRfqVendors([]);
      setManualRfqNotes("");
      setManualRfqProjectName("");
      setManualRfqDueDate("");
      loadPurchaseOrders();
    } catch (err) {
      alert("Error creating RFQ: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  // =========================================================================
  // 5) Editable lines for an existing PO
  // =========================================================================
  // MODIFIED: Handle filter changes by resetting material selection
  function handleLineFieldChange(lineKey, fieldName, newValue) {
    setPoLines(prevLines =>
      prevLines.map(line => {
        // Check against both database ID and temporary ID
        const currentLineKey = line.po_line_id ?? line.id;
        if (currentLineKey === lineKey) {
          let updated = { ...line };
          
          const filterFields = ["filterOwner", "filterMaterialType", "filterThickness", "filterSurfaceFinish"];

          // Handle specific field logic
          if (fieldName === "item_type") {
            updated = {
              ...updated,
              item_type: newValue,
              material_id: null,
              consumable_id: null,
              cost_per_unit: null,
              filterOwner: "",
              filterMaterialType: "",
              filterThickness: "",
              filterSurfaceFinish: ""
            };
          } else if (filterFields.includes(fieldName)) {
            updated[fieldName] = newValue;
            updated.material_id = null; // Reset material
            updated.cost_per_unit = null; // Reset cost
          } else if (fieldName === "material_id") {
            updated.material_id = parseInt(newValue, 10) || null;
            updated.cost_per_unit = null;
          } else if (fieldName === "consumable_id") {
            updated.consumable_id = parseInt(newValue, 10) || null;
            updated.cost_per_unit = null;
          } else if (fieldName === "quantity_ordered" || fieldName === "quantity_received") {
            updated[fieldName] = parseInt(newValue, 10) || 0;
          } else if (fieldName === "cost_per_unit") {
            updated.cost_per_unit = parseFloat(newValue) || 0;
          } else {
            updated[fieldName] = newValue;
          }

          return updated;
        }
        return line;
      })
    );
  }

  // =========================================================================
  // 5) Save or Update PO (including line updates)
  // =========================================================================

  function handleViewPOPDF() {
    if (!poId) {
      alert("No PO selected");
      return;
    }
    // Fetch with API key and open in a new browser tab:
    openPdf(poPdfUrl(poId)); // features/purchaseOrders/poData
  }

  function handleViewPOStatement() {
    if (!poId) {
      alert("No PO selected");
      return;
    }
    // Vendor-facing statement: ordered vs received vs outstanding + receipt history
    openPdf(`${API_BASE}/purchase_orders/${poId}/statement_pdf`);
  }

  // Build the post-save alert. Confirms which materials/consumables had their
  // standing vendor cost (+ CWT rule) auto-updated from the PO's line prices,
  // and warns when the supplier didn't resolve to a vendor so nothing silently
  // no-ops. See jobq-api patch_purchase_order `pricing_propagation`.
  function buildSaveFeedback(data) {
    const base = data?.message || "Purchase order updated."
    const pp = data?.pricing_propagation
    if (!pp) return base

    const parts = [base]
    const updated = pp.updated || []
    const skipped = pp.skipped_no_vendor || []
    const vendor = pp.vendor_name || "this supplier"

    if (updated.length > 0) {
      const items = updated.map(u => {
        let s = ` • ${u.label}`
        if (u.item_type === "Material") {
          if (u.cwt_updated && u.implied_cwt != null) {
            s += ` → cost + CWT rule ($${Number(u.implied_cwt).toFixed(2)}/cwt)`
          } else {
            s += ` → cost updated; CWT rule skipped${u.cwt_skip_reason ? ` (${u.cwt_skip_reason})` : ""}`
          }
        } else {
          s += ` → cost updated`
        }
        return s
      })
      parts.push(
        `\n↻ Updated standing pricing for ${updated.length} item(s) from ${vendor}:\n` +
        items.join("\n")
      )
    }

    if (skipped.length > 0) {
      const names = skipped.map(u => u.label).join(", ")
      parts.push(
        `\n⚠ Supplier "${vendor}" has no matching vendor record, so the standing ` +
        `cost + CWT rule were NOT updated for: ${names}.\n` +
        `Rename the supplier to match a vendor (or add the vendor), then re-save — ` +
        `or update the CWT rule manually.`
      )
    }

    return parts.join("\n")
  }

  async function handleSavePO() {
    setError(null)
    if (!supplier && status !== 'RFQ') { // Allow empty supplier if status is RFQ
      alert("Supplier is required.")
      return
    }

    const payload = {
      supplier,
      order_date: orderDate || null,
      estimated_receive_date: estimatedReceiveDate || null,
      // 0 is valid (due on receipt) — only blank means null (API defaults to Net 30)
      payment_terms_days: paymentTermsDays === "" ? null : parseInt(paymentTermsDays, 10),
      notes,
      project_name: projectName.trim() || null,
      vendor_sales_order: vendorSalesOrder,
      fulfillment,
      confirmed
    }

    try {
      if (!poId) {
        // CREATE a new PO
        const parsedLines = newLines.map(l => {
          const quantity_ordered = parseInt(l.quantity_ordered, 10) || 0
          let costVal = l.cost_per_unit
          if (costVal !== null && costVal !== undefined) {
            costVal = parseFloat(costVal) || 0
          } else {
            costVal = null
          }

          if (l.item_type === "Material") {
            const matId = parseInt(l.material_id, 10) || 0
            if (!matId) {
              throw new Error("You must select a valid Material for all 'Material' lines")
            }
            return {
              item_type: "Material",
              material_id: matId,
              quantity_ordered,
              cost_per_unit: costVal
            }
          } else {
            const consId = parseInt(l.consumable_id, 10) || 0
            if (!consId) {
              throw new Error("You must select a valid Consumable for all 'Consumable' lines")
            }
            return {
              item_type: "Consumable",
              consumable_id: consId,
              quantity_ordered,
              cost_per_unit: costVal
            }
          }
        })

        if (parsedLines.length === 0) {
          alert("You must add at least 1 line to create a PO.")
          return
        }

        const createPayload = {
          supplier: payload.supplier,
          order_date: payload.order_date,
          estimated_receive_date: payload.estimated_receive_date,
          payment_terms_days: payload.payment_terms_days,
          notes: payload.notes,
          project_name: payload.project_name,
          vendor_sales_order: payload.vendor_sales_order,
          fulfillment: payload.fulfillment,
          confirmed: payload.confirmed,
          lines: parsedLines
        }

        const res = await fetch(`${API_BASE}/purchase_orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(createPayload)
        })
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res));
        }
        const data = await res.json()
        alert(`New PO created. ID=${data.purchase_order_id}, PO#=${data.po_number}`)
        loadPurchaseOrders()
      } else {
        // PATCH existing PO
        payload.status = status

        payload.lines = poLines.map(ln => {
          // If flagged for deletion => only send {po_line_id, remove:true}
          if (ln._delete) {
            return {
              po_line_id: ln.po_line_id,
              remove: true
            }
          }
          // Otherwise normal line update
          let costVal = ln.cost_per_unit
          if (costVal !== null && costVal !== undefined) {
            costVal = parseFloat(costVal) || 0
          } else {
            costVal = null
          }

          const isExistingLine = ln.po_line_id && ln.po_line_id > 0

          return {
            po_line_id: isExistingLine ? ln.po_line_id : null,
            item_type: ln.item_type,
            material_id: ln.material_id || null,
            consumable_id: ln.consumable_id || null,
            quantity_ordered: ln.quantity_ordered,
            cost_per_unit: costVal
          }
        })

        const res = await fetch(`${API_BASE}/purchase_orders/${poId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res));
        }
        const data = await res.json()
        alert(buildSaveFeedback(data))
        loadPurchaseOrders()
        setFollowUpRefresh((k) => k + 1)
      }
    } catch (err) {
      setError(err.message)
    }
  }

  // =========================================================================
  // 6) Cancel RFQ / Delete PO
  // =========================================================================

  // An RFQ has no "Cancelled" option in the Status control — that select is
  // replaced by a read-only badge while the PO is out for quote — and Delete is
  // not an option either: every RFQ has been emailed to its vendor, and
  // po_email_history carries a FK to purchase_orders with no ON DELETE action,
  // so DELETE /purchase_orders/{id} dies on the constraint and surfaces as a
  // bare "An internal server error occurred." Cancelling is the non-destructive
  // equivalent and keeps the quote and its email history on file.
  async function handleCancelRFQ() {
    if (!poId) {
      alert("No RFQ selected to cancel.")
      return
    }

    // Cancelling ONE vendor out of a bundle that still has other vendors out
    // for quote wedges the award: _finalize_rfq_bundle refuses any group
    // holding a non-RFQ PO, and PATCH back to 'RFQ' is blocked, so there is no
    // way to undo it from the UI. "Cancel RFQ Bundle" in the comparison view
    // cancels them together, which is the operation that stays coherent.
    //
    // Counted against `purchaseOrders` (the server list) and deliberately NOT
    // `filteredPOs` — the search box is client-side, so searching a vendor name
    // collapses a multi-vendor bundle to a single row, and a single-child group
    // is exactly what opens this editor instead of the comparison grid.
    const groupId = selectedPO?.rfq_group_id
    if (groupId) {
      const openSiblings = purchaseOrders.filter(
        (p) =>
          p.rfq_group_id === groupId &&
          p.status === "RFQ" &&
          p.purchase_order_id !== poId
      )
      if (openSiblings.length > 0) {
        alert(
          `RFQ #${poNumber} is part of a bundle with ${openSiblings.length} other ` +
          `vendor${openSiblings.length === 1 ? "" : "s"} still out for quote ` +
          `(${openSiblings.map((p) => p.supplier).join(", ")}).\n\n` +
          `Cancelling only this vendor would block the whole bundle from being ` +
          `awarded, and it can't be undone. Open the RFQ Bundle from the list and ` +
          `use "Cancel RFQ Bundle" to cancel them together.`
        )
        return
      }
    }

    if (
      !window.confirm(
        `Cancel RFQ #${poNumber}${supplier ? ` to ${supplier}` : ""}?\n\n` +
        `It stays on file as Cancelled, with its lines, quoted prices and email ` +
        `history intact. An RFQ can't be reopened once cancelled.`
      )
    )
      return

    try {
      setLoading(true)
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Cancelled" })
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      alert(`RFQ #${poNumber} cancelled.`)
      await loadPurchaseOrders()
      handleCreateNewPO()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleDeletePO() {
    if (!poId) {
      alert("No PO selected to delete.")
      return
    }
    if (!window.confirm("Really delete this purchase order?")) return
    try {
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}`, {
        method: "DELETE"
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      const data = await res.json()
      alert(data.message)
      loadPurchaseOrders()
      handleCreateNewPO()
    } catch (err) {
      setError(err.message)
    }
  }

  // =========================================================================
  // 7) Receiving: partial or entire (via Shipments Only)
  // =========================================================================
  function handleReceiveFieldChange(po_line_id, val) {
    setReceiveQuantities((prev) => ({ ...prev, [po_line_id]: val }));
  }

  function handleHeatNumberChange(po_line_id, val) {
    setReceiveHeatNumbers((prev) => ({ ...prev, [po_line_id]: val }));
  }

  // Multi-pallet split helpers — used when a single PO line was shipped on
  // multiple pallets with different heat numbers.
  function handleSplitIntoPallets(po_line_id) {
    const seedQty = parseInt(receiveQuantities[po_line_id] || 0, 10) || 0;
    const seedHeat = receiveHeatNumbers[po_line_id] || "";
    setReceiveHandlingUnits((prev) => ({
      ...prev,
      [po_line_id]: [
        { quantity: seedQty || "", heat_number: seedHeat },
        { quantity: "", heat_number: "" },
      ],
    }));
  }

  function handleAddHandlingUnit(po_line_id) {
    setReceiveHandlingUnits((prev) => ({
      ...prev,
      [po_line_id]: [...(prev[po_line_id] || []), { quantity: "", heat_number: "" }],
    }));
  }

  function handleRemoveHandlingUnit(po_line_id, idx) {
    setReceiveHandlingUnits((prev) => {
      const cur = prev[po_line_id] || [];
      const next = cur.filter((_, i) => i !== idx);
      // If only one HU remains the split is no longer meaningful — collapse
      // back to the flat single-pallet inputs and stash its values there.
      if (next.length <= 1) {
        const lone = next[0];
        if (lone) {
          setReceiveQuantities((q) => ({ ...q, [po_line_id]: lone.quantity || "" }));
          setReceiveHeatNumbers((h) => ({ ...h, [po_line_id]: lone.heat_number || "" }));
        }
        const { [po_line_id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [po_line_id]: next };
    });
  }

  function handleHandlingUnitChange(po_line_id, idx, field, val) {
    setReceiveHandlingUnits((prev) => {
      const cur = prev[po_line_id] || [];
      const next = cur.map((hu, i) => (i === idx ? { ...hu, [field]: val } : hu));
      return { ...prev, [po_line_id]: next };
    });
  }

  function handleCancelSplit(po_line_id) {
    setReceiveHandlingUnits((prev) => {
      const { [po_line_id]: _, ...rest } = prev;
      return rest;
    });
  }

  // Total qty across all handling units on a line (used for over-receive
  // checks and the "sum so far" hint in the UI).
  function sumHandlingUnitQty(po_line_id) {
    const hus = receiveHandlingUnits[po_line_id];
    if (!hus) return 0;
    return hus.reduce((acc, hu) => acc + (parseInt(hu.quantity || 0, 10) || 0), 0);
  }

  async function handleReceivePartial() {
    if (!poId) {
      alert("No PO selected to receive.");
      return;
    }
    const today = shopToday(); // the shop's (Central) day: UTC's is tomorrow after 7 PM CT
    const items = [];
    for (const ln of poLines) {
      const lineId = ln.po_line_id;
      const splitHus = receiveHandlingUnits[lineId];

      if (splitHus && ln.item_type === "Material") {
        // Multi-pallet (split) entry path.
        const cleaned = splitHus
          .map((hu) => ({
            quantity: parseInt(hu.quantity || 0, 10) || 0,
            heat_number: (hu.heat_number || "").trim(),
          }))
          .filter((hu) => hu.quantity > 0 || hu.heat_number);
        if (cleaned.length === 0) continue;
        if (cleaned.some((hu) => hu.quantity <= 0)) {
          alert(`Line ${lineId}: every pallet needs a quantity > 0.`);
          return;
        }
        if (cleaned.some((hu) => !hu.heat_number)) {
          alert(`Line ${lineId}: every pallet needs a heat number.`);
          return;
        }
        const total = cleaned.reduce((a, hu) => a + hu.quantity, 0);
        items.push({
          po_line_id: lineId,
          quantity_received: total,
          received_date: today,
          handling_units: cleaned.map((hu) => ({
            quantity_received: hu.quantity,
            heat_number: hu.heat_number,
          })),
        });
        continue;
      }

      const qty = parseInt(receiveQuantities[lineId] || 0, 10);
      const heatNum = receiveHeatNumbers[lineId] || null;

      if (qty > 0) {
        if (!heatNum && ln.item_type === "Material") {
             alert(`Heat Number is required for Material line ${lineId}.`);
             return;
        }
        items.push({
          po_line_id: lineId,
          quantity_received: qty,
          received_date: today,
          heat_number: heatNum
        });
      }
    }
    if (items.length === 0) {
      alert("No line quantities entered for receiving.");
      return;
    }
    try {
      // Since the API is now using Option A (one body param),
      // we send these fields directly at the top level
      const payload = {
        shipment_date: today,
        notes: "Partial receipt",
        lines: items
      };
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}/shipments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      const newShipment = await res.json();
      const completedMsg = newShipment.auto_completed ? " PO auto-marked Completed (all lines fully received)." : "";
      alert(`Shipment #${newShipment.shipment_id} created for partial receiving.${completedMsg}`);
      handleSelectPO({ purchase_order_id: poId });
      loadPurchaseOrders();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleReceiveEntirePO() {
    if (!poId) {
      alert("No PO selected.");
      return;
    }
    if (
      !window.confirm(
        "Are you sure you want to fully receive all remaining lines in a single shipment?"
      )
    ) {
      return;
    }

    const today = shopToday(); // the shop's (Central) day: UTC's is tomorrow after 7 PM CT
    const items = [];
    for (let ln of poLines) {
      const remain = ln.quantity_ordered - ln.quantity_received;
      const splitHus = receiveHandlingUnits[ln.po_line_id];

      if (splitHus && ln.item_type === "Material") {
        // Split path: HU qtys must sum to the line's remaining qty.
        const cleaned = splitHus
          .map((hu) => ({
            quantity: parseInt(hu.quantity || 0, 10) || 0,
            heat_number: (hu.heat_number || "").trim(),
          }))
          .filter((hu) => hu.quantity > 0 || hu.heat_number);
        if (cleaned.length === 0) continue;
        if (cleaned.some((hu) => hu.quantity <= 0)) {
          alert(`Line ${ln.po_line_id}: every pallet needs a quantity > 0.`);
          return;
        }
        if (cleaned.some((hu) => !hu.heat_number)) {
          alert(`Line ${ln.po_line_id}: every pallet needs a heat number.`);
          return;
        }
        const total = cleaned.reduce((a, hu) => a + hu.quantity, 0);
        if (total !== remain) {
          alert(
            `Line ${ln.po_line_id}: pallet quantities sum to ${total} but ${remain} remain on the line. ` +
            `Adjust the split to match before receiving entire order.`
          );
          return;
        }
        items.push({
          po_line_id: ln.po_line_id,
          quantity_received: total,
          received_date: today,
          handling_units: cleaned.map((hu) => ({
            quantity_received: hu.quantity,
            heat_number: hu.heat_number,
          })),
        });
        continue;
      }

      const heatNum = receiveHeatNumbers[ln.po_line_id] || null;

      if (remain > 0) {
         if (!heatNum && ln.item_type === "Material") {
             alert(`Heat Number is required for Material line ${ln.po_line_id}.`);
             return;
        }
        items.push({
          po_line_id: ln.po_line_id,
          quantity_received: remain,
          received_date: today,
          heat_number: heatNum
        });
      }
    }
    if (items.length === 0) {
      alert("All lines are already fully received.");
      return;
    }

    try {
      // Same top-level payload structure, matching the single body parameter
      const payload = {
        shipment_date: today,
        notes: "Full order receipt",
        lines: items
      };
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}/shipments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      const newShipment = await res.json();
      const completedMsg = newShipment.auto_completed ? " PO auto-marked Completed." : "";
      alert(`Created shipment #${newShipment.shipment_id} to fully receive the remaining lines.${completedMsg}`);
      handleSelectPO({ purchase_order_id: poId });
      loadPurchaseOrders();
    } catch (err) {
      alert("Error creating shipment: " + err.message);
    }
  }

  // =========================================================================
  // 8) Delete Shipment
  // =========================================================================
  async function handleDeleteShipment(shipmentId) {
    if (!window.confirm(`Are you sure you want to delete shipment #${shipmentId}?`)) return
    try {
      const res = await fetch(`${API_BASE}/po_shipments/${shipmentId}`, {
        method: "DELETE"
      })
      if (!res.ok) {
        let msg = await res.text()
        try {
          msg = detailToString(JSON.parse(msg).detail) || msg
        } catch (parseErr) { /* keep raw text */ }
        throw new Error(msg)
      }
      const result = await res.json().catch(() => null)
      alert(result?.message || `Shipment #${shipmentId} deleted successfully.`)
      handleSelectPO({ purchase_order_id: poId })
    } catch (err) {
      alert("Error deleting shipment: " + err.message)
    }
  }

  async function handleDeleteShipmentDoc(docId) {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    
    try {
      const res = await fetch(`${API_BASE}/po_shipments/documents/${docId}`, {
        method: "DELETE"
      });
      
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      
      // Refresh the view
      handleSelectPO({ purchase_order_id: poId });
      
    } catch (err) {
      alert("Error deleting document: " + err.message);
    }
  }

  // =========================================================================
  // 9) Attach unlinked docs to shipments
  // =========================================================================
  async function fetchUnlinkedDocsForAttach() {
    try {
      const res = await fetch(`${API_BASE}/receipt_documents?unlinked=true`)
      if (!res.ok) throw new Error(`Failed to load unlinked docs: ${res.status}`)
      const data = await res.json()
      setUnlinkedDocsForAttach(data)
    } catch (err) {
      alert("Error fetching unlinked docs: " + err.message)
    }
  }

  function handleAttachDocChange(e) {
    setAttachDocId(e.target.value)
  }
  function handleAttachShipmentChange(e) {
    setAttachShipmentId(e.target.value)
  }

  async function handleAttachDocumentToShipment() {
    if (!attachDocId || !attachShipmentId) {
      alert("Please select both a document and a shipment.")
      return
    }
    try {
      const doc = unlinkedDocsForAttach.find(
        (d) => d.receipt_document_id === parseInt(attachDocId, 10)
      )
      if (!doc) {
        alert("Doc not found in the unlinked list. Try reloading.")
        return
      }

      const payload = {
        s3_key: doc.s3_key,
        original_filename: doc.original_filename,
        uploaded_by: doc.uploaded_by || "DocQueue",
        notes: "Attached to existing shipment via UI"
      }
      const res = await fetch(`${API_BASE}/po_shipments/${attachShipmentId}/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }

      // Delete old doc record
      const delRes = await fetch(`${API_BASE}/receipt_documents/${doc.receipt_document_id}`, {
        method: "DELETE"
      })
      if (!delRes.ok) {
        throw new Error(await apiErrorMessage(delRes, "Shipment doc created, but failed to delete old record"));
      }

      setUnlinkedDocsForAttach(prev =>
        prev.filter((d) => d.receipt_document_id !== doc.receipt_document_id)
      )
      setAttachDocId("")
      setAttachShipmentId("")

      handleSelectPO({ purchase_order_id: poId })

      alert("Document successfully linked to the shipment.")
    } catch (err) {
      alert("Error attaching doc: " + err.message)
    }
  }

  // Handler for Material Cert Upload
  async function handleUploadMatCert(e, shipmentId, shipmentLineId) {
    const file = e.target.files[0]
    if (!file) return

    // 1. Prompt the user for their PIN
    const userPin = await pinPrompt("Please enter your PIN to upload this document:")
    if (!userPin) {
      // Clear the file input so they can try again if they cancelled
      e.target.value = null
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    // 2. Use the real PIN provided by the user
    formData.append("pin", userPin) 
    
    try {
      setLoading(true)
      
      // --- STEP A: Upload file to S3 bucket (Creates unlinked record) ---
      const uploadRes = await fetch(`${API_BASE}/receiving/upload-image`, {
        method: "POST",
        body: formData
      })
      
      if (!uploadRes.ok) {
        if (uploadRes.status === 401) {
           throw new Error("Invalid PIN. Please try again.")
        }
        throw new Error("S3 Upload Failed")
      }
      
      const uploadData = await uploadRes.json()
      // Capture the ID of the unlinked record so we can clean it up later
      const tempDocId = uploadData.receipt_document_id 
      
      // --- STEP B: Link it to the Shipment Line ---
      const s3Url = uploadData.s3_url
      const s3Key = s3Url.split("amazonaws.com/")[1]

      const payload = {
        s3_key: s3Key,
        original_filename: file.name,
        uploaded_by: "User", 
        notes: "Material Cert",
        shipment_line_id: shipmentLineId,
        doc_type: "Material Cert"
      }

      const linkRes = await fetch(`${API_BASE}/po_shipments/${shipmentId}/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })

      if (!linkRes.ok) throw new Error("Failed to link Material Cert")
      
      // --- STEP C: Clean up the unlinked queue (THE FIX) ---
      // Now that the document is successfully linked to the shipment,
      // we delete the temporary "unlinked" record so it doesn't show on Receiving Page.
      // Your backend delete logic handles keeping the S3 file safe.
      if (tempDocId) {
        await fetch(`${API_BASE}/receipt_documents/${tempDocId}`, {
          method: "DELETE"
        })
      }

      alert("Material Cert attached successfully!")
      handleSelectPO({ purchase_order_id: poId }) // Refresh UI

    } catch (err) {
      alert("Error uploading cert: " + err.message)
    } finally {
      setLoading(false)
      // Reset file input
      e.target.value = null
    }
  }

  function handlePreviewUnlinkedDoc(doc) {
    setPreviewDoc(doc)
  }

  // =========================================================================
  // 10) Documents Queue
  // =========================================================================
  async function fetchUnlinkedDocs() {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/receipt_documents?unlinked=true`)
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      const data = await res.json()
      setUnlinkedDocs(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function handleSelectQueueDoc(doc) {
    setQueueSelectedDoc(doc)
    setQueueSelectedPO("")
    setQueuePOLines([])
    setQueueReceiveQuantities({})
    setQueueReceiveHeatNumbers({})
    setPreviewDoc(doc)
  }

  function handleQueueReceiveEntirePO() {
    const obj = {}
    queuePOLines.forEach((ln) => {
      const needed = ln.quantity_ordered - ln.quantity_received
      if (needed > 0) {
        obj[ln.po_line_id] = needed
      }
    })
    setQueueReceiveQuantities(obj)
  }

  async function loadLinesForQueuePO(poIdVal) {
    if (!poIdVal) {
      setQueuePOLines([])
      return
    }
    try {
      const res = await fetch(`${API_BASE}/purchase_orders/${poIdVal}`)
      if (!res.ok) throw new Error("Failed to fetch PO lines for queue selection.")
      const data = await res.json()
      setQueuePOLines(data.lines || [])
      setQueueReceiveQuantities({})
      setQueueReceiveHeatNumbers({})
    } catch (err) {
      alert(err.message)
      setQueuePOLines([])
    }
  }

  function handleQueuePOChange(e) {
    const val = e.target.value
    setQueueSelectedPO(val)
    if (val) {
      loadLinesForQueuePO(val)
    } else {
      setQueuePOLines([])
      setQueueReceiveQuantities({})
      setQueueReceiveHeatNumbers({})
    }
  }

  function handleQueueQtyChange(po_line_id, val) {
    setQueueReceiveQuantities(prev => ({ ...prev, [po_line_id]: val }))
  }

  function handleQueueHeatNumberChange(po_line_id, val) {
    setQueueReceiveHeatNumbers(prev => ({ ...prev, [po_line_id]: val }))
  }

  async function handleCreateShipmentFromDoc(doc) {
    if (!queueSelectedPO) {
      alert("Please select a PO for this document.")
      return
    }
    const today = new Date().toISOString().substr(0, 10)
    const items = []
    for (let ln of queuePOLines) {
      const lineId = ln.po_line_id
      const qty = parseInt(queueReceiveQuantities[lineId] || 0, 10)
      const heatNum = queueReceiveHeatNumbers[lineId] || null

      if (qty > 0) {
        if (!heatNum && ln.item_type === "Material") {
             alert(`Heat Number is required for Material line ${lineId}.`);
             return; 
        }
        items.push({
          po_line_id: lineId,
          quantity_received: qty,
          received_date: today,
          heat_number: heatNum
        })
      }
    }
    if (items.length === 0) {
      alert("No line quantities entered.")
      return
    }
    try {
      const shipmentPayload = {
        shipment_date: today,
        notes: `Created from doc #${doc.receipt_document_id}`,
        lines: items
      }
      const res = await fetch(`${API_BASE}/purchase_orders/${queueSelectedPO}/shipments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(shipmentPayload)
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      const newShipment = await res.json()
      if (!newShipment.shipment_id) {
        alert("Shipment creation returned no shipment_id.")
        return
      }

      // Attach the doc to the newly created shipment
      const docPayload = {
        s3_key: doc.s3_key,
        original_filename: doc.original_filename,
        uploaded_by: doc.uploaded_by || "DocQueue",
        notes: "Auto-linked from Documents Queue"
      }
      const res2 = await fetch(`${API_BASE}/po_shipments/${newShipment.shipment_id}/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(docPayload)
      })
      if (!res2.ok) {
        throw new Error(await apiErrorMessage(res2));
      }

      // Delete the old doc record
      const delResp = await fetch(`${API_BASE}/receipt_documents/${doc.receipt_document_id}`, {
        method: "DELETE"
      })
      if (!delResp.ok) {
        throw new Error(await apiErrorMessage(delResp, "Shipment created, but doc could not be deleted"));
      }

      alert(
        `Successfully created shipment #${newShipment.shipment_id} for PO ${queueSelectedPO}, ` +
        `and linked doc #${doc.receipt_document_id}.`
      )

      setUnlinkedDocs(prev => prev.filter(d => d.receipt_document_id !== doc.receipt_document_id))

      setQueueSelectedDoc(null)
      setQueueSelectedPO("")
      setQueuePOLines([])
      setQueueReceiveQuantities({})
      setQueueReceiveHeatNumbers({})
    } catch (err) {
      alert("Error creating shipment from doc: " + err.message)
    }
  }

  function handleOpenLabelModal(shipment) {
    if (!shipment.lines || shipment.lines.length === 0) {
      alert("No lines in this shipment.")
      return
    }

    // One row per handling unit (po_shipment_lines row). When a PO line was
    // split across multiple pallets each pallet has its own line + MTN + heat,
    // so the modal shows one row per pallet with default printQty=1 — that
    // way the operator gets the right number of labels with the correct heat
    // on each, no extra clicks needed for the typical case.
    const palletRows = []
    shipment.lines.forEach(shLine => {
      const parentLine = poLines.find(pl => pl.po_line_id === shLine.po_line_id)
      if (parentLine && parentLine.item_type === "Material") {
        palletRows.push({
          shipment_line_id: shLine.shipment_line_id,
          material_id: parentLine.material_id,
          material_code: parentLine.material_code,
          description: parentLine.material_description,
          heat_number: shLine.heat_number,
          mtn_id: shLine.mtn_id,
          mtn_display: shLine.tracking_number_display,
          quantity_received: shLine.quantity_received,
          printQty: 1
        })
      }
    })

    if (palletRows.length === 0) {
      alert("No material lines found in this shipment to label.")
      return
    }

    setLabelModalItems(palletRows)
    setLabelModalShipmentId(shipment.shipment_id)
    setShowLabelModal(true)
  }

  function handleLabelQtyChange(index, newQty) {
    const updated = [...labelModalItems]
    updated[index].printQty = Math.max(0, parseInt(newQty) || 0)
    setLabelModalItems(updated)
  }

  async function handleGenerateLabels() {
    try {
      const payload = {
        items: labelModalItems
          .filter(i => i.printQty > 0)
          .map(i => ({
            shipment_line_id: i.shipment_line_id,
            quantity: i.printQty
          }))
      }

      if (payload.items.length === 0) {
        alert("Total label quantity is 0.")
        return
      }

      setLoading(true)
      const res = await fetch(`${API_BASE}/po_shipments/${labelModalShipmentId}/labels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })

      if (!res.ok) throw new Error("Failed to generate labels")

      // Handle PDF download
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Shipment_${labelModalShipmentId}_Labels.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setShowLabelModal(false)

    } catch (err) {
      alert("Error: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  // =========================================================================
  // 11) Email Purchase Order
  // =========================================================================

  function handleEmailPO() {
    if (!poId) {
      alert("No PO selected.");
      return;
    }
    setEmailDocType('po');
    setShowEmailModal(true);
  }

  function handleEmailStatement() {
    if (!poId) {
      alert("No PO selected.");
      return;
    }
    setEmailDocType('statement');
    setShowEmailModal(true);
  }

  async function handleSendPOEmail(body, includeDrawings = true) {
    const targetPoId = rfqComparisonPoId || poId;
    setEmailSending(true);
    try {
      const res = await fetch(`${API_BASE}/purchase_orders/${targetPoId}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...emailSenderHeaders() },
        body: JSON.stringify({
          email_body: body,
          document: emailDocType,
          include_drawings: includeDrawings,
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to send email");
      }

      const data = await res.json();
      // Say what actually went out — a PO that silently dropped a drawing for
      // size should not look identical to one that carried it.
      let note = data.message;
      if (data.drawings_attached) {
        note += `\n\n${data.drawings_attached} drawing(s) attached.`;
      }
      if (data.drawings_skipped && data.drawings_skipped.length) {
        note += `\n\nToo large to attach: ${data.drawings_skipped.join(", ")}. `
              + `The email says they are available on request.`;
      }
      alert(note);
      setShowEmailModal(false);
      setRfqComparisonPoId(null);
      setRfqComparisonSupplier("");
      setEmailDocType('po');
      // Refresh PO list to update email_count
      loadPurchaseOrders();
      setFollowUpRefresh((k) => k + 1);
    } catch (err) {
      alert("Error sending email: " + err.message);
    } finally {
      setEmailSending(false);
    }
  }

  function handleShowEmailHistory() {
    if (!poId) return;
    setShowEmailHistory(true);
  }

  // =========================================================================
  // 13) Helpers
  // =========================================================================
  function computeVendorETA(vendor) {
    let rawEta = new Date()
    if (vendor.lead_time_days) {
      rawEta.setDate(rawEta.getDate() + vendor.lead_time_days)
    }
    if (vendor.delivery_day_of_week) {
      const desiredDow = parseInt(vendor.delivery_day_of_week, 10)
      while (rawEta.getDay() !== desiredDow) {
        rawEta.setDate(rawEta.getDate() + 1)
      }
    }
    return rawEta
  }

  function handleConsumableCheck(consumableId) {
    setConsumableRows(prev =>
      prev.map(item => {
        if (item.consumable_id === consumableId) {
          return { ...item, selected: !item.selected }
        }
        return item
      })
    )
  }

  function handleConsumableVendorChange(consumableId, newVendor) {
    setConsumableRows(prev =>
      prev.map(item => {
        if (item.consumable_id === consumableId) {
          return { ...item, vendorOverride: newVendor }
        }
        return item
      })
    )
  }

  function handleMaterialCheck(materialId) {
    setMaterialRows(prev =>
      prev.map(item => {
        if (item.material_id === materialId) {
          return { ...item, selected: !item.selected }
        }
        return item
      })
    )
  }

  function handleMaterialVendorChange(materialId, newVendor) {
    setMaterialRows(prev =>
      prev.map(item => {
        if (item.material_id === materialId) {
          return { ...item, vendorOverride: newVendor }
        }
        return item
      })
    )
  }

  function handleJobStopCheck(materialId) {
    setJobStopRows(prev =>
      prev.map(item => (
        item.material_id === materialId ? { ...item, selected: !item.selected } : item
      ))
    )
  }

  function handleJobStopSelectAll(checked) {
    // A stock drop is cut in-house: never ticked for a PO.
    setJobStopRows(prev => prev.map(item => ({ ...item, selected: checked && !item.madeInHouse })))
  }

  // Preview (GET) or run (POST) the drop planner: switch jobs to their full-sheet cut
  // sheet, add sheet-division jobs (jobq-api drop_planner.py).
  async function runDropPlan(apply) {
    setDropPlanBusy(true)
    try {
      const res = await fetch(`${API_BASE}/materials/drop_plan`, { method: apply ? "POST" : "GET" })
      if (!res.ok) throw new Error(await apiErrorMessage(res))
      const report = await res.json()
      setDropPlan({ lines: dropPlanSummary(report), preview: !apply, heading: dropPlanHeading(report, !apply),
                    canApply: !apply && report.mode !== 'off' &&
                      ((report.switches || []).length + (report.division_jobs || []).length) > 0 })
      if (apply) fetchMaterialShortages()
    } catch (err) {
      setDropPlan({ lines: [`Drop planner failed: ${err.message}`], preview: !apply, canApply: false,
                    heading: 'Drop planner — failed:' })
    } finally {
      setDropPlanBusy(false)
    }
  }

  function handleJobStopVendorChange(materialId, newVendor) {
    setJobStopRows(prev =>
      prev.map(item => (
        item.material_id === materialId
          ? { ...item, vendorOverride: newVendor, hasVendor: newVendor.trim() !== "" }
          : item
      ))
    )
  }

  // =========================================================================
  // 14) Creating multiple POs (grouped by vendor) for Consumables/Materials
  // =========================================================================
  // opts.note replaces the PO note; opts.onDone replaces the default
  // "untick this card's rows" step for a caller that owns its own rows.
  async function createPOsFromSelectedItems(itemRows, itemTypeLabel, opts = {}) {
    const chosen = itemRows.filter(r => r.selected)
    if (chosen.length === 0) {
      alert(`No ${itemTypeLabel} selected!`)
      return
    }

    const byVendor = {}
    for (let row of chosen) {
      const v = row.vendorOverride.trim()
      if (!byVendor[v]) {
        byVendor[v] = []
      }
      byVendor[v].push(row)
    }

    const totalItems = chosen.length
    const vendorCount = Object.keys(byVendor).length
    if (!window.confirm(
      `You are about to create ${vendorCount} Purchase Order(s) for ${totalItems} ${itemTypeLabel} items. Proceed?`
    )) {
      return
    }

    let resultMsg = "Created POs:\n"
    for (let vendorName of Object.keys(byVendor)) {
      const itemsArr = byVendor[vendorName]
      const linesPayload = itemsArr.map(it => {
        if (itemTypeLabel === "Consumable") {
          return {
            item_type: "Consumable",
            consumable_id: it.consumable_id,
            quantity_ordered: it.recommendedQty,
            cost_per_unit: null
          }
        } else {
          return {
            item_type: "Material",
            material_id: it.material_id,
            quantity_ordered: it.recommendedQty,
            cost_per_unit: null
          }
        }
      })

      const poPayload = {
        supplier: vendorName,
        order_date: shopToday(),
        estimated_receive_date: null,
        notes: opts.note || `Auto-created from low stock ${itemTypeLabel} alert`,
        lines: linesPayload
      }

      try {
        const res = await fetch(`${API_BASE}/purchase_orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(poPayload)
        })
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res));
        }
        const data = await res.json()
        resultMsg += ` - PO#${data.po_number} for vendor "${vendorName}"\n`
      } catch (err) {
        alert(`Failed creating PO for vendor ${vendorName}: ${err.message}`)
      }
    }

    alert(resultMsg)
    loadPurchaseOrders()

    if (typeof opts.onDone === "function") {
      opts.onDone()
    } else if (itemTypeLabel === "Consumable") {
      setConsumableRows(prev => prev.map(r => ({ ...r, selected: false })))
    } else {
      setMaterialRows(prev => prev.map(r => ({ ...r, selected: false })))
    }
  }

  // =========================================================================
  // 15) Dashboard
  // =========================================================================
  function renderDashboard() {
    const todayStr = shopToday() // the shop's (Central) day, as the phone's list
    const overduePOs = purchaseOrders.filter(po => {
      if (!po.estimated_receive_date) return false
      if (po.status === "Completed" || po.status === "Cancelled") return false
      return po.estimated_receive_date < todayStr
    })

    const renderOpenPurchasesTable = () => {
      if (!openClosedLines || openClosedLines.length === 0) {
        return <p>No open purchase items found.</p>
      }
      return (
        <table className="table table-sm table-hover">
          <thead className="table-light">
            <tr>
              <th>PO#</th>
              <th>Supplier</th>
              <th>Item</th>
              <th>Description</th>
              <th>Ordered</th>
              <th>Received</th>
              <th>Remain</th>
            </tr>
          </thead>
          <tbody>
            {openClosedLines.map((ln, idx) => (
              <tr key={idx}>
                <td>{ln.po_number}</td>
                <td>{ln.supplier}</td>
                <td>{ln.itemCode}</td>
                <td>{ln.description}</td>
                <td>{ln.qtyOrdered}</td>
                <td>{ln.qtyReceived}</td>
                <td className="fw-bold text-danger">{ln.qtyRemaining}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }

    const filteredConsumableRows = consumableRows.filter(r => {
      if (consumableVendorFilter === "All") return true
      return r.vendorOverride === consumableVendorFilter
    })

    const filteredMaterialRows = materialRows.filter(r => {
      if (materialVendorFilter === "All") return true
      return r.vendorOverride === materialVendorFilter
    })

    // Job-stopping shortages: the one card on this page that means "a job is
    // already waiting on material we do not have and have not ordered". It is
    // the reserved > qoh + inbound subset of the floor-based material alerts
    // it sits above, with no vendor filter on purpose — a filter that hides a
    // job-stopper is how one gets missed.
    const renderJobStopShortagesCard = () => {
      const hot = jobStopRows.length > 0
      const orderable = orderableJobStopRows(jobStopRows)
      const drops = jobStopRows.filter(r => r.madeInHouse)
      const noVendor = orderable.filter(r => !r.vendorOverride.trim())
      const selected = orderable.filter(r => r.selected)
      const vendorNames = vendors.map(v => v.name)
      // A supplier string that is not in the vendor list still has to be
      // offered, or the select would silently show the first vendor instead.
      const optionsFor = (current) =>
        current && !vendorNames.includes(current) ? [current, ...vendorNames] : vendorNames

      const createPOs = () => {
        const blank = selected.filter(r => !r.vendorOverride.trim())
        if (blank.length > 0) {
          alert(`Pick a vendor first for: ${blank.map(r => r.material_code).join(", ")}`)
          return
        }
        createPOsFromSelectedItems(orderable, "Material", {
          note: "Auto-created from Job-Stopping Material Shortages (reserved to open jobs exceeds on hand + inbound)",
          onDone: () => {
            setJobStopRows(prev => prev.map(r => ({ ...r, selected: false })))
            fetchMaterialShortages()
          }
        })
      }

      const createRfqBundle = () => {
        setPendingNewLines(selected.map(item => ({
          item_type: "Material",
          material_id: item.material_id,
          quantity_ordered: item.recommendedQty,
          cost_per_unit: null
        })))
        setShowRFQModal(true)
      }

      return (
        <div className={`card mb-3${hot ? " border-danger" : ""}`} data-testid="job-stop-shortages">
          <div className={`card-header d-flex align-items-center${hot ? " bg-danger text-white" : ""}`}>
            <span className="fw-bold">Job-Stopping Material Shortages</span>
            {hot && (
              <span className="badge bg-white text-danger ms-2" data-testid="job-stop-count">
                {jobStopRows.length}
              </span>
            )}
            <InfoTooltip
              className={hot ? "text-white" : "text-primary"}
              text="Materials whose quantity reserved to open jobs exceeds on hand + inbound (what is still to arrive on Committed POs, plus the stock drops queued laser jobs will cut). Short = Reserved − (QoH + Inbound): the units that must arrive before those jobs can run. Create PO orders exactly that, as a Committed PO, so the row clears as soon as the order is placed — add safety stock from Low Stock Material Alerts. A stock drop (Made in-house) is never bought: Plan drop cuts switches jobs to their full-sheet cut sheet or adds a sheet-division job. Materials with nothing reserved never appear here, even at negative stock."
            />
          </div>
          <div className="card-body">
            {!hot ? (
              <p className="mb-0 text-success">
                Every material reserved to an open job is covered by stock on hand or a Committed PO.
              </p>
            ) : (
              <>
                {noVendor.length > 0 && (
                  <div className="alert alert-warning py-2 small">
                    <strong>{noVendor.length} job-stopper(s) have no vendor</strong>:{" "}
                    {noVendor.map(r => r.material_code).join(", ")}. Pick one in the row to
                    order it here, or add a vendor on the Materials page.
                  </div>
                )}
                <table className="table table-sm mb-2">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          title="Select all"
                          aria-label="Select all job-stopping shortages"
                          checked={orderable.length > 0 && selected.length === orderable.length}
                          disabled={orderable.length === 0}
                          onChange={(e) => handleJobStopSelectAll(e.target.checked)}
                        />
                      </th>
                      <th>Vendor</th>
                      <th>Code</th>
                      <th>Description</th>
                      <th>QoH</th>
                      <th>Inbound</th>
                      <th>Reserved</th>
                      <th>Short</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobStopRows.map((row) => (
                      <tr key={row.material_id}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.material_code}`}
                            checked={row.selected}
                            disabled={row.madeInHouse}
                            title={row.madeInHouse ? "A stock drop is cut in-house, not bought" : undefined}
                            onChange={() => handleJobStopCheck(row.material_id)}
                          />
                        </td>
                        <td>
                          {row.madeInHouse ? (
                            <span
                              className="badge bg-secondary"
                              data-testid="job-stop-made-in-house"
                              title={row.dropMakers.length
                                ? `Cut in-house from: ${row.dropMakers.join(", ")}`
                                : "Cut in-house"}
                            >
                              Made in-house
                            </span>
                          ) : (
                          <select
                            className="form-select form-select-sm"
                            aria-label={`Vendor for ${row.material_code}`}
                            value={row.vendorOverride}
                            onChange={(e) => handleJobStopVendorChange(row.material_id, e.target.value)}
                          >
                            {!row.vendorOverride && <option value="">— pick a vendor —</option>}
                            {optionsFor(row.vendorOverride).map(n => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                          )}
                        </td>
                        <td>{row.material_code}</td>
                        <td>{row.description}</td>
                        <td className={row.qoh < 0 ? "text-danger" : ""}>{row.qoh}</td>
                        <td>
                          {row.inbound + row.dropInbound}
                          {row.dropInbound > 0 && (
                            <div className="text-muted small" title="Stock drops the queued laser jobs will cut">
                              {row.dropInbound} from queued cuts
                            </div>
                          )}
                        </td>
                        <td>{row.reserved}</td>
                        <td className="text-danger fw-bold">{row.shortfall}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  className="btn btn-sm btn-danger me-2"
                  disabled={selected.length === 0}
                  onClick={createPOs}
                >
                  Create PO(s) from Selected
                </button>
                <button
                  className="btn btn-sm btn-outline-dark"
                  disabled={selected.length === 0}
                  onClick={createRfqBundle}
                >
                  Create RFQ Bundle
                </button>
                {selected.length === 0 && orderable.length > 0 && (
                  <span className="text-muted small ms-2">Tick rows to order the Short quantity.</span>
                )}
                {drops.length > 0 && (
                  <button
                    className="btn btn-sm btn-outline-primary ms-2"
                    disabled={dropPlanBusy}
                    onClick={() => runDropPlan(false)}
                    data-testid="job-stop-plan-drops"
                  >
                    {dropPlanBusy ? "Planning…" : "Plan drop cuts"}
                  </button>
                )}
              </>
            )}
            {dropPlan && (
              <div className="alert alert-info py-2 small mt-2 mb-0" data-testid="job-stop-drop-plan">
                <strong>{dropPlan.heading}</strong>
                <ul className="mb-1 ps-3">
                  {dropPlan.lines.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
                {dropPlan.canApply && (
                  <button className="btn btn-sm btn-primary me-2" disabled={dropPlanBusy}
                          onClick={() => runDropPlan(true)}>
                    {dropPlanBusy ? "Working…" : "Do it"}
                  </button>
                )}
                <button className="btn btn-sm btn-link p-0" onClick={() => setDropPlan(null)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )
    }

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Purchase Orders Dashboard</h2>
          <div>
            <select
              className="form-select form-select-sm d-inline-block w-auto"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last 12 Months</option>
            </select>
          </div>
        </div>

        {/* Material Tracking Number Search */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-bold">Material Tracking Number Lookup</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter tracking number, material code, heat #, or PO..."
                    value={mtnSearchTerm}
                    onChange={(e) => setMtnSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!mtnSearchTerm.trim()) return;
                        setMtnSearching(true);
                        fetch(`${API_BASE}/material_tracking/search?q=${encodeURIComponent(mtnSearchTerm.trim())}`)
                          .then(r => r.ok ? r.json() : [])
                          .then(data => setMtnSearchResults(data))
                          .catch(() => setMtnSearchResults([]))
                          .finally(() => setMtnSearching(false));
                      }
                    }}
                  />
                  <button
                    className="btn btn-outline-dark"
                    disabled={mtnSearching || !mtnSearchTerm.trim()}
                    onClick={() => {
                      if (!mtnSearchTerm.trim()) return;
                      setMtnSearching(true);
                      fetch(`${API_BASE}/material_tracking/search?q=${encodeURIComponent(mtnSearchTerm.trim())}`)
                        .then(r => r.ok ? r.json() : [])
                        .then(data => setMtnSearchResults(data))
                        .catch(() => setMtnSearchResults([]))
                        .finally(() => setMtnSearching(false));
                    }}
                  >
                    {mtnSearching ? "..." : "Search"}
                  </button>
                </div>
              </div>
              <div className="col-md-8">
                {mtnSearchResults.length > 0 && (
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>TRK#</th>
                        <th>Material</th>
                        <th>Source</th>
                        <th>PO / Customer</th>
                        <th>Heat #</th>
                        <th>Qty</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mtnSearchResults.map(r => (
                        <tr key={r.mtn_id}>
                          <td><strong>{r.tracking_number_display}</strong></td>
                          <td>{r.material_code || '—'}</td>
                          <td>
                            {/* 'remnant' (a remnant's own lot, 2026-10-14) and 'transfer' carry their parent's PO / customer */}
                            <span className={`badge bg-${r.source_type === 'po_receipt' ? 'primary' : r.source_type === 'remnant' ? 'warning text-dark' : r.source_type === 'transfer' ? 'secondary' : 'success'}`}>
                              {r.source_type === 'po_receipt' ? 'PO' : r.source_type === 'remnant' ? 'Remnant' : r.source_type === 'transfer' ? 'Transfer' : 'Customer'}
                            </span>
                          </td>
                          <td>
                            {r.source_type === 'po_receipt'
                              ? `PO#${r.po_number} / Ship ${r.shipment_id}`
                              : (r.source_type === 'remnant' || r.source_type === 'transfer')
                                ? [r.parent_mtn_id ? `from TRK ${String(r.parent_mtn_id).padStart(6, '0')}` : null,
                                   r.po_number ? `PO#${r.po_number}` : r.customer_name].filter(Boolean).join(' · ')
                                : r.customer_name}
                          </td>
                          <td>{r.heat_number || '—'}</td>
                          <td>{r.quantity}</td>
                          <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {mtnSearchResults.length === 0 && mtnSearchTerm && !mtnSearching && (
                  <p className="text-muted mb-0">No results found.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Row #1: Low Stock Alerts. Right column stacks the job-stopping
            shortages (urgent, reserved > on hand + inbound) above the
            floor-based material alerts they are a subset of. */}
        <div className="row">
          <div className="col-md-6">
            <div className="card mb-3 h-100">
              <div className="card-header">
                Low Stock Consumable Alerts
                <InfoTooltip text="Items whose available position — on hand + inbound POs − quantity reserved to open jobs — has fallen below their reorder point. A reorder point of 0 means no safety stock is wanted, so these alert as soon as the available position goes negative." />
              </div>
              <div className="card-body">
                {consumableNoVendor.length > 0 && (
                  <div className="alert alert-warning py-2 small">
                    <strong>{consumableNoVendor.length} short item(s) have no vendor</strong> and
                    can't be put on a PO here:{" "}
                    {consumableNoVendor.map(r => r.product_id).join(", ")}. Add a vendor on the
                    Components page (or build them in-house).
                  </div>
                )}
                {consumableRows.length === 0 ? (
                  <p>Nothing with a vendor needs ordering right now.</p>
                ) : (
                  <>
                    <div className="mb-2">
                      <label>Filter by Vendor:</label>
                      <select
                        className="form-select form-select-sm"
                        value={consumableVendorFilter}
                        onChange={(e) => setConsumableVendorFilter(e.target.value)}
                      >
                        <option value="All">All</option>
                        {vendors.map(v => (
                          <option key={v.vendor_id} value={v.name}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Vendor</th>
                          <th>Code</th>
                          <th>Description</th>
                          <th>Stock</th>
                          <th>Reserved</th>
                          <th>Inbound</th>
                          <th>Available</th>
                          <th>Reorder Pt</th>
                          <th>MOQ</th>
                          <th>Qty Multiple</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredConsumableRows.map((row) => (
                          <tr key={row.consumable_id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.selected}
                                onChange={() => handleConsumableCheck(row.consumable_id)}
                              />
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                value={row.vendorOverride}
                                onChange={(e) =>
                                  handleConsumableVendorChange(row.consumable_id, e.target.value)
                                }
                              >
                                {vendors.map(v => (
                                  <option key={v.vendor_id} value={v.name}>
                                    {v.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>{row.product_id}</td>
                            <td>{row.description}</td>
                            <td>{row.stock_quantity}</td>
                            <td>{row.reserved_quantity > 0 ? row.reserved_quantity : "-"}</td>
                            <td>{row.inbound_qty > 0 ? row.inbound_qty : "-"}</td>
                            <td className={row.available < 0 ? "text-danger fw-bold" : ""}>
                              {row.available}
                            </td>
                            <td>{row.reorder_point > 0 ? row.reorder_point : "-"}</td>
                            <td>{row.min_order_qty > 1 ? row.min_order_qty : "-"}</td>
                            <td>{row.order_in_qty_of > 1 ? row.order_in_qty_of : "-"}</td>
                            <td className="fw-bold">{row.recommendedQty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => createPOsFromSelectedItems(filteredConsumableRows, "Consumable")}
                    >
                      Create PO(s) from Selected Consumables
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            {renderJobStopShortagesCard()}
            <div className="card mb-3">
              <div className="card-header">
                Low Stock Material Alerts
                <InfoTooltip text="Materials currently below minimum available quantity." />
              </div>
              <div className="card-body">
                {materialRows.length === 0 ? (
                  <p>No materials below minimum available (with a vendor) right now.</p>
                ) : (
                  <>
                    <div className="mb-2">
                      <label>Filter by Vendor:</label>
                      <select
                        className="form-select form-select-sm"
                        value={materialVendorFilter}
                        onChange={(e) => setMaterialVendorFilter(e.target.value)}
                      >
                        <option value="All">All</option>
                        {vendors.map(v => (
                          <option key={v.vendor_id} value={v.name}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Vendor</th>
                          <th>Code</th>
                          <th>Description</th>
                          <th>QoH</th>
                          <th>Inbound</th>
                          <th>Reserved</th>
                          <th>Min Avail</th>
                          <th>Recomm.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMaterialRows.map((row) => (
                          <tr key={row.material_id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.selected}
                                onChange={() => handleMaterialCheck(row.material_id)}
                              />
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                value={row.vendorOverride}
                                onChange={(e) =>
                                  handleMaterialVendorChange(row.material_id, e.target.value)
                                }
                              >
                                {vendors.map(v => (
                                  <option key={v.vendor_id} value={v.name}>
                                    {v.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>{row.material_code}</td>
                            <td>{row.description}</td>
                            <td>{row.qoh}</td>
                            <td>{row.inbound}</td>
                            <td>{row.reserved}</td>
                            <td>{row.minimum_available}</td>
                            <td>{row.recommendedQty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => createPOsFromSelectedItems(filteredMaterialRows, "Material")}
                    >
                      Create PO(s) from Selected Materials
                    </button>
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => {
                        // FIX: Use filteredMaterialRows here
                        const selected = filteredMaterialRows.filter(r => r.selected);
                        
                        if(selected.length === 0) { alert("Select items first"); return; }
                        
                        // FIX: Set item_type to "Material"
                        const formatted = selected.map(item => ({
                            item_type: "Material",
                            material_id: item.material_id, // Use material_id
                            quantity_ordered: item.recommendedQty,
                            cost_per_unit: null 
                        }));
                        setPendingNewLines(formatted);
                        setShowRFQModal(true);
                      }}
                    >
                      Create RFQ Bundle
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <WeeklyBuyPlanCard 
          onCreateRFQ={(items) => {
            // 1. Pass the formatted items to the pending state
            setPendingNewLines(items);
            // 2. Open the RFQ Modal
            setShowRFQModal(true);
          }}
        />

        {/* Row #2: Overdue POs */}
        <div className="row mt-3">
          <div className="col-md-6">
            <div className="card mb-3 h-100">
              <div className="card-header bg-danger text-white">
                Overdue Purchase Orders
                <InfoTooltip text="Orders that have passed their Estimated Receive Date and are not yet Completed." />
              </div>
              <div className="card-body">
                {overduePOs.length === 0 ? (
                  <p>No overdue POs.</p>
                ) : (
                  <ul className="list-group">
                    {overduePOs.map(po => (
                      <li key={po.purchase_order_id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>PO#{po.po_number} - {po.supplier}</span>
                        <span className="badge bg-light text-dark">Due: {po.estimated_receive_date}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card mb-3 h-100">
              <div className="card-header">
                On-Time vs Overdue
                <InfoTooltip text="Percentage of committed orders currently on-time vs overdue." />
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <label>Filter Supplier:</label>
                  <select
                    className="form-select form-select-sm w-auto"
                    value={selectedSupplier}
                    onChange={(e) => setSelectedSupplier(e.target.value)}
                  >
                    <option value="All">All Suppliers</option>
                    {[...new Set(purchaseOrders.map(po => po.supplier).filter(Boolean))].map(sup => (
                      <option key={sup} value={sup}>{sup}</option>
                    ))}
                  </select>
                </div>
                <div style={{ maxHeight: '250px', display: 'flex', justifyContent: 'center' }}>
                  {onTimeOverdueData ? <Doughnut data={onTimeOverdueData} /> : <p>No data</p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row #4: Avg Days Late (Replaced Lead Time) */}
        <div className="row">
          <div className="col-md-12">
            <div className="card mb-3">
              <div className="card-header">
                Avg Days Late by Supplier
                <InfoTooltip text="Average difference between Actual Receive Date and Estimated Date. Positive = Late, Negative = Early." />
              </div>
              <div className="card-body">
                {leadTimeData ? (
                  <Bar
                    data={leadTimeData}
                    options={{
                      indexAxis: "y",
                      responsive: true,
                      scales: {
                        x: {
                          title: { display: true, text: "Days (Pos=Late, Neg=Early)" },
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                ) : (
                  <p>No data to display.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Row #5: Spend & Volume */}
        <div className="row">
          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header">
                Top 10 Items by Spend
                <InfoTooltip text="Items with highest total cost in selected period." />
              </div>
              <div className="card-body">
                {top10SpendData ? (
                  <Bar
                    data={top10SpendData}
                    options={{ indexAxis: "y", responsive: true, scales: { x: { beginAtZero: true } } }}
                  />
                ) : <p>No data</p>}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header">
                Top 10 Items by Volume
                <InfoTooltip text="Items with highest quantity ordered in selected period." />
              </div>
              <div className="card-body">
                {top10VolumeData ? (
                  <Bar
                    data={top10VolumeData}
                    options={{ indexAxis: "y", responsive: true, scales: { x: { beginAtZero: true } } }}
                  />
                ) : <p>No data</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Row #6: Open Purchases Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card mb-3">
              <div className="card-header">
                Open Purchases (by PO & Product)
                <InfoTooltip text="List of individual line items that have not yet been fully received." />
              </div>
              <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {renderOpenPurchasesTable()}
              </div>
            </div>
          </div>
        </div>

        {/* Row #7: Average Cost with Filter */}
        <div className="row">
          <div className="col-md-12">
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span>
                  Average Cost Over Time
                  <InfoTooltip text="Trend of unit cost. Select a specific item to see its history." />
                </span>
                <select 
                  className="form-select form-select-sm w-auto"
                  value={avgCostFilter}
                  onChange={(e) => setAvgCostFilter(e.target.value)}
                >
                  <option value="All">All Items (Aggregate)</option>
                  {availableItems.map(it => (
                    <option key={it.key} value={it.key}>{it.label}</option>
                  ))}
                </select>
              </div>
              <div className="card-body">
                {avgCostData ? (
                  <Line
                    data={avgCostData}
                    options={{ responsive: true, scales: { y: { beginAtZero: false, title: { display: true, text: "Cost/Unit ($)" } } } }}
                  />
                ) : <p>No data</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Row #8: Rolling Spend */}
        <div className="row">
          <div className="col-md-12">
            <div className="card mb-3">
              <div className="card-header">
                Rolling 12-Month Spend
                <InfoTooltip text="Total spend per month over the last year." />
              </div>
              <div className="card-body">
                {rolling12Data ? (
                  <Line
                    data={rolling12Data}
                    options={{ responsive: true, scales: { y: { beginAtZero: true, title: { display: true, text: "Total Spend ($)" } } } }}
                  />
                ) : <p>No data</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // =========================================================================
  // 16) RFQ Comparison View Helper
  // =========================================================================

  // Save CWT-based prices for a single vendor PO from the comparison table
  async function handleSaveRFQPrices(poId) {
    const po = rfqGroupView?.childPOs?.find(p => p.purchase_order_id === poId);
    if (!po) return;

    // Build updated lines with unit-price OR CWT edits applied.
    // Unit edit wins when both are set for the same cell.
    const updatedLines = (po.lines || []).map(line => {
      const key = line.item_type === "Material"
        ? `M-${line.material_id}`
        : `C-${line.consumable_id}`;
      const editKey = `${poId}-${key}`;
      const unitVal = parseFloat(rfqUnitEdits[editKey]);
      const cwtVal = parseFloat(rfqCwtEdits[editKey]);

      if (!isNaN(unitVal) && unitVal > 0) {
        return { ...line, cost_per_unit: parseFloat(unitVal.toFixed(4)) };
      }
      if (!isNaN(cwtVal) && cwtVal > 0 && line.material_id) {
        const weight = getMaterialWeight(line.material_id);
        if (weight > 0) {
          const newCost = parseFloat(((weight / 100) * cwtVal).toFixed(4));
          return { ...line, cost_per_unit: newCost };
        }
      }
      return line;
    });

    try {
      setRfqSaving(true);
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines: updatedLines })
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));

      // Update rfqGroupView with saved prices so the comparison table refreshes
      setRfqGroupView(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          childPOs: prev.childPOs.map(p =>
            p.purchase_order_id === poId ? { ...p, lines: updatedLines } : p
          )
        };
      });

      // Also refresh the full PO list in background
      loadPurchaseOrders();

      // Clear edits for this PO (both CWT and unit-price edits)
      const clearedCwt = { ...rfqCwtEdits };
      const clearedUnit = { ...rfqUnitEdits };
      Object.keys(clearedCwt).forEach(k => {
        if (k.startsWith(`${poId}-`)) delete clearedCwt[k];
      });
      Object.keys(clearedUnit).forEach(k => {
        if (k.startsWith(`${poId}-`)) delete clearedUnit[k];
      });
      setRfqCwtEdits(clearedCwt);
      setRfqUnitEdits(clearedUnit);
    } catch (err) {
      alert("Error saving prices: " + err.message);
    } finally {
      setRfqSaving(false);
    }
  }

  // Name (or rename) the open bundle. The API writes the name to every PO in
  // the bundle, so the sidebar card and each vendor PO read the same.
  async function handleSaveRFQProjectName() {
    const groupId = rfqGroupView?.rfq_group_id || rfqGroupView?.childPOs?.[0]?.rfq_group_id;
    if (!groupId || rfqNameDraft === null) return;
    try {
      setRfqSaving(true);
      const res = await fetch(`${API_BASE}/purchase_orders/rfq_group/${groupId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_name: rfqNameDraft.trim() || null })
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json();
      setRfqGroupView(prev => prev && {
        ...prev,
        project_name: data.project_name,
        childPOs: (prev.childPOs || []).map(p => ({ ...p, project_name: data.project_name })),
      });
      setRfqNameDraft(null);
      loadPurchaseOrders();
    } catch (err) {
      alert("Error saving project name: " + err.message);
    } finally {
      setRfqSaving(false);
    }
  }

  // Save quantity edits for ALL vendor RFQs in the bundle in lockstep.
  // Qty is shared across every child PO (same item appears with the same qty
  // on each vendor's RFQ), so a single Save broadcasts the new qty to each
  // PO via parallel PATCH calls. Mirrors handleSaveRFQPrices but updates all
  // childPOs at once instead of one vendor at a time.
  async function handleSaveRFQQuantities() {
    if (!rfqGroupView?.childPOs) return;

    // Collect pending edits as a Map<itemKey, parsedQty>
    const parsedEdits = {};
    Object.entries(rfqQtyEdits).forEach(([key, val]) => {
      const n = parseInt(val, 10);
      if (!isNaN(n) && n > 0) parsedEdits[key] = n;
    });
    if (Object.keys(parsedEdits).length === 0) return;

    // Build per-PO updatedLines arrays.
    //
    // A single material can have N weekly lines per vendor (Weekly Buy Plan
    // generates one line per delivery week). The qty input in the comparison
    // table sums those weekly lines into one rollup number, so the operator's
    // edited value is the new TOTAL across all weekly lines. We scale each
    // weekly line proportionally and use the largest-remainder method for
    // rounding so the new per-week qtys sum exactly to the typed total (no
    // fractional rows, no drift).
    //
    // Pre-2026-05-20 the save loop set every weekly line to the typed value,
    // multiplying the total by N — e.g. typing 225 across 10 weekly lines
    // wrote 225 to each line, making the new total 2250.
    const scaleWeeklyQtys = (oldQtys, newTotal) => {
      if (oldQtys.length === 1) return [newTotal];
      if (newTotal === 0) return oldQtys.map(() => 0);
      const oldTotal = oldQtys.reduce((acc, q) => acc + (q || 0), 0);
      if (oldTotal === 0) {
        // No baseline to scale by — distribute evenly via largest-remainder
        const base = Math.floor(newTotal / oldQtys.length);
        const remainder = newTotal - base * oldQtys.length;
        return oldQtys.map((_, i) => base + (i < remainder ? 1 : 0));
      }
      const floats = oldQtys.map(q => (q || 0) * newTotal / oldTotal);
      const floors = floats.map(f => Math.floor(f));
      const remaining = newTotal - floors.reduce((a, b) => a + b, 0);
      const order = floats
        .map((f, i) => ({ i, frac: f - Math.floor(f), floor: floors[i] }))
        .sort((a, b) => (b.frac - a.frac) || (b.floor - a.floor));
      const result = floors.slice();
      for (let k = 0; k < remaining && k < order.length; k++) {
        result[order[k].i] += 1;
      }
      return result;
    };

    const perPoUpdates = rfqGroupView.childPOs.map(po => {
      // Group line indexes by material key — proportional scaling is scoped
      // to "all weekly lines for the same material on this PO."
      const groupsByKey = new Map();
      (po.lines || []).forEach((line, idx) => {
        const key = line.item_type === "Material"
          ? `M-${line.material_id}`
          : `C-${line.consumable_id}`;
        if (!groupsByKey.has(key)) groupsByKey.set(key, []);
        groupsByKey.get(key).push({ idx, line });
      });
      const updatedLines = (po.lines || []).slice();
      for (const [key, group] of groupsByKey.entries()) {
        if (!(key in parsedEdits)) continue;
        const newTotal = parsedEdits[key];
        const oldTotal = group.reduce((acc, g) => acc + (g.line.quantity_ordered || 0), 0);
        if (newTotal === oldTotal) continue;
        const newQtys = scaleWeeklyQtys(
          group.map(g => g.line.quantity_ordered || 0),
          newTotal
        );
        group.forEach((g, j) => {
          updatedLines[g.idx] = { ...g.line, quantity_ordered: newQtys[j] };
        });
      }
      return { poId: po.purchase_order_id, updatedLines };
    });

    try {
      setRfqSaving(true);
      // Fire all PATCH calls in parallel — server has no cross-PO transaction
      // but the bundle invariant is "all child POs have identical line qtys",
      // so a partial failure (one vendor fails, others succeed) leaves the
      // bundle inconsistent. We catch that below and alert.
      const results = await Promise.allSettled(
        perPoUpdates.map(({ poId, updatedLines }) =>
          fetch(`${API_BASE}/purchase_orders/${poId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lines: updatedLines })
          }).then(async r => {
            if (!r.ok) throw new Error(`PO ${poId}: ${await r.text()}`);
            return poId;
          })
        )
      );

      const failures = results.filter(r => r.status === "rejected");
      if (failures.length > 0) {
        const errs = failures.map(f => f.reason.message || String(f.reason)).join("\n");
        alert(
          `Quantity update partially failed (${failures.length} of ${perPoUpdates.length} vendor RFQs).\n` +
          `Some vendors are now out of sync with the bundle — fix and retry.\n\n${errs}`
        );
      }

      // Apply locally to the POs that succeeded so the comparison table refreshes.
      const succeededPoIds = new Set(
        results
          .filter(r => r.status === "fulfilled")
          .map(r => r.value)
      );
      setRfqGroupView(prev => {
        if (!prev) return prev;
        const byPo = new Map(perPoUpdates.map(u => [u.poId, u.updatedLines]));
        return {
          ...prev,
          childPOs: prev.childPOs.map(p =>
            succeededPoIds.has(p.purchase_order_id) && byPo.has(p.purchase_order_id)
              ? { ...p, lines: byPo.get(p.purchase_order_id) }
              : p
          )
        };
      });

      // Refresh PO list in background so anything outside the comparison view
      // (the PO list grid) reflects the new qty.
      loadPurchaseOrders();

      // Clear only the edits that successfully landed on every vendor.
      if (failures.length === 0) {
        setRfqQtyEdits({});
      }
    } catch (err) {
      alert("Error saving quantities: " + err.message);
    } finally {
      setRfqSaving(false);
    }
  }

  function renderRFQComparison() {
    if (!rfqGroupView || !rfqGroupView.childPOs) return null;

    const rfqs = rfqGroupView.childPOs;

    // 1. Gather all unique items (Materials & Consumables) across all RFQs
    // We assume the RFQs were created from a bundle, so lines should match,
    // but we use a Map to ensure we align them correctly by ID.
    const itemMap = new Map();

    rfqs.forEach(po => {
      if (!po.lines) return;
      po.lines.forEach(line => {
        // Create a unique key for alignment
        const key = line.item_type === "Material"
          ? `M-${line.material_id}`
          : `C-${line.consumable_id}`;

        if (!itemMap.has(key)) {
          itemMap.set(key, {
            key,
            item_type: line.item_type,
            material_id: line.material_id,
            consumable_id: line.consumable_id,
            code: line.item_type === "Material" ? line.material_code : (line.consumable_id || "Consumable"),
            description: line.material_description || "Unknown Item",
            quantity: 0,
            _qtyPO: null, // track which PO we sum qty from (avoid double-counting across vendors)
            prices: {}, // Map vendor PO id -> cost
            linesByPo: {} // Map vendor PO id -> full line object (needed for award radios)
          });
        }

        const itemEntry = itemMap.get(key);
        itemEntry.linesByPo[po.purchase_order_id] = line;

        // Sum quantity from the first PO only (all vendor POs have identical lines)
        if (!itemEntry._qtyPO) {
          itemEntry._qtyPO = po.purchase_order_id;
        }
        if (itemEntry._qtyPO === po.purchase_order_id) {
          itemEntry.quantity += (line.quantity_ordered || 0);
        }

        // Store this vendor's price for this item
        if (line.cost_per_unit != null) {
          itemEntry.prices[po.purchase_order_id] = line.cost_per_unit;
        } else if (!(po.purchase_order_id in itemEntry.prices)) {
          itemEntry.prices[po.purchase_order_id] = null;
        }
      });
    });

    const comparisonRows = Array.from(itemMap.values());

    // Helper — what qty to display/use for math: pending edit if valid, else saved.
    const effectiveQty = (row) => {
      const pending = parseInt(rfqQtyEdits[row.key], 10);
      return (!isNaN(pending) && pending > 0) ? pending : row.quantity;
    };

    // Helper — effective unit cost for a (vendor, row), honoring pending edits.
    // Precedence: pending unit-price > pending CWT (× weight/100) > saved price.
    // Used by the vendor total reduce, the green "lowest" highlight, and the
    // % delta badge so all three respond to in-flight edits identically.
    const getEffectiveUnit = (poId, row) => {
      const editKey = `${poId}-${row.key}`;
      const unitVal = parseFloat(rfqUnitEdits[editKey]);
      if (!isNaN(unitVal) && unitVal > 0) return unitVal;
      const cwtVal = parseFloat(rfqCwtEdits[editKey]);
      if (!isNaN(cwtVal) && cwtVal > 0 && row.material_id) {
        const w = getMaterialWeight(row.material_id);
        if (w > 0) return (w / 100) * cwtVal;
      }
      return row.prices[poId] || 0;
    };

    // Helper to format currency
    const fmt = (n) => n ? `$${parseFloat(n).toFixed(2)}` : "-";

    // Count pending qty edits that actually differ from saved value — drives
    // visibility of the "Save Quantities" button in the header.
    const pendingQtyEditCount = comparisonRows.reduce((acc, row) => {
      const pending = parseInt(rfqQtyEdits[row.key], 10);
      return acc + (!isNaN(pending) && pending > 0 && pending !== row.quantity ? 1 : 0);
    }, 0);

    // Calculate totals for the footer (uses effectiveQty + getEffectiveUnit so
    // both pending qty edits AND pending price edits recompute live; parallels
    // per-cell highlight + % delta).
    const vendorTotals = {};
    rfqs.forEach(po => {
        vendorTotals[po.purchase_order_id] = comparisonRows.reduce((acc, row) => {
            return acc + (effectiveQty(row) * getEffectiveUnit(po.purchase_order_id, row));
        }, 0);
    });

    // Find the winning (lowest) total to highlight
    const minTotal = Math.min(...Object.values(vendorTotals).filter(t => t > 0));

    // ---- Per-line award summary (May 19 2026) ----
    // Walks every line on every vendor's PO for each row's material. A single
    // material may have N weekly lines per vendor (Weekly Buy Plan), so the
    // subtotal must sum across ALL awarded lines for that material, not just
    // one representative. "lines" in vendorSummary counts weekly lines, so
    // a single material winner contributes 8 lines if there are 8 weeks.
    const awardSummary = (() => {
      const perVendor = {}; // supplier -> { lines, subtotal, dates:Set }
      const undecided = [];
      let awardedMaterialCount = 0;
      comparisonRows.forEach(row => {
        let awardedSupplier = null;
        let awardedLinesCount = 0;
        let awardedRowSubtotal = 0;
        const awardedRowDates = [];
        for (const po of rfqs) {
          for (const ln of (po.lines || [])) {
            const matches = ln.item_type === row.item_type && (
              row.item_type === "Material"
                ? ln.material_id === row.material_id
                : ln.consumable_id === row.consumable_id
            );
            if (!matches || !ln.awarded_at) continue;
            awardedSupplier = po.supplier;
            awardedLinesCount += 1;
            const cost = parseFloat(ln.cost_per_unit) || 0;
            const qty = ln.quantity_ordered || 0;
            awardedRowSubtotal += cost * qty;
            // Finalize creates one PO per (vendor, requested_delivery_date).
            // Null/absent dates collapse into a single bucket per vendor,
            // matching the backend's date.today() fallback.
            awardedRowDates.push(ln.requested_delivery_date || "TBD");
          }
        }
        if (!awardedSupplier) {
          undecided.push(row.code);
          return;
        }
        awardedMaterialCount += 1;
        if (!perVendor[awardedSupplier]) {
          perVendor[awardedSupplier] = { lines: 0, subtotal: 0, dates: new Set() };
        }
        perVendor[awardedSupplier].lines += awardedLinesCount;
        perVendor[awardedSupplier].subtotal += awardedRowSubtotal;
        awardedRowDates.forEach(d => perVendor[awardedSupplier].dates.add(d));
      });
      const vendorList = Object.entries(perVendor).map(([supplier, s]) => ({
        supplier,
        lines: s.lines,
        subtotal: s.subtotal,
        poCount: Math.max(1, s.dates.size),
        deliveryDates: Array.from(s.dates).sort(),
      })).sort((a, b) => a.supplier.localeCompare(b.supplier));
      const poTotal = vendorList.reduce((acc, v) => acc + v.poCount, 0);
      return {
        total: comparisonRows.length,
        awarded: awardedMaterialCount,
        undecided,
        vendorSummary: vendorList,
        poTotal,
        readyToFinalize: comparisonRows.length > 0 && undecided.length === 0,
      };
    })();

    return (
      <div className="p-3 h-100 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
                {rfqNameDraft === null ? (
                    <h4 className="mb-0">
                        {rfqBundleName(rfqGroupView) || "RFQ Bundle Comparison"}
                        <button
                            className="btn btn-sm btn-link align-baseline"
                            onClick={() => setRfqNameDraft(rfqBundleName(rfqGroupView))}
                            title="Project name is for JobQ only. It is not printed on the vendor's RFQ."
                        >
                            {rfqBundleName(rfqGroupView) ? "Rename" : "Add project name"}
                        </button>
                    </h4>
                ) : (
                    <div className="d-flex align-items-center gap-2">
                        <input
                            className="form-control"
                            style={{ maxWidth: "360px" }}
                            autoFocus
                            maxLength={120}
                            placeholder="Project name"
                            value={rfqNameDraft}
                            onChange={(e) => setRfqNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveRFQProjectName();
                                if (e.key === "Escape") setRfqNameDraft(null);
                            }}
                        />
                        <button className="btn btn-sm btn-primary" disabled={rfqSaving} onClick={handleSaveRFQProjectName}>
                            {rfqSaving ? "Saving..." : "Save"}
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setRfqNameDraft(null)}>
                            Cancel
                        </button>
                    </div>
                )}
                <div className="text-muted small">
                    {rfqBundleName(rfqGroupView) ? "RFQ Bundle Comparison · " : ""}({rfqGroupView.notes || "No notes"})
                </div>
            </div>
            <div>
                {pendingQtyEditCount > 0 && (
                    <button
                        className="btn btn-primary me-2"
                        disabled={rfqSaving}
                        onClick={handleSaveRFQQuantities}
                        title="Updates the quantity on every vendor RFQ in this bundle"
                    >
                        {rfqSaving
                            ? "Saving..."
                            : `Save ${pendingQtyEditCount} Quantity Change${pendingQtyEditCount === 1 ? "" : "s"}`}
                    </button>
                )}
                <button className="btn btn-danger me-2" onClick={handleCancelRFQGroup} disabled={loading}>
                    Cancel RFQ Bundle
                </button>
                <button className="btn btn-outline-secondary" onClick={() => setViewMode("PO")}>
                    Close Comparison
                </button>
            </div>
        </div>

        <div className="table-responsive card shadow-sm">
            <table className="table table-bordered table-hover mb-0 text-center align-middle">
                <thead className="table-light">
                    <tr>
                        <th className="text-start" style={{minWidth: "250px"}}>Item Description</th>
                        <th style={{width: "80px"}}>Qty</th>
                        
                        {/* Render Vendor Headers */}
                        {rfqs.map(po => {
                            const quotedCount = (po.lines || []).filter(l =>
                                l.cost_per_unit != null && parseFloat(l.cost_per_unit) > 0
                            ).length;
                            return (
                            <th key={po.purchase_order_id} style={{minWidth: "150px"}}>
                                <div className="fw-bold text-primary">{po.supplier}</div>
                                <div className="small text-muted mb-2">RFQ#{po.po_number}</div>
                                <button
                                    className="btn btn-sm btn-outline-success w-100"
                                    onClick={() => handleAwardAllToVendor(po)}
                                    disabled={quotedCount === 0 || rfqAwardingLineId !== null}
                                    title={quotedCount === 0
                                        ? `${po.supplier} hasn't quoted any lines`
                                        : `Mark ${po.supplier}'s ${quotedCount} quoted line(s) as awarded`}
                                >
                                    Award all to {po.supplier}
                                </button>
                            </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {comparisonRows.map(row => {
                        const pendingQty = rfqQtyEdits[row.key];
                        const parsedPending = parseInt(pendingQty, 10);
                        const qtyDirty =
                            pendingQty !== undefined &&
                            pendingQty !== "" &&
                            !isNaN(parsedPending) &&
                            parsedPending > 0 &&
                            parsedPending !== row.quantity;
                        return (
                        <tr key={row.key}>
                            <td className="text-start">
                                <strong>{row.code}</strong>
                                <div className="small text-muted">{row.description}</div>
                            </td>
                            <td className={`bg-light ${qtyDirty ? "border border-primary border-2" : ""}`}>
                                {(() => {
                                    // A material can have N weekly lines per vendor (Weekly Buy
                                    // Plan). Surface the weekly-line count so the operator
                                    // understands the qty input is a TOTAL across weeks — Save
                                    // scales each week proportionally to hit this new total.
                                    const firstPo = rfqs[0];
                                    const weeklyCount = !firstPo ? 0 : (firstPo.lines || []).filter(l => (
                                        l.item_type === row.item_type && (
                                            row.item_type === "Material"
                                                ? l.material_id === row.material_id
                                                : l.consumable_id === row.consumable_id
                                        )
                                    )).length;
                                    return (
                                        <>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                className="form-control form-control-sm fw-bold text-center"
                                                style={{width: "70px", margin: "0 auto"}}
                                                value={pendingQty !== undefined ? pendingQty : row.quantity}
                                                onChange={e => {
                                                    const cleaned = e.target.value.replace(/[^0-9]/g, "");
                                                    setRfqQtyEdits(prev => ({...prev, [row.key]: cleaned}));
                                                }}
                                                onFocus={e => e.target.select()}
                                                title={qtyDirty
                                                    ? (weeklyCount > 1
                                                        ? `Was ${row.quantity}. Save scales the ${weeklyCount} weekly deliveries proportionally to hit this new total across all vendors.`
                                                        : `Was ${row.quantity}. Click Save Quantities to apply across all vendors.`)
                                                    : (weeklyCount > 1
                                                        ? `Total across ${weeklyCount} weekly deliveries. Editing scales each week proportionally.`
                                                        : "Edit and click Save Quantities")}
                                            />
                                            {weeklyCount > 1 && (
                                                <div className="small text-muted mt-1" style={{fontSize: '0.7rem'}}>
                                                    Σ {weeklyCount} wks
                                                </div>
                                            )}
                                            {qtyDirty && (
                                                <div className="small text-primary mt-1">
                                                    was {row.quantity}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </td>

                            {/* Render Price Cells */}
                            {(() => {
                                // Pre-compute every vendor's effective unit cost for this row so
                                // "Best" / "+X.X%" badges + green highlight respond live to pending
                                // edits, not just saved prices.
                                const allEffective = rfqs
                                    .map(p => ({poId: p.purchase_order_id, eff: getEffectiveUnit(p.purchase_order_id, row)}))
                                    .filter(x => x.eff > 0);
                                const lowest = allEffective.length > 0
                                    ? Math.min(...allEffective.map(x => x.eff))
                                    : 0;
                                // Per-row awarded state: at most one vendor's lines for this row's
                                // material have awarded_at set (radio semantics enforced by the API).
                                // Scan ALL of each vendor's lines, not just linesByPo's single
                                // representative — a material can have N weekly lines per vendor
                                // and the representative might be any one of them.
                                const awardedPoIdForRow = (() => {
                                    for (const po of rfqs) {
                                        for (const ln of (po.lines || [])) {
                                            const matches = ln.item_type === row.item_type && (
                                                row.item_type === "Material"
                                                    ? ln.material_id === row.material_id
                                                    : ln.consumable_id === row.consumable_id
                                            );
                                            if (matches && ln.awarded_at) return po.purchase_order_id;
                                        }
                                    }
                                    return null;
                                })();
                                return rfqs.map(po => {
                                    const editKey = `${po.purchase_order_id}-${row.key}`;
                                    const cwtEdit = rfqCwtEdits[editKey] || "";
                                    const unitEdit = rfqUnitEdits[editKey] || "";
                                    const matWeight = row.material_id ? getMaterialWeight(row.material_id) : 0;
                                    const unitLabel = matWeight > 0 ? "sheet" : "unit";

                                    const savedUnit = row.prices[po.purchase_order_id] || 0;
                                    const effectiveUnit = getEffectiveUnit(po.purchase_order_id, row);
                                    const lineTotal = effectiveUnit * effectiveQty(row);
                                    const hasPendingEdit = effectiveUnit > 0 && effectiveUnit !== savedUnit;

                                    // Highlight + % delta only when ≥2 vendors have an effective price
                                    // (delta against a single bid is meaningless).
                                    const isLowest = effectiveUnit > 0 && effectiveUnit === lowest && allEffective.length >= 2;
                                    const pctAboveLowest = (effectiveUnit > 0 && lowest > 0 && !isLowest && allEffective.length >= 2)
                                        ? ((effectiveUnit / lowest - 1) * 100)
                                        : null;

                                    const line = row.linesByPo ? row.linesByPo[po.purchase_order_id] : null;
                                    const isAwarded = awardedPoIdForRow === po.purchase_order_id;
                                    // Match the savedUnit gate to handle effective-price-only cases
                                    // (pending edits that haven't been saved). Awarding requires a
                                    // SAVED price > 0 because the backend awards against the persisted
                                    // line, not the in-flight edit.
                                    const canAward = line != null && savedUnit > 0;
                                    const inFlight = rfqAwardingLineId !== null;
                                    const tdClass = isAwarded
                                        ? "bg-success bg-opacity-25 border border-success border-3"
                                        : isLowest ? "bg-success bg-opacity-10" : "";
                                    const tdTitle = canAward
                                        ? (isAwarded
                                            ? `Awarded to ${po.supplier} — click to clear`
                                            : `Click to award ${row.code} to ${po.supplier}`)
                                        : (line == null
                                            ? `${po.supplier} did not quote this line`
                                            : `Save ${po.supplier}'s price before awarding`);

                                    const handleCellClick = (e) => {
                                        // Ignore clicks that came from interactive inputs inside the cell.
                                        if (e.target.closest('input, button, select, textarea')) return;
                                        if (!canAward || inFlight) return;
                                        handleAwardLine(po, line, isAwarded);
                                    };

                                    return (
                                        <td
                                            key={po.purchase_order_id}
                                            className={tdClass}
                                            onClick={handleCellClick}
                                            style={{cursor: canAward && !inFlight ? "pointer" : "default"}}
                                            title={tdTitle}
                                        >
                                            <div className="d-flex align-items-start gap-1">
                                                <input
                                                    type="radio"
                                                    name={`award-${row.key}`}
                                                    checked={isAwarded}
                                                    disabled={!canAward || inFlight}
                                                    readOnly
                                                    style={{marginTop: "4px"}}
                                                    onClick={(e) => {
                                                        // The td onClick handles the toggle; stop the
                                                        // event so we don't double-fire.
                                                        e.stopPropagation();
                                                        if (!canAward || inFlight) return;
                                                        handleAwardLine(po, line, isAwarded);
                                                    }}
                                                    tabIndex={canAward ? 0 : -1}
                                                    aria-label={`Award ${row.code} to ${po.supplier}`}
                                                />
                                                <div className="flex-grow-1">
                                                    {effectiveUnit > 0 ? (
                                                        <>
                                                            <div className={isAwarded ? "fw-bold text-success" : isLowest ? "fw-bold text-success" : ""}>
                                                                {fmt(effectiveUnit)} <span className="text-muted small">/ {unitLabel}</span>
                                                            </div>
                                                            {matWeight > 0 && (
                                                                <div className="small text-muted">
                                                                    = {fmt((effectiveUnit / matWeight) * 100)} / CWT
                                                                </div>
                                                            )}
                                                            <div className="small text-muted">
                                                                Tot: {fmt(lineTotal)}
                                                            </div>
                                                            {hasPendingEdit && savedUnit > 0 && (
                                                                <div className="small text-info fst-italic">
                                                                    (saved {fmt(savedUnit)})
                                                                </div>
                                                            )}
                                                            {isAwarded && (
                                                                <span className="badge bg-success mt-1">Awarded</span>
                                                            )}
                                                            {!isAwarded && isLowest && (
                                                                <span className="badge bg-success mt-1">Best</span>
                                                            )}
                                                            {pctAboveLowest !== null && (
                                                                <span className="badge bg-danger mt-1" title={`${fmt(effectiveUnit)} is ${pctAboveLowest.toFixed(1)}% above the best bid of ${fmt(lowest)}`}>
                                                                    +{pctAboveLowest.toFixed(1)}% vs best
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="small text-muted fst-italic">No quote</div>
                                                    )}
                                                </div>
                                            </div>
                                            {row.item_type === "Material" && matWeight > 0 && (
                                                <div className="mt-1" onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        placeholder="$/CWT"
                                                        style={{width: "100px", margin: "0 auto"}}
                                                        value={cwtEdit}
                                                        onChange={e => {
                                                            const v = e.target.value;
                                                            setRfqCwtEdits(prev => ({...prev, [editKey]: v}));
                                                            // Typing in CWT clears any pending unit-price edit for this cell
                                                            if (v && rfqUnitEdits[editKey]) {
                                                                setRfqUnitEdits(prev => {
                                                                    const next = {...prev};
                                                                    delete next[editKey];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <div className="mt-1" onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    placeholder={`$/${unitLabel}`}
                                                    style={{width: "100px", margin: "0 auto"}}
                                                    value={unitEdit}
                                                    onChange={e => {
                                                        const v = e.target.value;
                                                        setRfqUnitEdits(prev => ({...prev, [editKey]: v}));
                                                        // Typing in unit price clears any pending CWT edit for this cell
                                                        if (v && rfqCwtEdits[editKey]) {
                                                            setRfqCwtEdits(prev => {
                                                                const next = {...prev};
                                                                delete next[editKey];
                                                                return next;
                                                            });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </td>
                                    );
                                });
                            })()}
                        </tr>
                        );
                    })}
                </tbody>
                <tfoot className="table-light" style={{borderTop: "2px solid #dee2e6"}}>
                    {/* Award status + Generate POs (May 19 2026 per-line award). */}
                    <tr style={{borderTop: "2px solid #198754"}}>
                        <td colSpan={2 + rfqs.length} className="p-3" style={{background: "#f8f9fa"}}>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className={
                                        "badge fs-6 " +
                                        (awardSummary.readyToFinalize ? "bg-success" :
                                         awardSummary.awarded > 0 ? "bg-warning text-dark" : "bg-secondary")
                                    }>
                                        {awardSummary.awarded} of {awardSummary.total} material(s) awarded
                                    </span>
                                    {awardSummary.vendorSummary.length > 0 && (
                                        <span className="text-muted small">
                                            will create {awardSummary.poTotal} PO{awardSummary.poTotal === 1 ? "" : "s"} across {awardSummary.vendorSummary.length} vendor(s) ({awardSummary.vendorSummary.map(v => `${v.supplier}: ${v.poCount}`).join(", ")}) — one PO per vendor per delivery week
                                        </span>
                                    )}
                                    {!awardSummary.readyToFinalize && awardSummary.undecided.length > 0 && (
                                        <span className="text-danger small" title={"Undecided: " + awardSummary.undecided.join(", ")}>
                                            Undecided: {awardSummary.undecided.slice(0, 3).join(", ")}
                                            {awardSummary.undecided.length > 3 ? `, +${awardSummary.undecided.length - 3} more` : ""}
                                        </span>
                                    )}
                                </div>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={() => setRfqShowFinalizeModal(true)}
                                    disabled={!awardSummary.readyToFinalize || rfqFinalizing || rfqAwardingLineId !== null}
                                    title={awardSummary.readyToFinalize
                                        ? `Generate ${awardSummary.poTotal} purchase order(s) and close this RFQ bundle`
                                        : "Award every line to a vendor before generating POs"}
                                >
                                    {rfqFinalizing
                                        ? "Generating..."
                                        : `Generate ${awardSummary.poTotal || 0} PO${awardSummary.poTotal === 1 ? "" : "s"}`}
                                </button>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="2" className="text-end fw-bold">TOTALS:</td>
                        {rfqs.map(po => {
                            const total = vendorTotals[po.purchase_order_id] || 0;
                            const isWinningTotal = total > 0 && total === minTotal;

                            return (
                                <td key={po.purchase_order_id} className={isWinningTotal ? "bg-success text-white" : ""}>
                                    <div className="fs-5 fw-bold">{fmt(total)}</div>
                                    {isWinningTotal && <div className="small">Best Value</div>}
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td colSpan="2"></td>
                        {rfqs.map(po => (
                            <td key={po.purchase_order_id}>
                                <div className="d-grid gap-1">
                                    {(Object.keys(rfqCwtEdits).some(k => k.startsWith(`${po.purchase_order_id}-`) && rfqCwtEdits[k]) ||
                                      Object.keys(rfqUnitEdits).some(k => k.startsWith(`${po.purchase_order_id}-`) && rfqUnitEdits[k])) && (
                                        <button
                                            className="btn btn-sm btn-primary"
                                            disabled={rfqSaving}
                                            onClick={() => handleSaveRFQPrices(po.purchase_order_id)}
                                        >
                                            {rfqSaving ? "Saving..." : "Save Prices"}
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => openPdf(`${API_BASE}/purchase_orders/${po.purchase_order_id}/pdf`)}
                                    >
                                        Preview PDF
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-dark"
                                        onClick={() => {
                                            setRfqComparisonPoId(po.purchase_order_id);
                                            setRfqComparisonSupplier(po.supplier);
                                            setEmailDocType('po');
                                            setShowEmailModal(true);
                                        }}
                                    >
                                        &#9993; Email RFQ
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleSelectPO(po)}
                                    >
                                        Edit Details
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-dark"
                                        onClick={() => handlePropagateCWT(po.purchase_order_id, po.supplier)}
                                    >
                                        Apply to CWT
                                    </button>
                                </div>
                            </td>
                        ))}
                    </tr>
                </tfoot>
            </table>
        </div>

        {/* Generate POs confirmation modal — shows what will be created so
            the operator can sanity-check vendors + subtotals before the
            destructive flip (Converted/Cancelled + new Committed POs). */}
        {rfqShowFinalizeModal && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{background: "rgba(0,0,0,0.5)", zIndex: 1055}}
            onClick={() => !rfqFinalizing && setRfqShowFinalizeModal(false)}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Generate Purchase Orders</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setRfqShowFinalizeModal(false)}
                    disabled={rfqFinalizing}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p>
                    Awarding <strong>{awardSummary.awarded}</strong> material(s) to <strong>{awardSummary.vendorSummary.length}</strong> vendor(s), creating{" "}
                    <strong>{awardSummary.poTotal}</strong> purchase order{awardSummary.poTotal === 1 ? "" : "s"} —
                    one per vendor per requested delivery date, so material
                    arrives on the schedule from the Weekly Buy Plan.
                  </p>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th className="text-end">POs (delivery dates)</th>
                        <th className="text-end">Weekly lines</th>
                        <th className="text-end">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {awardSummary.vendorSummary.map(v => (
                        <tr key={v.supplier}>
                          <td>{v.supplier}</td>
                          <td className="text-end" title={v.deliveryDates.join(", ")}>
                            {v.poCount}
                            <div className="text-muted" style={{fontSize: "0.72rem"}}>
                              {v.deliveryDates.join(" · ")}
                            </div>
                          </td>
                          <td className="text-end">{v.lines}</td>
                          <td className="text-end">{fmt(v.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-light fw-bold">
                        <td>Total</td>
                        <td className="text-end">{awardSummary.poTotal}</td>
                        <td className="text-end">
                          {awardSummary.vendorSummary.reduce((acc, v) => acc + v.lines, 0)}
                        </td>
                        <td className="text-end">
                          {fmt(awardSummary.vendorSummary.reduce((acc, v) => acc + v.subtotal, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="alert alert-warning mb-0 small">
                    <strong>Heads up:</strong> winning vendors will be marked <em>Converted</em>; vendors with no awarded lines will be marked <em>Cancelled</em>. This action can't be undone from here — you'd have to recreate the RFQ from scratch.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setRfqShowFinalizeModal(false)}
                    disabled={rfqFinalizing}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleGenerateAwardedPOs}
                    disabled={rfqFinalizing}
                  >
                    {rfqFinalizing ? "Generating..." : `Generate ${awardSummary.poTotal} PO${awardSummary.poTotal === 1 ? "" : "s"}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // *** NEW Helper: get total cost for the PO (ordered lines) ***
  // =========================================================================
  function getPOTotalCost() {
    // If it's a new PO, sum newLines; else sum poLines
    return poLinesTotal(poId ? poLines : newLines) // features/purchaseOrders/poData
  }
  

  async function handleDeleteDoc(docId) {
    if (!window.confirm("Are you sure you want to delete this unlinked document?")) return;
    try {
      const res = await fetch(`${API_BASE}/receipt_documents/${docId}`, {
        method: "DELETE"
      });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      // Update state
      setUnlinkedDocs(prev => prev.filter(d => d.receipt_document_id !== docId));
      // Also clear preview if deleted
      if (previewDoc && previewDoc.receipt_document_id === docId) {
        setPreviewDoc(null);
      }
    } catch (err) {
      alert("Error deleting document: " + err.message);
    }
  }

  async function handleSubmitRFQGroup() {
    if (rfqSelectedVendors.length === 0) {
      alert("Please select at least one vendor.");
      return;
    }

    // Gather items (either from pendingNewLines if triggered from Buy Plan, or manually added)
    // Assuming 'pendingNewLines' is populated from the Weekly Buy Plan
    const linesToOrder = pendingNewLines.length > 0 ? pendingNewLines : newLines;

    if (linesToOrder.length === 0) {
      alert("No items selected for RFQ.");
      return;
    }

    const payload = {
      vendor_ids: rfqSelectedVendors,
      lines: linesToOrder, // Ensure this matches PurchaseOrderLineCreate structure
      due_date: null,      // Or add a date picker to the modal
      notes: "RFQ Generated from Weekly Buy Plan",
      project_name: rfqProjectName.trim() || null
    };

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/purchase_orders/rfq_group`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      
      const data = await res.json();
      alert(data.message);
      
      // Cleanup
      setShowRFQModal(false);
      setRfqSelectedVendors([]);
      setRfqProjectName("");
      setPendingNewLines([]);
      loadPurchaseOrders(); // Refresh the list
      
    } catch (err) {
      alert("Error creating RFQ Group: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePropagateCWT(poId, vendorName) {
    if (!window.confirm(`Update system CWT Rules using pricing from ${vendorName}? \n\nThis will update the cost basis for ALL materials matching the types/thicknesses in this quote, and then immediately recalculate all part costs.`)) return;

    try {
      setLoading(true);

      // 1. Save any pending CWT or unit-price edits from comparison view, or detail view if open
      const pendingEditPrefix = `${poId}-`;
      const hasPendingEdits =
        Object.keys(rfqCwtEdits).some(k => k.startsWith(pendingEditPrefix) && rfqCwtEdits[k]) ||
        Object.keys(rfqUnitEdits).some(k => k.startsWith(pendingEditPrefix) && rfqUnitEdits[k]);
      if (hasPendingEdits) {
        await handleSaveRFQPrices(poId);
      } else if (!rfqGroupView) {
        // Only save detail view if we're not in comparison mode
        await handleSavePO();
      }
      
      // 2. Update CWT Rules from this PO's pricing (Await this, it should be fast)
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}/propagate_pricing_to_cwt`, {
        method: "POST"
      });

      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }

      const data = await res.json();

      // 3. Trigger Global Cost Recalculation (FIRE AND FORGET)
      // We do NOT await this. We let it run in the background so the UI doesn't freeze or timeout.
      fetch(`${API_BASE}/jobs/update_all_part_costs`, {
        method: "POST"
      }).catch(err => console.warn("Background recalc warning:", err));

      // 4. Alert immediately
      alert(`${data.message}\n\nGlobal material pricing is being recalculated in the background. You can continue working.`);

    } catch (err) {
      alert("Error updating CWT rules: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  // Apply a server award/unaward to local rfqGroupView optimistically.
  // A material may have many lines per vendor (one per week, from a Weekly
  // Buy Plan), so award/unaward FAN OUT across every line of that material
  // on the target PO:
  //   - Award: stamp awarded_at on every quoted line for this material on
  //     the target PO; clear awarded_at on every line for this material
  //     across OTHER vendors in the bundle (radio semantics).
  //   - Unaward: clear awarded_at on every line for this material on the
  //     target PO; leave other vendors untouched.
  function _applyAwardLocally(awardedPoId, itemType, itemId, isAward) {
    const stamp = new Date().toISOString();
    setRfqGroupView(prev => {
      if (!prev || !prev.childPOs) return prev;
      const matchesItem = (line) => (
        line.item_type === itemType &&
        (itemType === "Material"
          ? line.material_id === itemId
          : line.consumable_id === itemId)
      );
      const hasQuotedPrice = (line) =>
        line.cost_per_unit != null && parseFloat(line.cost_per_unit) > 0;
      return {
        ...prev,
        childPOs: prev.childPOs.map(p => ({
          ...p,
          lines: (p.lines || []).map(line => {
            if (!matchesItem(line)) return line;
            if (p.purchase_order_id === awardedPoId) {
              if (isAward) {
                // Only flip quoted lines; unquoted weeks (rare) stay undecided
                return hasQuotedPrice(line)
                  ? { ...line, awarded_at: stamp }
                  : { ...line, awarded_at: null };
              }
              return { ...line, awarded_at: null };
            }
            // Other vendor: clear any prior award for this material when awarding
            if (isAward && line.awarded_at) {
              return { ...line, awarded_at: null };
            }
            return line;
          })
        }))
      };
    });
  }

  // Toggle an award on/off for a single bundle line. Optimistically updates
  // local state, POSTs to /award or /unaward, and reverts on failure.
  async function handleAwardLine(po, line, isCurrentlyAwarded) {
    if (rfqAwardingLineId !== null) return; // in-flight guard
    const poId = po.purchase_order_id;
    const lineId = line.po_line_id;
    const itemId = line.item_type === "Material" ? line.material_id : line.consumable_id;
    const endpoint = isCurrentlyAwarded ? "unaward" : "award";

    // Optimistic update — fans out across every weekly line for this material
    _applyAwardLocally(poId, line.item_type, itemId, !isCurrentlyAwarded);
    setRfqAwardingLineId(lineId);

    try {
      const res = await fetch(
        `${API_BASE}/purchase_orders/${poId}/lines/${lineId}/${endpoint}`,
        { method: "POST" }
      );
      if (!res.ok) {
        const txt = await res.text();
        _applyAwardLocally(poId, line.item_type, itemId, isCurrentlyAwarded); // revert
        throw new Error(txt || `Failed to ${endpoint} line.`);
      }
      // Pull fresh state — server may have cleared competitor awards across
      // OTHER vendors for this material in ways the optimistic update can't
      // fully predict (e.g. partial-quote weeks). Lightweight: only refetch
      // the bundle's POs, not the entire PO list.
    } catch (err) {
      alert(`Could not ${endpoint} ${po.supplier}'s line: ${err.message}`);
    } finally {
      setRfqAwardingLineId(null);
    }
  }

  // "Award all to {vendor}" shortcut: marks every quoted material on this PO
  // as awarded, leaving fully-unquoted materials undecided. We dedupe to one
  // call per (item_type, item_id) because /award now fans out across all
  // weekly lines for a material — no need to call it once per weekly line.
  // Sequential POSTs because each /award also clears competitor awards on
  // the same material; parallel could race on the auto-clear.
  async function handleAwardAllToVendor(po) {
    const vendor = po.supplier;
    const seen = new Set();
    const oneLinePerMaterial = [];
    for (const ln of po.lines || []) {
      if (ln.cost_per_unit == null || parseFloat(ln.cost_per_unit) <= 0) continue;
      const k = ln.item_type === "Material" ? `M-${ln.material_id}` : `C-${ln.consumable_id}`;
      if (seen.has(k)) continue;
      seen.add(k);
      oneLinePerMaterial.push(ln);
    }
    if (oneLinePerMaterial.length === 0) {
      alert(`${vendor} hasn't quoted any lines yet.`);
      return;
    }
    if (!window.confirm(
      `Award ${vendor}'s ${oneLinePerMaterial.length} quoted material(s)? ` +
      `Any prior awards on other vendors for these materials will be cleared.`
    )) return;

    setRfqAwardingLineId(-1); // sentinel to block per-cell clicks during bulk
    try {
      for (const line of oneLinePerMaterial) {
        const res = await fetch(
          `${API_BASE}/purchase_orders/${po.purchase_order_id}/lines/${line.po_line_id}/award`,
          { method: "POST" }
        );
        if (!res.ok) {
          throw new Error(`Line ${line.po_line_id}: ${await res.text()}`);
        }
        const itemId = line.item_type === "Material" ? line.material_id : line.consumable_id;
        _applyAwardLocally(po.purchase_order_id, line.item_type, itemId, true);
      }
    } catch (err) {
      alert(`Award-all failed: ${err.message}`);
      loadPurchaseOrders(); // resync on failure
    } finally {
      setRfqAwardingLineId(null);
    }
  }

  // Legacy single-click award used by the per-PO Edit Details view. The
  // backend route now wraps the new finalize flow (marks every quoted line
  // on this PO as awarded, then runs the same finalize logic). A vendor
  // who didn't quote all lines now fails fast with an error listing the
  // undecided materials — better than the pre-2026-05-19 silent $0 lines.
  async function handleConvertToRFQ() {
    if (!poId) return;
    if (!supplier) {
      alert("Pick a vendor (Supplier) on this PO before sending it out for quote.");
      return;
    }
    if (!window.confirm(
      `Convert PO #${poNumber} into an RFQ and send it to ${supplier} for pricing?\n\n` +
      `This clears the line prices (the vendor fills them in). When they reply, ` +
      `enter the quoted prices, Save, then "Accept Quote & Order" to place the order.`
    )) return;

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}/convert_to_rfq`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await res.json();

      // Reload this PO into the editor (now status='RFQ') and surface it in the
      // list under the RFQ filter.
      setStatusFilter("RFQ");
      await handleSelectPO({ purchase_order_id: poId });
      loadPurchaseOrders();

      if (window.confirm(`PO #${poNumber} is now an RFQ. Email the quote request to ${supplier} now?`)) {
        handleEmailPO();
      }
    } catch (err) {
      alert(`Send for Quote failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleAwardBid(poId, vendorName) {
    if (!window.confirm(`Accept ${vendorName}'s quote and place the order at the quoted prices?\n\nAny competing vendor RFQs in this bundle will be cancelled.`)) return;

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/purchase_orders/${poId}/award`, {
        method: "POST",
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const result = await res.json();

      // A single-quote award commits THIS PO in place — same number, no second
      // row. Land the operator on it under the Committed filter, or they hit a
      // list that no longer holds the RFQ they were just looking at and assume
      // the award vanished. A split award still mints new POs; those come back
      // in created_pos and the list reload surfaces them.
      const landed = (result.created_pos || [])[0];
      alert(result.message || `Bid awarded to ${vendorName}!`);
      setRfqGroupView(null);
      setViewMode("PO");
      if (result.converted_in_place && landed) {
        setStatusFilter("Committed");
        await handleSelectPO({ purchase_order_id: landed.purchase_order_id });
      }
      loadPurchaseOrders();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerateAwardedPOs() {
    if (!rfqGroupView) return;
    const rfqGroupId =
      rfqGroupView.rfq_group_id ||
      (rfqGroupView.childPOs && rfqGroupView.childPOs[0] && rfqGroupView.childPOs[0].rfq_group_id);
    if (!rfqGroupId) {
      alert("Bundle has no rfq_group_id — cannot generate POs.");
      return;
    }

    setRfqFinalizing(true);
    try {
      const res = await fetch(
        `${API_BASE}/purchase_orders/rfq_group/${rfqGroupId}/finalize`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const result = await res.json();

      const lines = result.created_pos || [];
      const summary = lines
        .map(p => `  ${p.supplier} — PO #${p.po_number}, ${p.line_count} line(s), $${(p.subtotal || 0).toFixed(2)}`)
        .join("\n");
      alert(
        result.converted_in_place
          ? `${result.message}\n\n${summary}`
          : `${result.message}\n\n${summary}\n\n` +
            `RFQ POs in this bundle have been Converted (winning vendors) or Cancelled (no awards).`
      );

      setRfqShowFinalizeModal(false);
      loadPurchaseOrders();
      setRfqGroupView(null);
      setViewMode("PO");
    } catch (err) {
      alert(`Generate POs failed: ${err.message}`);
    } finally {
      setRfqFinalizing(false);
    }
  }

  async function handleCancelRFQGroup() {
    if (!rfqGroupView || !rfqGroupView.childPOs) return;
    const count = rfqGroupView.childPOs.length;
    if (!window.confirm(`Cancel this entire RFQ bundle? All ${count} vendor RFQs will be set to Cancelled.`)) return;

    try {
      setLoading(true);
      const results = await Promise.all(
        rfqGroupView.childPOs.map(po =>
          fetch(`${API_BASE}/purchase_orders/${po.purchase_order_id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "Cancelled" })
          })
        )
      );
      const failed = results.filter(r => !r.ok);
      if (failed.length > 0) {
        alert(`${failed.length} of ${count} RFQs failed to cancel.`);
      } else {
        alert("RFQ bundle cancelled.");
      }
      loadPurchaseOrders();
      setRfqGroupView(null);
      setViewMode("PO");
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  function renderPreview() {
    if (!previewDoc) return null;

    const s3Url = poDocUrl(previewDoc); // features/purchaseOrders/poData

    return (
      <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.8)" }} onClick={() => setPreviewDoc(null)}>
        <div className="modal-dialog modal-xl" onClick={e => e.stopPropagation()}>
          <div className="modal-content" style={{ height: '90vh' }}>
            <div className="modal-header">
              <h5 className="modal-title">Preview: {previewDoc.original_filename}</h5>
              <button type="button" className="btn-close" onClick={() => setPreviewDoc(null)}></button>
            </div>
            <div className="modal-body p-0 d-flex justify-content-center align-items-center bg-dark">
              {previewDoc.original_filename?.toLowerCase().endsWith('.pdf') ? (
                 <iframe src={s3Url} title="Preview" style={{width: '100%', height: '100%'}} />
              ) : (
                 <img src={s3Url} alt="Preview" className="dark-invertible" style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // Main Render
  // =========================================================================
  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* LEFT SIDEBAR — never shrinks. The main column's flex basis is its
          widest unwrapped content (PO 1304's earmark chips measured ~11,000px
          laid end to end), and a shrinkable sidebar gives up width in
          proportion: 264px became 71px and the PO list became unreadable. */}
      <div className="border-end p-3" style={{ width: "264px", flexShrink: 0, overflowY: "auto" }}>
        <h5>Navigation</h5>
        <button
          className={`btn btn-sm w-100 mb-2 ${viewMode === "DASH" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setViewMode("DASH")}
        >
          Dashboard
        </button>
        <button
          className={`btn btn-sm w-100 mb-2 ${viewMode === "PO" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setViewMode("PO")}
        >
          PO Management
        </button>
        <button
          className={`btn btn-sm w-100 ${viewMode === "DOCS" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setViewMode("DOCS")}
        >
          Documents Queue
        </button>

        {/* Only show the PO list/filter when in PO mode */}
        {viewMode === "PO" && (
          <>
            <hr />
            <label>Status Filter:</label>
            <select
              className="form-select form-select-sm mb-2"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {PO_STATUS_OPTIONS.map((o) => <option key={o.value || 'all'} value={o.value}>{o.label}</option>)}
            </select>

            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Search PO# or Supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Search Vendor SO..."
              value={vendorSalesOrderSearch}
              onChange={(e) => setVendorSalesOrderSearch(e.target.value)}
            />
            
            {loading && <div>Loading POs...</div>}
            
            <div className="mt-3 mb-2">
              <button className="btn btn-sm btn-outline-success w-100" onClick={handleCreateNewPO}>
                + Create New PO
              </button>
              <button className="btn btn-sm btn-outline-warning w-100 mt-1" onClick={() => setShowManualRFQModal(true)}>
                + Create Manual RFQ
              </button>
            </div>

            {!loading && (
              <div className="list-group mt-2">
                {(() => {
                  const todayStr = shopToday() // the shop's (Central) day (utils/shopDate), as the phone's list
                  return groupedPOs.map((item) => {
                  // CASE A: Render RFQ Group Header
                  if (item.isGroupHeader) {
                    return (
                      <button
                        key={item.rfq_group_id}
                        className="list-group-item list-group-item-warning list-group-item-action"
                        onClick={() => {
                            setRfqGroupView(item); // You need to add this state variable
                            setRfqNameDraft(null);
                            setViewMode("RFQ_GROUP");
                        }}
                      >
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-1">{rfqBundleName(item) || "RFQ Bundle"}</h6>
                          <span className="badge bg-dark">Bidding</span>
                        </div>
                        <small>{rfqBundleName(item) ? "RFQ Bundle · " : ""}{item.childPOs.length} Vendors Invited</small><br/>
                        <small className="text-muted" style={{fontSize: "0.75rem"}}>
                            {new Date(item.created_at).toLocaleDateString()}
                        </small>
                      </button>
                    )
                  }

                  // CASE B: Render Standard PO (Original Logic)
                  else {
                    const po = item; // Alias it back to 'po' to reuse your code
                    const isSelected = selectedPO && selectedPO.purchase_order_id === po.purchase_order_id
                    const { isLate, daysLate } = poLateInfo(po, todayStr) // features/purchaseOrders/poData
                    const lateClass = isLate && !isSelected ? "list-group-item-danger" : ""
                    return (
                      <button
                        key={po.purchase_order_id}
                        className={`list-group-item list-group-item-action ${isSelected ? "active" : ""} ${lateClass}`}
                        onClick={() => handleSelectPO(po)}
                        title={isLate ? `Past est. receive date (${po.estimated_receive_date}) by ${daysLate} day${daysLate === 1 ? '' : 's'}` : undefined}
                      >
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-1">
                            PO#{po.po_number}
                            {po.email_count > 0 && (
                              <span className="badge bg-info ms-1" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }} title={`Emailed ${po.email_count} time${po.email_count !== 1 ? 's' : ''}`}>
                                &#9993; {po.email_count}
                              </span>
                            )}
                            {isLate && (
                              <span className="badge bg-danger ms-1" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                                LATE {daysLate}d
                              </span>
                            )}
                          </h6>
                          <small className={isSelected ? "" : "text-muted"}>
                            {po.status}
                          </small>
                        </div>
                        <div className="d-flex w-100 justify-content-between align-items-end">
                          <small className={isSelected ? "" : "text-muted"}>
                            {po.supplier || "No Supplier"}
                          </small>
                          {po.confirmed && <span title="Confirmed by Supplier">&#x2705;</span>}
                        </div>
                        {po.project_name && (
                          <small className={`d-block text-truncate ${isSelected ? "" : "text-muted"}`} title={`Project: ${po.project_name}`}>
                            Project: {po.project_name}
                          </small>
                        )}
                      </button>
                    )
                  }
                  })
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-grow-1 p-4 overflow-auto">
        {error && <div className="alert alert-danger">{error}</div>}

        {/* DASHBOARD VIEW */}
        {viewMode === "DASH" && renderDashboard()}

        {/* RFQ COMPARISON VIEW (Replaces old table) */}
          {viewMode === "RFQ_GROUP" && renderRFQComparison()}

        {/* PO MANAGEMENT VIEW */}
        {viewMode === "PO" && (
  <>
    {!poId ? (
      <h4>Creating New Purchase Order</h4>
    ) : (
      <h4>Editing Purchase Order (#{poNumber})</h4>
    )}

    {/* Basic Fields */}
    <div className="row mb-3">
      <div className="col-md-3">
        <label className="form-label">Supplier</label>
        <select
          className="form-select"
          value={supplier}
          onChange={(e) => {
            const val = e.target.value
            setSupplier(val)
            const vObj = vendors.find(v => v.name === val)
            if (vObj) {
              // Example logic to auto-set est. date if vendor lead_time applies
              const newETA = computeVendorETA(vObj)
              const isoDate = shopToday(newETA) // that moment's Central day
              setEstimatedReceiveDate(isoDate)
            } else {
              setEstimatedReceiveDate("")
            }
            // Re-price auto-filled New PO lines against the newly selected supplier
            repriceNewLinesForSupplier(val)
          }}
        >
          <option value="">-- Select a Vendor --</option>
          {vendors.map((v) => (
            <option key={v.vendor_id} value={v.name}>
              {v.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-3">
        <label className="form-label">Vendor Sales Order</label>
        <input
          type="text"
          className="form-control"
          value={vendorSalesOrder}
          onChange={(e) => setVendorSalesOrder(e.target.value)}
        />
      </div>
      <div className="col-md-2">
        <label className="form-label">Order Date</label>
        <input
          type="date"
          className="form-control"
          value={orderDate}
          onChange={(e) => setOrderDate(e.target.value)}
        />
      </div>
      <div className="col-md-2">
        <label className="form-label">Est. Receive Date</label>
        <input
          type="date"
          className="form-control"
          value={estimatedReceiveDate}
          onChange={(e) => setEstimatedReceiveDate(e.target.value)}
        />
      </div>
      <div className="col-md-2">
        <label className="form-label">Payment Terms (days)</label>
        <input
          type="number"
          className="form-control"
          min="0"
          max="365"
          step="1"
          placeholder="Net 30 (default)"
          value={paymentTermsDays}
          onChange={(e) => setPaymentTermsDays(e.target.value)}
        />
        <div className="form-text">0 = due on receipt</div>
      </div>
    </div>

    {/* Status + Notes */}
    <div className="row mb-3">
      <div className="col-md-4">
        <label className="form-label">Status</label>
        {status === 'RFQ' ? (
          <div>
            <span className="badge bg-warning text-dark" style={{ fontSize: '0.95rem', padding: '0.5rem 0.75rem' }}>
              RFQ — out for quote
            </span>
            <div className="form-text">
              Enter the vendor's quoted prices on the lines below and Save, then
              &nbsp;<strong>Accept Quote &amp; Order</strong>.
            </div>
          </div>
        ) : status === 'Converted' ? (
          /* A Converted PO has no option in the select below, so binding it
             there rendered the first option instead and the editor read
             "Committed" on a dead RFQ row — while the sidebar read the true
             status. Two people chased that today. It is also not a status
             anyone should be able to pick: flipping this row to Committed
             resurrects a duplicate of the order the award already placed. */
          <div>
            <span className="badge bg-secondary" style={{ fontSize: '0.95rem', padding: '0.5rem 0.75rem' }}>
              Converted — quote awarded
            </span>
            <div className="form-text">
              {(() => {
                const grp = selectedPO && selectedPO.rfq_group_id;
                const order = grp && (purchaseOrders || []).find(p =>
                  p.rfq_group_id === grp &&
                  !['RFQ', 'Converted', 'Cancelled'].includes(p.status));
                return order
                  ? <>The order was placed on <strong>PO #{order.po_number}</strong>. This row is the
                     RFQ it came from — history, not a live order.</>
                  : <>The award placed the order on another PO. This row is the RFQ it came
                     from — history, not a live order.</>;
              })()}
            </div>
          </div>
        ) : (
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={!poId}
          >
            <option value="Committed">Committed</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        )}
      </div>
      <div className="col-md-8">
        <label className="form-label">Notes</label>
        {/* A textarea, not <input type="text">: a single-line input silently
            drops line breaks, so opening a PO and saving collapsed its notes
            into one line (RFQ #1393's release schedule, 2026-10-06). The notes
            print on the vendor's PO / RFQ PDF line for line. */}
        <textarea
          className="form-control"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </div>

    {/* Fulfillment + Confirmed */}
    <div className="row mb-3">
      <div className="col-md-4">
        <label className="form-label">Fulfillment</label>
        <select
          className="form-select"
          value={fulfillment}
          onChange={(e) => setFulfillment(e.target.value)}
        >
          <option value="">-- Select --</option>
          <option value="Delivery">Delivery</option>
          <option value="Customer Pickup">Customer Pickup</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Project</label>
        <input
          type="text"
          className="form-control"
          maxLength={120}
          placeholder="Optional"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
        />
        <div className="form-text">For JobQ only. Not printed on the PO.</div>
      </div>
      <div className="col-md-4 d-flex align-items-end">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="confirmedCheckbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="confirmedCheckbox">
            Confirmed by Supplier
          </label>
        </div>
      </div>
    </div>

    {/* Automatic vendor follow-up (jobq-api po_followups.py): MailQ reply status, the switch, the email */}
    {poId && status !== 'RFQ' && (
      <POFollowUpPanel apiBase={API_BASE} poId={poId} refreshKey={followUpRefresh} />
    )}

    {/* Buttons for Save, Delete */}
    <div className="mb-3">
      <button className="btn btn-primary me-2" onClick={handleSavePO}>
        {poId ? "Save Changes" : "Create PO"}
      </button>

      {poId && status === 'Committed' && (
        <button
          className="btn btn-warning me-2"
          onClick={handleConvertToRFQ}
          title="Turn this PO into a Request for Quote and send it to the vendor for pricing before you commit to the order"
        >
          &#128233; Send for Quote (RFQ)
        </button>
      )}

      {poId && status === 'RFQ' && (
        <button
          className="btn btn-outline-primary me-2"
          onClick={() => handlePropagateCWT(poId, supplier)}
          title="Update system CWT Rules using pricing from this RFQ"
        >
          Apply to CWT
        </button>
      )}

      {poId && status === 'RFQ' && (
          <button
            className="btn btn-success me-2"
            onClick={() => handleAwardBid(poId, supplier)}
            title="Award this quote and create the Committed order PO from the quoted line prices"
          >
            &#10003; Accept Quote &amp; Order
          </button>
      )}
      {poId && (
        <>
          <button className="btn btn-secondary me-2" onClick={handleViewPOPDF}>
            View PDF
          </button>
          {status !== 'RFQ' && (
            <button
              className="btn btn-outline-secondary me-2"
              onClick={handleViewPOStatement}
              title="Vendor statement: ordered vs received with receipt history and outstanding balance"
            >
              &#129534; Statement
            </button>
          )}
          <button className="btn btn-outline-dark me-2" onClick={handleEmailPO}>
             &#9993; {status === 'RFQ' ? "Email RFQ" : "Email PO"}
          </button>
          {status !== 'RFQ' && (
            <button className="btn btn-outline-dark me-2" onClick={handleEmailStatement}>
               &#9993; Email Statement
            </button>
          )}
          <button className="btn btn-outline-info me-2" onClick={handleShowEmailHistory}>
             &#128203; Email History
          </button>
          {status === 'RFQ' ? (
            <button
              className="btn btn-danger"
              onClick={handleCancelRFQ}
              disabled={loading}
              title="Cancel this RFQ. It stays on file as Cancelled with its lines, quoted prices and email history — an RFQ that was sent to a vendor cannot be deleted."
            >
              Cancel RFQ
            </button>
          ) : (
            <button className="btn btn-danger" onClick={handleDeletePO}>
              Delete PO
            </button>
          )}
        </>
      )}
    </div>

    {/* If creating a new PO => show "newLines" sub-form */}
    {/* MODIFIED: Added cascading filter dropdowns for materials */}
    {!poId && (
      <div className="mb-4">
        <h5>New PO Lines</h5>
        <button className="btn btn-sm btn-secondary mb-2" onClick={handleAddNewLine}>
          Add Line
        </button>
        {newLines.map((ln, idx) => {
          // NEW: Cascading filter logic
          const ownerOptions = [...new Set(allMaterials.map(m => m.owner).filter(Boolean))].sort();
          
          let materialsForType = allMaterials;
          if (ln.filterOwner) {
            materialsForType = materialsForType.filter(m => m.owner === ln.filterOwner);
          }
          const materialTypeOptions = [...new Set(materialsForType.map(m => m.material_type).filter(Boolean))].sort();

          let materialsForThickness = materialsForType;
          if (ln.filterMaterialType) {
            materialsForThickness = materialsForThickness.filter(m => m.material_type === ln.filterMaterialType);
          }
          const thicknessOptions = [...new Set(materialsForThickness.map(m => m.thickness).filter(Boolean))].sort((a, b) => a - b);

          let materialsForFinish = materialsForThickness;
          if (ln.filterThickness) {
            // Use == for type coercion between string from dropdown and number in data
            materialsForFinish = materialsForFinish.filter(m => m.thickness == ln.filterThickness);
          }
          const surfaceFinishOptions = [...new Set(materialsForFinish.map(m => m.surface_finish).filter(Boolean))].sort();

          let finalFilteredMaterials = materialsForFinish;
          if (ln.filterSurfaceFinish) {
            finalFilteredMaterials = finalFilteredMaterials.filter(m => m.surface_finish === ln.filterSurfaceFinish);
          }

          // Stock Info logic (unchanged)
          const mat = ln.material_id
            ? allMaterials.find(m => m.material_id === parseInt(ln.material_id, 10))
            : null
          const cons = ln.consumable_id
            ? allConsumables.find(c => c.consumable_id === parseInt(ln.consumable_id, 10))
            : null

          let stockInfo = ""
          if (mat) {
            const q = mat.stock_quantity || 0
            const r = mat.reserved_quantity || 0
            const i = mat.inbound_quantity || 0
            const a = q - r
            stockInfo = `(Mat: Q=${q}, Av=${a}, Inb=${i})`
          }
          if (cons) {
            const q = cons.stock_quantity || 0
            stockInfo = `(Cons: Q=${q}, Reorder=${cons.reorder_point})`
          }

          return (
            <div key={idx} className="mb-2 border p-2">
              <div className="row">
                <div className="col-md-2">
                  <label>Item Type</label>
                  <select
                    className="form-select"
                    value={ln.item_type}
                    onChange={(e) => updateNewLineField(idx, "item_type", e.target.value)}
                  >
                    <option value="Material">Material</option>
                    <option value="Consumable">Consumable</option>
                  </select>
                </div>
              </div>

              {/* NEW: Filter UI */}
              {ln.item_type === "Material" && (
                <div className="row mt-2 g-2">
                  <div className="col-md-3">
                    <label className="form-label form-label-sm">Owner</label>
                    <select className="form-select form-select-sm" value={ln.filterOwner} onChange={(e) => updateNewLineField(idx, "filterOwner", e.target.value)}>
                      <option value="">All Owners</option>
                      {ownerOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                   <div className="col-md-3">
                    <label className="form-label form-label-sm">Type</label>
                    <select className="form-select form-select-sm" value={ln.filterMaterialType} onChange={(e) => updateNewLineField(idx, "filterMaterialType", e.target.value)}>
                      <option value="">All Types</option>
                      {materialTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label form-label-sm">Thickness</label>
                    <select className="form-select form-select-sm" value={ln.filterThickness} onChange={(e) => updateNewLineField(idx, "filterThickness", e.target.value)}>
                      <option value="">All</option>
                      {thicknessOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label form-label-sm">Finish</label>
                    <select className="form-select form-select-sm" value={ln.filterSurfaceFinish} onChange={(e) => updateNewLineField(idx, "filterSurfaceFinish", e.target.value)}>
                      <option value="">All</option>
                      {surfaceFinishOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
              )}
              
              <div className="row mt-2">
                {ln.item_type === "Material" && (
                  <div className="col-md-4">
                    <label>Material</label>
                    <select
                      className="form-select"
                      value={ln.material_id}
                      onChange={(e) => {
                        const val = e.target.value
                        updateNewLineField(idx, "material_id", val)
                        autoFillNewLineVendorPrice(idx, "Material", val)
                      }}
                    >
                      <option value="">-- select material --</option>
                      {/* MODIFIED: Use the filtered list */}
                      {finalFilteredMaterials.map((m) => {
                        const q = m.stock_quantity || 0
                        const r = m.reserved_quantity || 0
                        const i = m.inbound_quantity || 0
                        const a = q - r
                        return (
                          <option key={m.material_id} value={m.material_id}>
                            {m.material_code} - {m.description} (Q={q}, Av={a}, Inb={i})
                          </option>
                        )
                      })}
                    </select>
                  </div>
                )}

                {ln.item_type === "Consumable" && (
                  <div className="col-md-4">
                    <label>Consumable</label>
                    <select
                      className="form-select"
                      value={ln.consumable_id}
                      onChange={(e) => {
                        const val = e.target.value
                        updateNewLineField(idx, "consumable_id", val)
                        autoFillNewLineVendorPrice(idx, "Consumable", val)
                      }}
                    >
                      <option value="">-- select consumable --</option>
                      {allConsumables.map((c) => (
                        <option key={c.consumable_id} value={c.consumable_id}>
                          {c.product_id} - {c.description} (Q={c.stock_quantity})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="col-md-3">
                  <label>Qty Ordered</label>
                  <input
                    type="number"
                    className="form-control"
                    value={ln.quantity_ordered ?? ""}
                    onChange={(e) => {
                      const val = e.target.value
                      updateNewLineField(idx, "quantity_ordered", val === "" ? 0 : parseInt(val, 10))
                    }}
                    onBlur={() => {
                      if (ln.item_type === "Consumable" && ln.consumable_id) {
                        const cons = allConsumables.find(c => String(c.consumable_id) === String(ln.consumable_id))
                        if (cons) {
                          const moq = cons.min_order_qty || 1
                          const multiple = cons.order_in_qty_of || 1
                          let adjusted = Math.max(ln.quantity_ordered || 0, moq)
                          if (multiple > 1) adjusted = Math.ceil(adjusted / multiple) * multiple
                          if (adjusted !== ln.quantity_ordered) {
                            if (window.confirm(`Qty adjusted from ${ln.quantity_ordered} to ${adjusted} (MOQ: ${moq}, Multiple: ${multiple}). Accept?`)) {
                              updateNewLineField(idx, "quantity_ordered", adjusted)
                            }
                          }
                        }
                      }
                    }}
                  />
                  {ln.item_type === "Consumable" && ln.consumable_id && (() => {
                    const cons = allConsumables.find(c => String(c.consumable_id) === String(ln.consumable_id))
                    if (!cons) return null
                    const moq = cons.min_order_qty || 1
                    const multiple = cons.order_in_qty_of || 1
                    if (moq <= 1 && multiple <= 1) return null
                    const qty = ln.quantity_ordered || 0
                    const belowMoq = qty < moq
                    const badMultiple = multiple > 1 && qty % multiple !== 0
                    return (
                      <small className={belowMoq || badMultiple ? "text-danger" : "text-muted"}>
                        {moq > 1 && `Min: ${moq}`}{moq > 1 && multiple > 1 && ", "}{multiple > 1 && `Multiple: ${multiple}`}
                      </small>
                    )
                  })()}
                </div>

                <div className="col-md-3">
                  <label>Cost/Unit</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={ln.cost_per_unit ?? ""}
                    onChange={(e) => {
                      const val = e.target.value
                      updateNewLineField(idx, "cost_per_unit", val === "" ? null : val)
                    }}
                  />
                  {ln.cost_source && (
                    <small
                      className={ln.cost_auto_filled ? "text-success" : "text-muted"}
                      style={{ fontSize: "0.75rem" }}
                    >
                      {ln.cost_auto_filled ? `Auto · ${ln.cost_source}` : ln.cost_source}
                    </small>
                  )}
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-12 d-flex align-items-end">
                  {stockInfo && (
                    <div style={{ fontSize: "0.85rem" }} className="text-muted flex-grow-1">
                      {stockInfo}
                    </div>
                  )}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleRemoveNewLine(idx)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )
        })}

        {/* *** Display total cost for new PO lines *** */}
        <div className="mt-2">
          <strong>Total Cost:</strong> ${getPOTotalCost().toFixed(2)}
        </div>
      </div>
    )}

    {/* If editing an existing PO => show lines + partial receiving panel */}
    {/* MODIFIED: Added filter fields and logic to existing PO lines */}
    {poId && (
      <>
        {/* *** NEW: Add line for existing PO *** */}
        <button
          className="btn btn-sm btn-secondary mb-2"
          onClick={() => {
            setPoLines(prev => {
              const nextId = tempIdCounter + 1;
              setTempIdCounter(nextId);
              return [
                ...prev,
                {
                  id: `temp-${nextId}`, // Assign a stable temporary ID
                  po_line_id: null,
                  item_type: "Material",
                  material_id: null,
                  consumable_id: null,
                  quantity_ordered: 0,
                  quantity_received: 0,
                  cost_per_unit: null,
                  vendor_sku: null,
                  // NEW: Initialize filter state for new lines on existing POs
                  filterOwner: "",
                  filterMaterialType: "",
                  filterThickness: "",
                  filterSurfaceFinish: ""
                }
              ];
            })
          }}
        >
          + Add New Line
        </button>

        {/* --- NEW: Material Pricing Summary Table --- */}
        {uniqueMaterialSummary.length > 0 && status === 'RFQ' && (
          <div className="card mb-4 border-primary">
            <div className="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
              <span className="fw-bold">Material Pricing Summary (Grouped)</span>
              <small>Updates apply to all delivery weeks for the material</small>
            </div>
            <div className="card-body p-0 table-responsive">
              <table className="table table-sm table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Material Code</th>
                    <th>Description</th>
                    <th>Total Qty</th>
                    <th style={{width: "140px"}}>Set CWT</th>
                    <th style={{width: "140px"}}>Set Unit Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {uniqueMaterialSummary.map(mat => (
                    <tr key={mat.material_id}>
                      <td className="align-middle">{mat.code}</td>
                      <td className="align-middle">{mat.description}</td>
                      <td className="align-middle"><strong>{mat.total_qty}</strong></td>
                      <td>
                        <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control"
                            placeholder="CWT"
                            onChange={(e) => handleBulkMaterialCWTChange(mat.material_id, e.target.value)}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <input 
                            type="number" 
                            className="form-control"
                            step="0.01"
                            value={mat.current_cost ?? ""}
                            onChange={(e) => handleBulkMaterialCostChange(mat.material_id, e.target.value)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <h5>PO Lines</h5>
        {/* --- NEW: CWT Batch Tool --- */}
        <div className="card p-2 mb-3 bg-light border-0">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold text-muted small">RFQ TOOLS:</span>
            <input 
              type="number" 
              className="form-control form-control-sm" 
              style={{width: "100px"}} 
              placeholder="CWT Price"
              value={bulkCWT}
              onChange={(e) => setBulkCWT(e.target.value)}
            />
            <button className="btn btn-sm btn-outline-secondary" onClick={handleBulkApplyCWT}>
              Apply CWT to All Materials
            </button>
            <InfoTooltip text="Calculates Unit Cost = (Weight / 100) * CWT. Only affects Material lines with a defined weight." />
          </div>
        </div>
        <table className="table table-sm">
          <thead>
            <tr>
              <th style={{ minWidth: "80px" }}>Item Type</th>
              <th>Item</th>
              <th>Description</th>
              <th>Q Info</th>
              <th>Ordered</th>
              <th>Received</th>
              <th>Remain</th>
              <th style={{width: "100px", color: "#6c757d"}}>CWT Input</th>
              <th>Cost/Unit</th>
              <th>Heat Number</th> {/* <--- NEW COLUMN */}
              <th>Receive Qty</th>
              <th>Remove</th>
            </tr>
          </thead>
          <tbody>
            {/** Filter out lines that we've flagged for deletion */}
            {poLines
              .filter(ln => !ln._delete)
              .map((ln) => {
                const lineKey = ln.po_line_id ?? ln.id;
                const remain = ln.quantity_ordered - ln.quantity_received
                let desc = ""
                let qInfo = ""
                
                // NEW: Cascading filter logic for existing PO lines
                const ownerOptions = [...new Set(allMaterials.map(m => m.owner).filter(Boolean))].sort();
                
                let materialsForType = allMaterials;
                if (ln.filterOwner) {
                    materialsForType = materialsForType.filter(m => m.owner === ln.filterOwner);
                }
                const materialTypeOptions = [...new Set(materialsForType.map(m => m.material_type).filter(Boolean))].sort();
      
                let materialsForThickness = materialsForType;
                if (ln.filterMaterialType) {
                    materialsForThickness = materialsForThickness.filter(m => m.material_type === ln.filterMaterialType);
                }
                const thicknessOptions = [...new Set(materialsForThickness.map(m => m.thickness).filter(Boolean))].sort((a, b) => a - b);
      
                let materialsForFinish = materialsForThickness;
                if (ln.filterThickness) {
                    materialsForFinish = materialsForFinish.filter(m => m.thickness == ln.filterThickness);
                }
                const surfaceFinishOptions = [...new Set(materialsForFinish.map(m => m.surface_finish).filter(Boolean))].sort();
      
                let finalFilteredMaterials = materialsForFinish;
                if (ln.filterSurfaceFinish) {
                    finalFilteredMaterials = finalFilteredMaterials.filter(m => m.surface_finish === ln.filterSurfaceFinish);
                }

                if (ln.item_type === "Material") {
                  const matId = ln.material_id
                  const mat = allMaterials.find(m => m.material_id === matId)
                  if (mat) {
                    desc = mat.description || ""
                    const stock = mat.stock_quantity || 0
                    const reserved = mat.reserved_quantity || 0
                    const inbound = mat.inbound_quantity || 0
                    const avail = stock - reserved
                    qInfo = `Q=${stock}, Av=${avail}, Inb=${inbound}`
                  }
                } else {
                  const c = allConsumables.find(cc => cc.consumable_id === ln.consumable_id)
                  if (c) {
                    desc = c.description || "Consumable"
                    qInfo = `Q=${c.stock_quantity || 0}, ROP=${c.reorder_point || 0}`
                  } else {
                    desc = ln.material_description || "Consumable"
                  }
                }

                return (
                  <tr key={lineKey}>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={ln.item_type}
                        onChange={(e) => handleLineFieldChange(lineKey, "item_type", e.target.value)}
                      >
                        <option value="Material">Material</option>
                        <option value="Consumable">Consumable</option>
                      </select>
                    </td>

                    {/* Item (material_id or consumable_id) */}
                    <td>
                      {ln.item_type === "Material" ? (
                        <>
                          {/* NEW: Filter UI for existing PO lines */}
                           <div className="d-flex flex-wrap gap-1 mb-1">
                                <select title="Filter by Owner" className="form-select form-select-sm" style={{width: 'auto', flexGrow: 1}} value={ln.filterOwner || ''} onChange={(e) => handleLineFieldChange(lineKey, "filterOwner", e.target.value)}>
                                  <option value="">Owner...</option>
                                  {ownerOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                                <select title="Filter by Type" className="form-select form-select-sm" style={{width: 'auto', flexGrow: 1}} value={ln.filterMaterialType || ''} onChange={(e) => handleLineFieldChange(lineKey, "filterMaterialType", e.target.value)}>
                                  <option value="">Type...</option>
                                  {materialTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                                <select title="Filter by Thickness" className="form-select form-select-sm" style={{width: 'auto', flexGrow: 1}} value={ln.filterThickness || ''} onChange={(e) => handleLineFieldChange(lineKey, "filterThickness", e.target.value)}>
                                  <option value="">Thickness...</option>
                                  {thicknessOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                                <select title="Filter by Finish" className="form-select form-select-sm" style={{width: 'auto', flexGrow: 1}} value={ln.filterSurfaceFinish || ''} onChange={(e) => handleLineFieldChange(lineKey, "filterSurfaceFinish", e.target.value)}>
                                  <option value="">Finish...</option>
                                  {surfaceFinishOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                            </div>
                          <select
                            className="form-select form-select-sm"
                            value={ln.material_id || ""}
                            onChange={(e) =>
                              handleLineFieldChange(lineKey, "material_id", e.target.value)
                            }
                          >
                            <option value="">-- select material --</option>
                            {/* MODIFIED: Use filtered list */}
                            {finalFilteredMaterials.map((m) => {
                              const q = m.stock_quantity || 0
                              const r = m.reserved_quantity || 0
                              const i = m.inbound_quantity || 0
                              const a = q - r
                              return (
                                <option key={m.material_id} value={m.material_id}>
                                  {m.material_code} - {m.description} (Q={q}, Av={a}, Inb={i})
                                </option>
                              )
                            })}
                          </select>
                        </>
                      ) : (
                        <select
                          className="form-select form-select-sm"
                          value={ln.consumable_id || ""}
                          onChange={(e) =>
                            handleLineFieldChange(lineKey, "consumable_id", e.target.value)
                          }
                        >
                          <option value="">-- select consumable --</option>
                          {allConsumables.map((c) => (
                            <option key={c.consumable_id} value={c.consumable_id}>
                              {c.product_id} - {c.description} (Q={c.stock_quantity})
                            </option>
                          ))}
                        </select>
                      )}
                    </td>

                    {/* Description (plus vendor_sku if consumable) */}
                    <td>
                      {desc}
                      {ln.item_type === "Consumable" && ln.vendor_sku && (
                        <span style={{ marginLeft: 6, color: "#555" }}>
                          ({ln.vendor_sku})
                        </span>
                      )}
                    </td>

                    <td>{qInfo}</td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: "80px" }}
                        value={ln.quantity_ordered ?? ""}
                        onChange={(e) => {
                          const val = e.target.value
                          handleLineFieldChange(
                            lineKey,
                            "quantity_ordered",
                            val === "" ? 0 : parseInt(val, 10)
                          )
                        }}
                      />
                    </td>
                    <td>{ln.quantity_received}</td>
                    <td>{lineRemaining(ln)}</td>
                    <td>
                      {ln.item_type === "Material" ? (
                        <div className="input-group input-group-sm">
                          <input
                            type="number"
                            className="form-control bg-light"
                            placeholder="CWT"
                            title="Enter CWT here to auto-calculate Cost/Unit"
                            onChange={(e) => 
                              handleLineCWTChange(
                                ln.po_line_id ?? ln.id, 
                                e.target.value, 
                                ln.material_id
                              )
                            }
                          />
                        </div>
                      ) : (
                        <span className="text-muted small">-</span>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control form-control-sm"
                        style={{ width: "80px" }}
                        value={ln.cost_per_unit ?? ""}
                        onChange={(e) => {
                          const val = e.target.value
                          handleLineFieldChange(
                            lineKey,
                            "cost_per_unit",
                            val === "" ? null : val
                          )
                        }}
                      />
                    </td>
                    
                    {/* Heat Number + Receive Qty (collapses to a single split-pallets editor when the operator clicks Split) */}
                    {ln.item_type === "Material" && receiveHandlingUnits[ln.po_line_id] ? (
                      <td colSpan={2}>
                        <div className="border rounded p-2 bg-light">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong className="small">Pallets / handling units</strong>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-decoration-none p-0"
                              onClick={() => handleCancelSplit(ln.po_line_id)}
                              title="Collapse back to a single heat / qty entry"
                            >
                              Cancel split
                            </button>
                          </div>
                          <table className="table table-sm mb-1" style={{ background: "transparent" }}>
                            <thead>
                              <tr>
                                <th style={{ width: "80px" }} className="small">Qty</th>
                                <th style={{ width: "120px" }} className="small">Heat #</th>
                                <th style={{ width: "30px" }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {receiveHandlingUnits[ln.po_line_id].map((hu, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={hu.quantity}
                                      min="1"
                                      onChange={(e) =>
                                        handleHandlingUnitChange(ln.po_line_id, idx, "quantity", e.target.value)
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={hu.heat_number}
                                      placeholder="Heat #"
                                      onChange={(e) =>
                                        handleHandlingUnitChange(ln.po_line_id, idx, "heat_number", e.target.value)
                                      }
                                    />
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleRemoveHandlingUnit(ln.po_line_id, idx)}
                                      title="Remove pallet"
                                    >
                                      ✕
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="d-flex justify-content-between align-items-center small">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleAddHandlingUnit(ln.po_line_id)}
                            >
                              + Add pallet
                            </button>
                            <span className={`small ${sumHandlingUnitQty(ln.po_line_id) > remain ? "text-danger fw-bold" : "text-muted"}`}>
                              Total: {sumHandlingUnitQty(ln.po_line_id)} / {remain} remaining
                            </span>
                          </div>
                        </div>
                      </td>
                    ) : (
                      <>
                        {/* Heat Number Input */}
                        <td>
                          {ln.item_type === "Material" ? (
                            <div className="d-flex align-items-center gap-1">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ width: "100px" }}
                                value={receiveHeatNumbers[ln.po_line_id] || ""}
                                onChange={(e) => handleHeatNumberChange(ln.po_line_id, e.target.value)}
                                placeholder="Heat #"
                              />
                              {remain > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleSplitIntoPallets(ln.po_line_id)}
                                  title="Vendor sent multiple pallets with different heat numbers"
                                  style={{ padding: "2px 6px", lineHeight: 1 }}
                                >
                                  ⊞ Split
                                </button>
                              )}
                            </div>
                          ) : (
                             <span className="text-muted">N/A</span>
                          )}
                        </td>

                        {/* Receive Qty */}
                        <td>
                          {remain > 0 ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: "80px" }}
                              value={receiveQuantities[ln.po_line_id] || ""}
                              onChange={(e) => handleReceiveFieldChange(ln.po_line_id, e.target.value)}
                            />
                          ) : (
                            <span className="text-muted">Fully Rcvd</span>
                          )}
                        </td>
                      </>
                    )}

                    {/* Remove button */}
                    <td>
                      {ln.po_line_id === null ? (
                        // brand-new line => remove from state
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            setPoLines(prev => prev.filter(p => (p.id ?? p.po_line_id) !== lineKey))
                          }}
                        >
                          Remove
                        </button>
                      ) : (
                        // existing line => mark it for deletion
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            setPoLines(prev =>
                              prev.map(p =>
                                p.po_line_id === ln.po_line_id
                                  ? { ...p, _delete: true }
                                  : p
                              )
                            )
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>

        <div className="mb-2" style={{ display: "flex", gap: "1rem" }}>
          <button className="btn btn-outline-success" onClick={handleReceiveEntirePO}>
            Receive Entire Order
          </button>
          <button className="btn btn-primary" onClick={handleReceivePartial}>
            Receive Partial Order
          </button>
        </div>

        {/* Which sales orders this PO's material is spoken for. Optional: a PO
            with no earmarks behaves exactly as it always has. */}
        {poId && (
          <POSalesOrderEarmarks
            poId={poId}
            poLines={poLines}
            salesOrders={earmarkSalesOrders}
          />
        )}

        {/* Show total cost for an existing PO */}
        <div className="mb-3">
          <strong>Total Cost:</strong>{" "}
          ${getPOTotalCost().toFixed(2)}
        </div>

        {/* Show Shipments History */}
        {poShipments.length > 0 && (
          <>
            <h6>Shipments History</h6>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Shipment ID</th>
                  <th>Date</th>
                  <th>Notes</th>
                  <th style={{ width: "40%" }}>Lines</th>
                  <th>Docs</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {poShipments.map((sh) => (
                  <tr key={sh.shipment_id}>
                    <td style={{verticalAlign: "top"}}>{sh.shipment_id}</td>
                    <td style={{verticalAlign: "top"}}>{sh.shipment_date}</td>
                    <td style={{verticalAlign: "top"}}>{sh.notes}</td>
                    
                    {/* LINES COLUMN UPDATED */}
                    <td style={{ maxWidth: "400px", overflowX: "auto" }}>
                      {sh.lines && sh.lines.length > 0 ? (
                        <table className="table table-sm table-borderless mb-0" style={{fontSize:"0.85rem"}}>
                          <tbody>
                            {sh.lines.map(line => (
                              <tr key={line.shipment_line_id} className="border-bottom">
                                <td style={{width: "40%"}}>
                                  <strong>{line.item_code}</strong><br/>
                                  <span className="text-muted">{line.item_description?.substring(0,20)}...</span>
                                </td>
                                <td style={{width: "20%"}}>
                                  Qty: {line.quantity_received}
                                </td>
                                <td style={{width: "40%"}}>
                                  {/* Display Tracking Number */}
                                  {line.tracking_number_display && (
                                    <div className="mb-1">
                                      <span className="badge bg-dark">TRK: {line.tracking_number_display}</span>
                                      {beaconsOn && lotBeacons[line.mtn_id] && (
                                        <span className="badge bg-primary ms-1" title="Pallet beacon on this lot" data-testid={`po-line-beacon-${line.mtn_id}`}>
                                          <i className="bi bi-broadcast me-1"></i>{lotBeacons[line.mtn_id]}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {/* Display Heat Number */}
                                  {line.heat_number && <div className="mb-1">Heat: <strong>{line.heat_number}</strong></div>}
                                  
                                  {/* Display Existing Certs */}
                                  {line.documents && line.documents.map(doc => (
                                    <div key={doc.shipment_document_id} className="mb-1">
                                      <a href={poDocUrl(doc)} target="_blank" rel="noreferrer" className="badge bg-info text-decoration-none">
                                        📄 Cert
                                      </a>
                                      <span 
                                        style={{cursor:"pointer", marginLeft:5, color:"red"}} 
                                        onClick={() => handleDeleteShipmentDoc(doc.shipment_document_id)} 
                                      >×</span>
                                    </div>
                                  ))}
                                  
                                  {/* Upload Button */}
                                  <label className="btn btn-sm btn-outline-secondary p-0 px-1" style={{fontSize:"0.7rem"}}>
                                    + Cert
                                    <input 
                                      type="file" 
                                      hidden 
                                      onChange={(e) => handleUploadMatCert(e, sh.shipment_id, line.shipment_line_id)}
                                    />
                                  </label>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <span className="text-muted">No lines</span>
                      )}
                    </td>

                    {/* GENERAL DOCS (Packing List) */}
                    <td style={{verticalAlign: "top"}}>
                      {packingListDocs(sh).length > 0 ? (
                        packingListDocs(sh).map(doc => (
                          <button
                            key={doc.shipment_document_id}
                            className="btn btn-sm btn-link d-block text-start p-0"
                            onClick={() =>
                              setPreviewDoc({
                                shipment_document_id: doc.shipment_document_id,
                                s3_key: doc.s3_key,
                                original_filename: doc.original_filename
                              })
                            }
                          >
                            📎 {doc.original_filename}
                          </button>
                        ))
                      ) : (
                        <span className="text-muted">No Packing Slip</span>
                      )}
                    </td>
                    <td style={{verticalAlign: "top"}}>
                      <div className="d-flex gap-2">
                        {/* New Print Labels Button */}
                        <button 
                          className="btn btn-sm btn-outline-dark"
                          onClick={() => handleOpenLabelModal(sh)}
                          title="Print Material Labels"
                        >
                          🖨 Labels
                        </button>

                        {beaconsOn && shipmentHasPallets(sh) && (
                          <button
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            onClick={() => setBeaconShipment(sh)}
                            title="Put free pallet beacons on this shipment's pallets"
                          >
                            <i className="bi bi-broadcast me-1"></i>Beacons
                          </button>
                        )}
                        
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteShipment(sh.shipment_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="mt-4">
          <h5>Attach Documents to Shipments</h5>
          <button
            className="btn btn-sm btn-outline-secondary mb-2"
            onClick={fetchUnlinkedDocsForAttach}
          >
            Load Unlinked Docs
          </button>
          {unlinkedDocsForAttach.length > 0 && (
            <ul className="list-group mb-2">
              {unlinkedDocsForAttach.map(doc => (
                <li
                  key={doc.receipt_document_id}
                  className="list-group-item d-flex justify-content-between"
                >
                  <span>
                    Doc#{doc.receipt_document_id}: {doc.original_filename}
                  </span>
                  <div>
                    <button
                      className="btn btn-sm btn-light me-2"
                      onClick={() => handlePreviewUnlinkedDoc(doc)}
                    >
                      Preview
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => {
                        setAttachDocId(doc.receipt_document_id)
                      }}
                    >
                      Select
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="row mb-2">
            <div className="col-md-6">
              <label>Selected Doc ID:</label>
              <input
                className="form-control"
                value={attachDocId}
                onChange={handleAttachDocChange}
                placeholder="Pick from list or enter manually"
              />
            </div>
            <div className="col-md-6">
              <label>Attach to Shipment:</label>
              <select
                className="form-select"
                value={attachShipmentId}
                onChange={handleAttachShipmentChange}
              >
                <option value="">-- select shipment --</option>
                {poShipments.map((sh) => (
                  <option key={sh.shipment_id} value={sh.shipment_id}>
                    Shipment#{sh.shipment_id} (Date {sh.shipment_date})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button className="btn btn-sm btn-success" onClick={handleAttachDocumentToShipment}>
            Link Document
          </button>
        </div>
      </>
    )}
  </>
)}

        {/* DOCS QUEUE VIEW */}
        {viewMode === "DOCS" && (
          <>
            <h4>Unlinked Documents Queue</h4>
            <p>
              Assign these unlinked documents to an open PO and create a multi-line partial shipment
              in one go. Then link the doc automatically and remove it from this queue.
            </p>

            {loading && <div>Loading documents...</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            {!loading && unlinkedDocs.length > 0 && (
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Doc ID</th>
                    <th>Filename</th>
                    <th>Uploaded By</th>
                    <th>Uploaded At</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {unlinkedDocs.map((doc) => (
                    <tr key={doc.receipt_document_id}>
                      <td>{doc.receipt_document_id}</td>
                      <td>{doc.original_filename}</td>
                      <td>{doc.uploaded_by}</td>
                      <td>{doc.uploaded_at}</td>
                      <td>
                        {queueSelectedDoc?.receipt_document_id === doc.receipt_document_id ? (
                          <span className="text-success">Selected</span>
                        ) : (
                          <div className="d-flex" style={{ gap: "0.5rem" }}>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleSelectQueueDoc(doc)}
                            >
                              Select
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary me-3"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              Preview
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteDoc(doc.receipt_document_id)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && unlinkedDocs.length === 0 && (
              <div>No unlinked documents found.</div>
            )}

            {queueSelectedDoc && (
              <div className="mt-4 border p-3">
                <h5>Create Shipment for Document #{queueSelectedDoc.receipt_document_id}</h5>

                <div className="mb-2">
                  <label>Select PO:</label>
                  <select
                    className="form-select"
                    value={queueSelectedPO}
                    onChange={handleQueuePOChange}
                  >
                    <option value="">-- pick a PO --</option>
                    {purchaseOrders
                      .filter(po => po.status === "Committed")
                      .map(po => (
                        <option key={po.purchase_order_id} value={po.purchase_order_id}>
                          PO#{po.po_number} - {po.supplier}
                        </option>
                      ))}
                  </select>
                </div>

                {queuePOLines.length > 0 && (
                  <>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Line#</th>
                          <th>Material/Cons</th>
                          <th>Ordered</th>
                          <th>Received</th>
                          <th>Remain</th>
                          <th>Heat Number</th> {/* <--- NEW COLUMN */}
                          <th>Receive Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {queuePOLines.map((ln) => {
                          const remain = ln.quantity_ordered - ln.quantity_received
                          let label = ""
                          if (ln.item_type === "Consumable") {
                            const c = allConsumables.find(cc => cc.consumable_id === ln.consumable_id)
                            label = c ? `${c.product_id} (cons)` : "(cons)"
                          } else {
                            label = ln.material_code
                          }
                          return (
                            <tr key={ln.po_line_id}>
                              <td>{ln.po_line_id}</td>
                              <td>{label}</td>
                              <td>{ln.quantity_ordered}</td>
                              <td>{ln.quantity_received}</td>
                              <td>{lineRemaining(ln)}</td>
                              
                              {/* Heat Number Input */}
                              <td>
                                {ln.item_type === "Material" ? (
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ width: "100px" }}
                                    value={queueReceiveHeatNumbers[ln.po_line_id] || ""}
                                    onChange={(e) => handleQueueHeatNumberChange(ln.po_line_id, e.target.value)}
                                    placeholder="Heat #"
                                  />
                                ) : (
                                   <span className="text-muted">N/A</span>
                                )}
                              </td>

                              {/* Receive Qty */}
                              <td>
                                {remain > 0 ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ width: "80px" }}
                                    value={queueReceiveQuantities[ln.po_line_id] || ""}
                                    onChange={(e) => handleQueueQtyChange(ln.po_line_id, e.target.value)}
                                  />
                                ) : (
                                  <span className="text-muted">Fully Rcvd</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>

                    <div className="d-flex gap-2 mb-3">
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={handleQueueReceiveEntirePO}
                      >
                        Receive All Remaining
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleCreateShipmentFromDoc(queueSelectedDoc)}
                      >
                        Create Shipment + Link
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setQueueSelectedDoc(null)
                          setQueueSelectedPO("")
                          setQueuePOLines([])
                          setQueueReceiveQuantities({})
                          setQueueReceiveHeatNumbers({})
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
        
        {renderPreview()}

        {beaconShipment && (
          <ShipmentBeaconsModal
            key={beaconShipment.shipment_id}
            shipment={beaconShipment}
            poLines={poLines}
            poNumber={poNumber}
            onClose={() => setBeaconShipment(null)}
            onChanged={() => refreshLotBeacons()}
          />
        )}

        {/* Label Printing Modal */}
        {showLabelModal && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Print Shipment Labels (ID: {labelModalShipmentId})</h5>
                  <button type="button" className="btn-close" onClick={() => setShowLabelModal(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted small">
                    One row per pallet (handling unit). Each label carries its own heat number + tracking number.
                    Default is 1 label per pallet — increase for extra copies.
                  </p>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th>Description</th>
                        <th style={{width: "100px"}}>MTN</th>
                        <th style={{width: "120px"}}>Heat #</th>
                        <th style={{width: "70px"}}>Pcs</th>
                        <th style={{width: "80px"}}>Labels</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labelModalItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.material_code}</td>
                          <td className="small">{item.description}</td>
                          <td className="small font-monospace">{item.mtn_display || "—"}</td>
                          <td className="small">{item.heat_number || <span className="text-muted">—</span>}</td>
                          <td className="small">{item.quantity_received}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.printQty}
                              onChange={(e) => handleLabelQtyChange(idx, e.target.value)}
                              min="0"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowLabelModal(false)}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={handleGenerateLabels}>Download PDF</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manual RFQ Creation Modal */}
        {showManualRFQModal && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create Manual RFQ</h5>
                  <button type="button" className="btn-close" onClick={() => setShowManualRFQModal(false)}></button>
                </div>
                <div className="modal-body">
                  {/* RFQ Lines Section */}
                  <h6>RFQ Items</h6>
                  <button className="btn btn-sm btn-secondary mb-2" onClick={handleAddManualRfqLine}>
                    + Add Line
                  </button>

                  {manualRfqLines.length === 0 && (
                    <p className="text-muted">No items added yet. Click "+ Add Line" to start building your RFQ.</p>
                  )}

                  {manualRfqLines.map((ln, idx) => {
                    // Cascading filter logic (same as PO creation)
                    const ownerOptions = [...new Set(allMaterials.map(m => m.owner).filter(Boolean))].sort();
                    let materialsForType = allMaterials;
                    if (ln.filterOwner) materialsForType = materialsForType.filter(m => m.owner === ln.filterOwner);
                    const materialTypeOptions = [...new Set(materialsForType.map(m => m.material_type).filter(Boolean))].sort();

                    let materialsForThickness = materialsForType;
                    if (ln.filterMaterialType) materialsForThickness = materialsForThickness.filter(m => m.material_type === ln.filterMaterialType);
                    const thicknessOptions = [...new Set(materialsForThickness.map(m => m.thickness).filter(Boolean))].sort((a, b) => a - b);

                    let materialsForFinish = materialsForThickness;
                    if (ln.filterThickness) materialsForFinish = materialsForFinish.filter(m => m.thickness == ln.filterThickness);
                    const surfaceFinishOptions = [...new Set(materialsForFinish.map(m => m.surface_finish).filter(Boolean))].sort();

                    let finalFilteredMaterials = materialsForFinish;
                    if (ln.filterSurfaceFinish) finalFilteredMaterials = finalFilteredMaterials.filter(m => m.surface_finish === ln.filterSurfaceFinish);

                    const mat = ln.material_id ? allMaterials.find(m => m.material_id === parseInt(ln.material_id, 10)) : null;
                    const cons = ln.consumable_id ? allConsumables.find(c => c.consumable_id === parseInt(ln.consumable_id, 10)) : null;

                    let stockInfo = "";
                    if (mat) {
                      const q = mat.stock_quantity || 0, r = mat.reserved_quantity || 0, i = mat.inbound_quantity || 0;
                      stockInfo = `Q=${q}, Av=${q - r}, Inb=${i}`;
                    }
                    if (cons) {
                      stockInfo = `Q=${cons.stock_quantity || 0}, Reorder=${cons.reorder_point}`;
                    }

                    return (
                      <div key={idx} className="mb-2 border rounded p-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong>Line {idx + 1}</strong>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveManualRfqLine(idx)}>Remove</button>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-2">
                            <label className="form-label form-label-sm">Item Type</label>
                            <select className="form-select form-select-sm" value={ln.item_type} onChange={(e) => updateManualRfqLineField(idx, "item_type", e.target.value)}>
                              <option value="Material">Material</option>
                              <option value="Consumable">Consumable</option>
                            </select>
                          </div>

                          {ln.item_type === "Material" && (
                            <>
                              <div className="col-md-2">
                                <label className="form-label form-label-sm">Owner</label>
                                <select className="form-select form-select-sm" value={ln.filterOwner} onChange={(e) => updateManualRfqLineField(idx, "filterOwner", e.target.value)}>
                                  <option value="">All Owners</option>
                                  {ownerOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label form-label-sm">Type</label>
                                <select className="form-select form-select-sm" value={ln.filterMaterialType} onChange={(e) => updateManualRfqLineField(idx, "filterMaterialType", e.target.value)}>
                                  <option value="">All Types</option>
                                  {materialTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label form-label-sm">Thickness</label>
                                <select className="form-select form-select-sm" value={ln.filterThickness} onChange={(e) => updateManualRfqLineField(idx, "filterThickness", e.target.value)}>
                                  <option value="">All</option>
                                  {thicknessOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label form-label-sm">Finish</label>
                                <select className="form-select form-select-sm" value={ln.filterSurfaceFinish} onChange={(e) => updateManualRfqLineField(idx, "filterSurfaceFinish", e.target.value)}>
                                  <option value="">All</option>
                                  {surfaceFinishOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="row g-2 mt-1">
                          {ln.item_type === "Material" && (
                            <div className="col-md-6">
                              <label className="form-label form-label-sm">Material</label>
                              <select className="form-select form-select-sm" value={ln.material_id} onChange={(e) => updateManualRfqLineField(idx, "material_id", e.target.value)}>
                                <option value="">-- select material --</option>
                                {finalFilteredMaterials.map(m => {
                                  const q = m.stock_quantity || 0, r = m.reserved_quantity || 0, i = m.inbound_quantity || 0;
                                  return <option key={m.material_id} value={m.material_id}>{m.material_code} - {m.description} (Q={q}, Av={q - r}, Inb={i})</option>;
                                })}
                              </select>
                            </div>
                          )}

                          {ln.item_type === "Consumable" && (
                            <div className="col-md-6">
                              <label className="form-label form-label-sm">Consumable</label>
                              <select className="form-select form-select-sm" value={ln.consumable_id} onChange={(e) => updateManualRfqLineField(idx, "consumable_id", e.target.value)}>
                                <option value="">-- select consumable --</option>
                                {allConsumables.map(c => (
                                  <option key={c.consumable_id} value={c.consumable_id}>{c.product_id} - {c.description} (Q={c.stock_quantity})</option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div className="col-md-2">
                            <label className="form-label form-label-sm">Quantity</label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={ln.quantity_ordered || ""}
                              min="1"
                              onChange={(e) => updateManualRfqLineField(idx, "quantity_ordered", e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
                            />
                          </div>

                          {stockInfo && (
                            <div className="col-md-4 d-flex align-items-end">
                              <small className="text-muted">{stockInfo}</small>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <hr />

                  {/* Vendor Selection */}
                  <h6>Select Vendors</h6>
                  <p className="text-muted small">Each selected vendor will receive their own copy of this RFQ.</p>
                  <div className="list-group mb-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {vendors.map(v => (
                      <label key={v.vendor_id} className="list-group-item list-group-item-action">
                        <input
                          className="form-check-input me-2"
                          type="checkbox"
                          checked={manualRfqVendors.includes(v.vendor_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setManualRfqVendors(prev => [...prev, v.vendor_id]);
                            } else {
                              setManualRfqVendors(prev => prev.filter(id => id !== v.vendor_id));
                            }
                          }}
                        />
                        {v.name}
                      </label>
                    ))}
                  </div>

                  {/* Optional Fields */}
                  <div className="row g-2">
                    <div className="col-md-3">
                      <label className="form-label form-label-sm">Response Due Date (optional)</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={manualRfqDueDate}
                        onChange={(e) => setManualRfqDueDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label form-label-sm">Project Name (optional)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        maxLength={120}
                        placeholder="Names the bundle in JobQ"
                        value={manualRfqProjectName}
                        onChange={(e) => setManualRfqProjectName(e.target.value)}
                      />
                      <div className="form-text">For JobQ only. Vendors don't see it.</div>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label form-label-sm">Notes (optional)</label>
                      <textarea
                        className="form-control form-control-sm"
                        rows={2}
                        placeholder="e.g. rush order..."
                        value={manualRfqNotes}
                        onChange={(e) => setManualRfqNotes(e.target.value)}
                      />
                      <div className="form-text">Printed on the vendor's RFQ.</div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowManualRFQModal(false)}>Cancel</button>
                  <button
                    className="btn btn-warning"
                    onClick={handleSubmitManualRFQ}
                    disabled={loading || manualRfqLines.length === 0 || manualRfqVendors.length === 0}
                  >
                    {loading ? "Creating..." : `Create ${manualRfqVendors.length} RFQ${manualRfqVendors.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RFQ Vendor Selection Modal */}
        {showRFQModal && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create RFQ Bundle</h5>
                  <button type="button" className="btn-close" onClick={() => setShowRFQModal(false)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label">Project Name (optional)</label>
                  <input
                    type="text"
                    className="form-control mb-1"
                    maxLength={120}
                    placeholder="Names the bundle in JobQ"
                    value={rfqProjectName}
                    onChange={(e) => setRfqProjectName(e.target.value)}
                  />
                  <div className="form-text mb-3">For JobQ only. Vendors don't see it.</div>
                  <p>Select vendors to receive this Request for Quote:</p>
                  <div className="list-group" style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {vendors.map((v) => (
                      <label key={v.vendor_id} className="list-group-item">
                        <input
                          className="form-check-input me-2"
                          type="checkbox"
                          checked={rfqSelectedVendors.includes(v.vendor_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRfqSelectedVendors([...rfqSelectedVendors, v.vendor_id]);
                            } else {
                              setRfqSelectedVendors(rfqSelectedVendors.filter(id => id !== v.vendor_id));
                            }
                          }}
                        />
                        {v.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowRFQModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSubmitRFQGroup}>Create {rfqSelectedVendors.length} RFQs</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PO Email Modal */}
        <POEmailModal
          show={showEmailModal}
          supplierName={rfqComparisonSupplier || supplier}
          onClose={() => { setShowEmailModal(false); setRfqComparisonPoId(null); setRfqComparisonSupplier(""); setEmailDocType('po'); }}
          onSend={handleSendPOEmail}
          isLoading={emailSending}
          isRFQ={rfqComparisonPoId ? true : status === 'RFQ'}
          docType={emailDocType}
        />

        {/* PO Email History Modal */}
        <POEmailHistoryModal
          show={showEmailHistory}
          poId={rfqComparisonPoId || poId}
          apiBase={API_BASE}
          onClose={() => { setShowEmailHistory(false); setRfqComparisonPoId(null); setRfqComparisonSupplier(""); }}
        />

      </div>
    </div>
  )
}